import React, { useState, useContext, useEffect, useRef, useImperativeHandle, forwardRef, useMemo, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { get, post, put, del } from '../api';
import {
  FaClipboardList, FaPlus, FaPaperclip,
  FaUser, FaUsers, FaClock, FaCalendarAlt, FaTag, FaUpload,
  FaPaperclip as FaPaperclipIcon, FaFileAlt, FaEye, FaDownload,
  FaCheckCircle, FaCheckSquare, FaPlay, FaSave, FaEdit, FaArrowLeft, FaCircle, FaLock,
  FaInfoCircle, FaTasks, FaTrash, FaPhone, FaExclamationTriangle,
  FaChevronDown, FaChevronUp, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import './WorkflowAutomations.css';

const DEFAULT_BACKEND_BASE =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://intranet.nesimltd.co.ke'
    : 'http://localhost:5000');

const API_BASE = DEFAULT_BACKEND_BASE;
const MAX_DIRECT_UPLOAD_SIZE = 4 * 1024 * 1024; // 4MB - stay below proxy limits
const UPLOAD_CHUNK_SIZE = 512 * 1024; // 512KB chunks to stay under proxy limits comfortably

const getAuthHeaders = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  const token = window.localStorage ? window.localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const mapUploadedFileResponse = (uploadedFile) => {
  if (!uploadedFile) return null;
  return {
    filename: uploadedFile.filename,
    originalName: uploadedFile.originalName || uploadedFile.name,
    url: uploadedFile.url || uploadedFile.path,
    path: uploadedFile.path || uploadedFile.url,
    size: uploadedFile.size,
    mimetype: uploadedFile.mimetype,
    uploadedAt: new Date()
  };
};

const uploadViaStandardEndpoint = async (files, authHeaders, context) => {
  if (!files || files.length === 0) {
    return [];
  }

  const formData = new FormData();
  if (context) {
    formData.append('context', context);
  }
  files.forEach(file => formData.append('files', file));

  const response = await fetch(`${API_BASE}/api/upload/chat`, {
    method: 'POST',
    headers: authHeaders,
    body: formData,
    credentials: 'include'
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload failed: ${response.status} - ${errorText}`);
  }

  const result = await response.json();
  if (!result.files || result.files.length === 0) {
    throw new Error('No files returned from upload');
  }

  return result.files
    .map(mapUploadedFileResponse)
    .filter(Boolean);
};

const uploadFileViaChunks = async (file, context, authHeaders) => {
  const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const totalChunks = Math.max(1, Math.ceil(file.size / UPLOAD_CHUNK_SIZE));

  for (let index = 0; index < totalChunks; index += 1) {
    const start = index * UPLOAD_CHUNK_SIZE;
    const end = Math.min(file.size, start + UPLOAD_CHUNK_SIZE);
    const chunk = file.slice(start, end);

    const chunkForm = new FormData();
    chunkForm.append('uploadId', uploadId);
    chunkForm.append('chunkIndex', index);
    chunkForm.append('totalChunks', totalChunks);
    chunkForm.append('filename', file.name);
    chunkForm.append('mimetype', file.type || 'application/octet-stream');
    chunkForm.append('context', context);
    chunkForm.append('chunk', chunk, file.name);

    const response = await fetch(`${API_BASE}/api/upload/chunk`, {
      method: 'POST',
      headers: authHeaders,
      body: chunkForm,
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Chunk upload failed (chunk ${index + 1}/${totalChunks}): ${response.status} - ${errorText}`);
    }
  }

  const finalizeHeaders = {
    'Content-Type': 'application/json',
    ...authHeaders
  };

  const finalizeResponse = await fetch(`${API_BASE}/api/upload/chunk/complete`, {
    method: 'POST',
    headers: finalizeHeaders,
    body: JSON.stringify({
      uploadId,
      filename: file.name,
      mimetype: file.type || 'application/octet-stream',
      totalChunks,
      context
    }),
    credentials: 'include'
  });

  if (!finalizeResponse.ok) {
    const errorText = await finalizeResponse.text();
    throw new Error(`Failed to finalize upload: ${finalizeResponse.status} - ${errorText}`);
  }

  const finalizeResult = await finalizeResponse.json();
  if (!finalizeResult.files || finalizeResult.files.length === 0) {
    throw new Error('No files returned after chunked upload completion');
  }

  const mappedFile = mapUploadedFileResponse(finalizeResult.files[0]);
  if (!mappedFile) {
    throw new Error('Unable to map uploaded file response');
  }
  return mappedFile;
};

const uploadFilesWithAutoChunking = async ({ files, context = 'chat' }) => {
  const fileArray = Array.from(files || []);
  if (fileArray.length === 0) {
    return [];
  }

  const authHeaders = getAuthHeaders();

  const regularFiles = [];
  const largeFiles = [];

  fileArray.forEach((file, index) => {
    if (file.size > MAX_DIRECT_UPLOAD_SIZE) {
      largeFiles.push({ file, index });
    } else {
      regularFiles.push({ file, index });
    }
  });

  const uploadedFiles = new Array(fileArray.length);

  if (regularFiles.length > 0) {
    const mapped = await uploadViaStandardEndpoint(
      regularFiles.map(item => item.file),
      authHeaders,
      context
    );
    mapped.forEach((fileData, idx) => {
      const targetIndex = regularFiles[idx].index;
      uploadedFiles[targetIndex] = fileData;
    });
  }

  for (const largeFileEntry of largeFiles) {
    const chunkedFile = await uploadFileViaChunks(largeFileEntry.file, context, authHeaders);
    uploadedFiles[largeFileEntry.index] = chunkedFile;
  }

  return uploadedFiles.filter(Boolean);
};

const safeParseDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const formatDateOnly = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateTimeLocal = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getWorkflowMinDate = (startedAt) => {
  const today = startOfDay(new Date());
  const workflowStart = safeParseDate(startedAt);
  if (!workflowStart) return today;
  const workflowStartDay = startOfDay(workflowStart);
  return workflowStartDay < today ? workflowStartDay : today;
};

const getWorkflowDateBounds = (startedAt) => {
  const minDate = getWorkflowMinDate(startedAt);
  return {
    minDate,
    minDateString: formatDateOnly(minDate),
    minDateTimeString: formatDateTimeLocal(minDate),
  };
};

const ensureAbsoluteUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  return `${DEFAULT_BACKEND_BASE}${url.startsWith('/') ? url : `/${url}`}`;
};

const parseFilenameFromDisposition = (headerValue) => {
  if (!headerValue) return null;
  const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(headerValue);
  if (!match || !match[1]) return null;
  try {
    return decodeURIComponent(match[1].replace(/\"/g, '').trim());
  } catch (error) {
    console.warn('Failed to decode filename from header:', error);
    return match[1].replace(/\"/g, '').trim();
  }
};

const resolveFileDownloadInfo = (file) => {
  if (!file) {
    return { url: null, filename: null };
  }

  const url =
    file.downloadUrl ||
    file.url ||
    file.path ||
    file.filePath ||
    (file.location ? file.location : null) ||
    (file.id ? `/api/files/${file.id}` : null) ||
    (file._id ? `/uploads/chat/${file._id}` : null) ||
    (file.filename ? `/uploads/chat/${file.filename}` : null);

  const filename =
    file.name ||
    file.originalName ||
    file.filename ||
    file.fileName ||
    file.label ||
    'download';

  return { url, filename };
};

const downloadWorkflowFile = async (file) => {
  const { url, filename } = resolveFileDownloadInfo(file);

  if (!url) {
    throw new Error('No valid file URL found.');
  }

  const absoluteUrl = ensureAbsoluteUrl(url);
  if (!absoluteUrl) {
    throw new Error('Unable to construct download URL.');
  }

  try {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(absoluteUrl, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Download failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('content-disposition');
    const derivedFilename =
      parseFilenameFromDisposition(contentDisposition) || filename;
    const downloadUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', derivedFilename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      window.URL.revokeObjectURL(downloadUrl);
    }, 60 * 1000);
  } catch (error) {
    console.error('❌ [FILE_DOWNLOAD] Failed to download file', {
      file,
      absoluteUrl,
      error,
    });
    throw error;
  }
};

// Global cleanup: Override any stale removeMenu functions from cached code
// This prevents errors from old cached code trying to call removeChild
if (typeof window !== 'undefined') {
  try {
    if (typeof window.removeMenu === 'function') {
      window.removeMenu = function() { /* no-op */ };
    }
    if (typeof document !== 'undefined' && typeof document.removeMenu === 'function') {
      document.removeMenu = function() { /* no-op */ };
    }
    if (typeof HTMLDocument !== 'undefined' && HTMLDocument.prototype && typeof HTMLDocument.prototype.removeMenu === 'function') {
      HTMLDocument.prototype.removeMenu = function() { /* no-op */ };
    }
  } catch (err) {
    // Ignore errors during global cleanup
  }
}

// Workflow definitions
const workflowDefinitions = {
  technical_sales: {
    id: 'technical_sales',
    name: 'Technical Sales Workflow',
    description: 'Complete technical sales process from lead inquiry to purchase order closure',
    steps: [
      {
        id: 'make_inquiry',
        name: 'Make an Inquiry',
        description: 'Select or create a customer for this technical sales inquiry.',
        order: 1,
        role: 'tech_sales',
        phase: 'Sales Team',
        guidance: "Choose between adding a new customer or selecting an existing one",
        formFields: [
          { id: "assigned_user", label: "Assigned to", type: "user_select", required: true },
          { id: "assigned_user_2", label: "Second Assignee (Optional)", type: "user_select", required: false },
          { id: "customer_type", label: "Customer Type", type: "select", required: true, options: ["New Customer", "Existing Customer"] },
          
          // Customer Selection (shown when "Existing Customer" is selected)
          { id: "existing_customer_id", label: "Select Customer", type: "customer_select", required: false, showWhen: { field: "customer_type", value: "Existing Customer" } },
          
          // Customer Fields (shown for both New and Existing customers)
          { id: "customer_name", label: "Customer Name (Company Name)", type: "text", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          { id: "contact_name", label: "Contact Name", type: "text", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          { id: "contact_phone", label: "Contact Phone Number", type: "text", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          { id: "contact_email", label: "Contact Email Address", type: "email", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          { id: "buyer_name", label: "Buyer Name", type: "text", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          { id: "buyer_phone", label: "Buyer Phone", type: "text", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          { id: "buyer_email", label: "Buyer Email", type: "email", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          { id: "owner_name", label: "Owner Name", type: "text", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          { id: "owner_phone", label: "Owner Phone", type: "text", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          { id: "owner_email", label: "Owner Email", type: "email", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          { id: "user_name", label: "User Name", type: "text", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          { id: "user_phone", label: "User Phone", type: "text", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          { id: "user_email", label: "User Email", type: "email", required: false, showWhen: { field: "customer_type", values: ["New Customer", "Existing Customer"] } },
          
          { id: "inquiry_start_date", label: "Inquiry Start Date", type: "date", required: true },
          { id: "comments", label: "Inquiry Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'plan_visit',
        name: 'Plan a visit',
        description: 'Manage tasks to be completed before and after the visit.',
        order: 2,
        role: 'tech_sales',
        phase: 'Sales Team',
        guidance: "Add tasks to be completed before and after the visit. You can add more items later.",
        formFields: [
          { 
            id: "tasks_before", 
            label: "Tasks to be filled before", 
            type: "dynamic_list", 
            required: false,
            listType: "before",
            fields: [
              { id: "task", label: "Task", type: "text", required: true },
              { id: "description", label: "Description", type: "textarea", required: true },
              { id: "date", label: "Date", type: "date", required: true },
              { id: "time", label: "Time", type: "time", required: true }
            ]
          },
          {
            id: "tasks_after", 
            label: "Tasks to be filled after", 
            type: "dynamic_list", 
            required: false,
            listType: "after",
            fields: [
              { id: "outcome", label: "Outcome of Task", type: "textarea", required: true },
              { id: "attachments", label: "File Attachments", type: "file", required: false }
            ]
          },
          { id: "comments", label: "Visit Planning Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'activities',
        name: 'Activities',
        description: 'Manage in progress and completed activities including tasks, meetings, and calls.',
        order: 3,
        role: 'tech_sales',
        phase: 'Sales Team',
        guidance: "Create and manage activities to track your workflow progress",
        formFields: [
          { 
            id: "activities", 
            label: "Activities", 
            type: "activities_manager", 
            required: false
          }
        ]
      },
      {
        id: 'trials',
        name: 'Trials',
        description: 'Manage trial setup, execution, evaluation, and follow-up decisions.',
        order: 4,
        role: 'tech_sales',
        phase: 'Sales Team',
        guidance: "Complete trial setup, execution, evaluation, and decision-making process",
        formFields: [
          { 
            id: "trials", 
            label: "Trials", 
            type: "trials_manager", 
            required: false
          }
        ]
      },
      {
        id: 'customer_inquiry_quotation',
        name: 'Customer Inquiry and Quotation',
        description: 'Receive or identify customer inquiries. Issue a Proforma Invoice (PFI) when quotation is requested. Track inquiries that do not convert immediately.',
        order: 5,
        role: 'tech_sales',
        phase: 'Sales Team',
        guidance: "Process customer inquiries and issue quotations with Proforma Invoices",
        formFields: [
          { id: "date_of_inquiry", label: "Date of Inquiry", type: "date", required: true },
          { id: "inquiry_details", label: "Inquiry Details", type: "textarea", required: true },
          { id: "request_document", label: "Request Document", type: "file", required: false },
          { id: "quotation_reference", label: "Quotation Reference (PFI No.)", type: "text", required: false },
          { id: "pfi_sent_on", label: "PFI Sent On", type: "date", required: false },
          { id: "inquiry_status", label: "Inquiry Status", type: "select", required: true, options: ["Pending", "Quoted", "Closed", "Follow-up"] },
          { id: "remarks_next_steps", label: "Remarks / Next Steps", type: "textarea", required: false },
          { id: "comments", label: "Additional Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'purchase_order_receipt',
        name: 'Purchase Order (PO) Receipt',
        description: 'Receive official PO from client. Verify it matches the quotation terms. Follow up if not received.',
        order: 6,
        role: 'tech_sales',
        phase: 'Finance Team',
        guidance: "Verify and process purchase orders from clients",
        formFields: [
          { id: "po_number", label: "PO Number", type: "text", required: true },
          { id: "po_date", label: "PO Date", type: "date", required: true },
          { id: "po_document", label: "Upload PO Document", type: "file", required: true },
          { id: "po_source", label: "PO Source", type: "select", required: true, options: ["Email", "Courier", "Hand delivery"] },
          { id: "po_status", label: "PO Status", type: "select", required: true, options: ["Received", "Pending", "Clarification Needed"] },
          { id: "po_notes", label: "Notes", type: "textarea", required: false },
          { id: "comments", label: "Additional Comments", type: "comments", required: false }
        ]
      },
      {
        id: 'preparation_export_local',
        name: 'Preparation for Export or Local Delivery',
        description: 'Prepare shipping documentation (Packing List, Delivery Note, Invoice). Ensure compliance for export or local delivery. Review all details for accuracy.',
        order: 7,
        role: 'tech_sales',
        phase: 'Finance Team',
        guidance: "Prepare all required shipping documentation accurately",
        formFields: [
          { id: "document_prep_date", label: "Document Prep Date", type: "date", required: true },
          { id: "packing_list", label: "Packing List", type: "file", required: false },
          { id: "delivery_note", label: "Delivery Note", type: "file", required: false },
          { id: "invoice_copy", label: "Invoice Copy", type: "file", required: false },
          { id: "preparation_remarks", label: "Remarks", type: "textarea", required: false },
          { id: "all_docs_verified", label: "All Docs Verified", type: "checkbox", required: true },
          { id: "comments", label: "Additional Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'customs_delivery_coordination',
        name: 'Customs and Delivery Coordination',
        description: 'Coordinate shipment with client and shipping company. Handle customs clearance (for export). Manage any logistics delays or contingencies.',
        order: 8,
        role: 'tech_sales',
        phase: 'Finance Team',
        guidance: "Coordinate customs clearance and delivery logistics effectively",
        formFields: [
          { id: "shipment_type", label: "Shipment Type", type: "textarea", required: true },
          { id: "shipping_company", label: "Shipping Company", type: "textarea", required: true },
          { id: "expected_departure_date", label: "Expected Departure Date", type: "date", required: false },
          { id: "clearance_documents", label: "Clearance Documents", type: "file", required: false },
          { id: "customs_notes", label: "Customs Notes", type: "textarea", required: false },
          { id: "clearance_completed", label: "Clearance Completed", type: "checkbox", required: false },
          { id: "comments", label: "Additional Comments", type: "comments", required: false }
        ]
      },
      {
        id: 'shipment_of_goods',
        name: 'Shipment of Goods',
        description: 'Arrange and confirm shipment. Resolve any logistics issues. Record proof of dispatch.',
        order: 9,
        role: 'tech_sales',
        phase: 'Finance Team',
        guidance: "Ensure goods are shipped and dispatch is properly documented",
        formFields: [
          { id: "shipment_date", label: "Shipment Date", type: "date", required: true },
          { id: "destination_address", label: "Destination Address", type: "textarea", required: true },
          { id: "proof_of_dispatch", label: "Proof of Dispatch / Waybill", type: "file", required: true },
          { id: "shipment_status", label: "Shipment Status", type: "select", required: true, options: ["Dispatched", "In Transit", "Delivered"] },
          { id: "contingency_notes", label: "Contingency Notes", type: "textarea", required: false },
          { id: "comments", label: "Additional Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'invoicing_payment_processing',
        name: 'Invoicing and Payment Processing',
        description: 'Generate invoice and ETR receipt. Send to client and monitor payments. Handle reminders or revised terms if delays occur.',
        order: 10,
        role: 'tech_sales',
        phase: 'Finance Team',
        guidance: "Process invoicing and track payment status accurately",
        formFields: [
          { id: "invoice_number", label: "Invoice Number", type: "text", required: true },
          { id: "invoice_date", label: "Invoice Date", type: "date", required: true },
          { id: "invoice_document", label: "Invoice Document", type: "file", required: true },
          { id: "etr_receipt", label: "ETR Receipt", type: "file", required: false },
          { id: "expected_payment_date", label: "Expected Payment Date", type: "date", required: false },
          { id: "payment_status", label: "Payment Status", type: "select", required: true, options: ["Pending", "Paid", "Overdue", "Partial"] },
          { id: "payment_notes", label: "Payment Notes", type: "textarea", required: false },
          { id: "comments", label: "Additional Comments", type: "comments", required: false }
        ]
      },
      {
        id: 'post_delivery_followup',
        name: 'Post-Delivery Follow-Up',
        description: 'Confirm goods received by client. Gather satisfaction feedback. Address any complaints or issues.',
        order: 11,
        role: 'tech_sales',
        phase: 'Finance Team',
        guidance: "Follow up with clients to ensure satisfaction and address any issues",
        formFields: [
          { id: "followup_date", label: "Follow-Up Date", type: "date", required: true },
          { id: "satisfaction_level", label: "Satisfaction Level", type: "select", required: true, options: ["Satisfied", "Neutral", "Dissatisfied"] },
          { id: "customer_feedback", label: "Customer Feedback", type: "textarea", required: false },
          { id: "confirmation_proof", label: "Confirmation or Email Proof", type: "file", required: false },
          { id: "issues_reported", label: "Issues Reported", type: "checkbox", required: false },
          { id: "resolution_notes", label: "Resolution Notes", type: "textarea", required: false },
          { id: "comments", label: "Additional Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'completion_sale_documentation',
        name: 'Completion of Sale and Documentation',
        description: 'Update stock and financial records. Archive all transaction documents. Correct any discrepancies.',
        order: 12,
        role: 'tech_sales',
        phase: 'Finance Team',
        guidance: "Ensure all records are updated and documentation is properly archived",
        formFields: [
          { id: "completion_date", label: "Completion Date", type: "date", required: true },
          { id: "final_invoice", label: "Final Invoice", type: "file", required: false },
          { id: "stock_card_update", label: "Stock Card Update", type: "file", required: false },
          { id: "delivery_confirmation", label: "Delivery Confirmation", type: "file", required: false },
          { id: "all_records_updated", label: "All Records Updated", type: "checkbox", required: true },
          { id: "remarks_audit_notes", label: "Remarks / Audit Notes", type: "textarea", required: false },
          { id: "comments", label: "Final Comments", type: "comments", required: false }
        ]
      }
    ]
  },
  store_management_combined: {
    id: 'store_management_combined',
    name: 'Store Management Workflow',
    description: 'Complete end-to-end store management process from purchase request to final inventory reconciliation',
    steps: [
      {
        id: 'SM1',
        name: 'Submit Purchase Request',
        description: 'User enters vendor name, items, quantities, and justification for purchase.',
        order: 1,
        role: 'procurement',
        guidance: "Ensure all required information is provided and supplier is properly vetted",
        formFields: [
          { id: "assigned_user", label: "Assigned to", type: "user_select", required: true },
          { id: "assigned_user_2", label: "Second Assignee (Optional)", type: "user_select", required: false },
          { id: "vendorName", label: "Vendor Name", type: "text", required: true },
          { id: "itemsRequested", label: "Items Requested", type: "textarea", required: true },
          { id: "justification", label: "Justification for Purchase", type: "textarea", required: true },
          { id: "supplier_vetted", label: "Supplier vetted", type: "checkbox", required: true },
          { id: "prices_validated", label: "Prices validated", type: "checkbox", required: true },
          { id: "attachments", label: "Upload Requisition Document", type: "file", required: false },
          { id: "comments", label: "Additional Comments", type: "comments", required: false }
        ]
      },
      {
        id: 'SM2',
        name: 'Create and Send Purchase Order',
        description: 'Procurement generates and sends official PO to vendor.',
        order: 2,
        role: 'procurement',
        guidance: "Ensure PO details are accurate and sent to the correct vendor",
        formFields: [
          { id: "purchaseOrderNumber", label: "Purchase Order Number", type: "text", required: true },
          { id: "sendDate", label: "Send Date", type: "date", required: true },
          { id: "attachments", label: "Upload Purchase Order Document", type: "file", required: true },
          { id: "comments", label: "PO Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'SM3',
        name: 'Upload AWB/BL',
        description: 'Upload Air Waybill (AWB) or Bill of Lading (BL) for shipment tracking.',
        order: 3,
        role: 'procurement',
        guidance: "Upload the shipping document (AWB for air shipments or BL for sea shipments)",
        formFields: [
          { id: "shipment_type", label: "Shipment Type", type: "select", options: ["Air (AWB)", "Sea (BL)"], required: true },
          { id: "tracking_number", label: "Tracking/Reference Number", type: "text", required: true },
          { id: "estimated_arrival", label: "Estimated Arrival Date", type: "date", required: false },
          { id: "attachments", label: "Upload AWB/BL Document", type: "file", required: true },
          { id: "comments", label: "Shipping Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'SM4',
        name: 'Receive Goods',
        description: 'Store team receives shipment, verifies quantity and condition.',
        order: 4,
        role: 'store',
        guidance: "Ensure proper verification of quantities and condition of received goods",
        formFields: [
          { id: "item_count_verified", label: "Item count verified", type: "checkbox", required: true },
          { id: "no_visible_damage", label: "No visible damage", type: "checkbox", required: true },
          { id: "attachments", label: "Upload Goods Receipt", type: "file", required: true },
          { id: "comments", label: "Receipt Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'SM5',
        name: 'Inspect Goods',
        description: 'Store performs quality inspection and records findings.',
        order: 5,
        role: 'store',
        guidance: "Conduct thorough quality inspection and document all findings",
        formFields: [
          { id: "inspection_passed", label: "Inspection passed", type: "checkbox", required: true },
          { id: "attachments", label: "Upload Inspection Report", type: "file", required: true },
          { id: "comments", label: "Inspection Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'SM6',
        name: 'Match Invoice',
        description: 'Finance compares vendor invoice with order and received goods.',
        order: 6,
        role: 'finance',
        guidance: "Ensure accurate matching of invoice with order and received goods",
        formFields: [
          { id: "items_prices_match", label: "Items & prices match", type: "checkbox", required: true },
          { id: "no_discrepancies", label: "No discrepancies", type: "checkbox", required: true },
          { id: "attachments", label: "Upload Supplier Invoice", type: "file", required: true },
          { id: "comments", label: "Invoice Matching Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'SM7',
        name: 'Store Goods',
        description: 'Store arranges and stores goods according to FIFO (First In First Out) principles.',
        order: 7,
        role: 'store',
        guidance: "Ensure proper FIFO (First In First Out) storage arrangement",
        formFields: [
          { id: "fifo_arrangement", label: "FIFO Arrangement Applied (First In First Out)", type: "checkbox", required: true },
          { id: "storage_location", label: "Storage Location", type: "text", required: true },
          { id: "attachments", label: "Upload Storage Documentation", type: "file", required: false },
          { id: "comments", label: "Storage Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'SM8',
        name: 'Update Inventory Records',
        description: 'Update stock cards and inventory records with received goods.',
        order: 8,
        role: 'store',
        guidance: "Ensure accurate inventory record updates",
        formFields: [
          { id: "stock_cards_updated", label: "Stock Cards Updated", type: "checkbox", required: true },
          { id: "inventory_records_updated", label: "Inventory Records Updated", type: "checkbox", required: true },
          { id: "attachments", label: "Upload Updated Records", type: "file", required: false },
          { id: "comments", label: "Record Update Notes", type: "comments", required: false }
        ]
      },
      {
        id: 'SM9',
        name: 'Final Inventory Reconciliation',
        description: 'Conduct final reconciliation of inventory records.',
        order: 9,
        role: 'admin',
        guidance: "Ensure complete and accurate inventory reconciliation",
        formFields: [
          { id: "reconciliation_completed", label: "Reconciliation Completed", type: "checkbox", required: true },
          { id: "all_records_verified", label: "All Records Verified", type: "checkbox", required: true },
          { id: "attachments", label: "Upload Reconciliation Report", type: "file", required: false },
          { id: "comments", label: "Final Comments", type: "comments", required: false }
        ]
      }
    ]
  }
};

// Active workflow instances
const workflowInstances = [];

// Document attachments
const documentAttachments = {};

// Task management functions
const getMyTasks = (user, instances) => {
  if (!user || !instances) return [];
  
  // First, deduplicate instances by ID before processing
  const uniqueInstances = Array.from(
    new Map(instances.map(inst => [String(inst.id || inst._id), inst])).values()
  );
  
  if (uniqueInstances.length !== instances.length) {
    console.warn(`⚠️ [GET_MY_TASKS] Found ${instances.length - uniqueInstances.length} duplicate instances. Deduplicated to ${uniqueInstances.length} unique instances.`);
  }
  
  const myTasks = [];
  const seenInstanceIds = new Set();
  const seenTaskIds = new Set();
  uniqueInstances.forEach(instance => {
    const instanceKey = String(instance.id || instance._id);
    if (seenInstanceIds.has(instanceKey)) {
      console.warn('⚠️ [GET_MY_TASKS] Duplicate instance ID found after deduplication:', instanceKey);
      return; // Skip duplicate
    }
    seenInstanceIds.add(instanceKey);
    // Derive workflowId if backend doesn't provide it
    // Priority: 1) instance.workflowId (from database), 2) derive from workflowType (backward compatibility)
    let derivedWorkflowId = instance.workflowId;
    if (!derivedWorkflowId) {
      // Fallback for old workflows without workflowId field
      if (instance.workflowType === 'store_management') {
        derivedWorkflowId = 'store_management_combined';
      } else {
        // Try to derive from title or other fields
        derivedWorkflowId = undefined;
      }
    }
    
    const workflowDef = workflowDefinitions[derivedWorkflowId];
    if (!workflowDef) return;
    
    // All workflows are now viewable by everyone - no user filtering
    
    // For completed workflows, show as single unified task
    if (instance.status === 'completed') {
      
      // Use the same getAssignedUser logic for completed workflows
      const getAssignedUserForCompleted = () => {
        // PRIORITY 1: Check stepData for assigned_user in any step
        if (instance.stepData) {
          // First, try to find the first step in the workflow definition that has assigned_user field
          const workflowDef = workflowDefinitions[derivedWorkflowId];
          if (workflowDef && workflowDef.steps && workflowDef.steps.length > 0) {
            // Find the first step that has assigned_user field
            const firstStepWithAssignedUser = workflowDef.steps.find(step => 
              step.formFields && step.formFields.some(field => field.id === 'assigned_user')
            );
            
            if (firstStepWithAssignedUser) {
              const firstStepId = firstStepWithAssignedUser.id;
              const stepData = instance.stepData[firstStepId];
              if (stepData && stepData.formData && stepData.formData.assigned_user) {
                console.log('✅ [GET_MY_TASKS] Found assigned_user in completed workflow first step', firstStepId, ':', stepData.formData.assigned_user);
                return stepData.formData.assigned_user;
              }
            }
          }
          
          // Fallback: Check all steps for assigned_user
          for (const stepId in instance.stepData) {
            const stepData = instance.stepData[stepId];
            if (stepData && stepData.formData && stepData.formData.assigned_user) {
              console.log('✅ [GET_MY_TASKS] Found assigned_user in completed workflow step', stepId, ':', stepData.formData.assigned_user);
              return stepData.formData.assigned_user;
            }
          }
        }
        
        // PRIORITY 2: Check instance formData (legacy)
        if (instance.formData && instance.formData.assigned_user) {
          return instance.formData.assigned_user;
        }
        
        // PRIORITY 3: Check backend populated assignedTo (now an array)
        if (instance.assignedTo) {
          if (Array.isArray(instance.assignedTo)) {
            // Handle array of assigned users
            const userNames = instance.assignedTo
              .map(user => {
                if (typeof user === 'object' && user.name) return user.name;
                if (typeof user === 'object' && user.email) return user.email;
                if (typeof user === 'string') return getUserNameFromUsername(user);
                return null;
              })
              .filter(Boolean);
            return userNames.length > 0 ? userNames.join(', ') : 'Unassigned';
          } else if (typeof instance.assignedTo === 'object' && instance.assignedTo.name) {
            return instance.assignedTo.name;
          } else if (typeof instance.assignedTo === 'object' && instance.assignedTo.email) {
            return instance.assignedTo.email;
          } else if (typeof instance.assignedTo === 'string') {
            return instance.assignedTo;
          }
        }
        
        // Final fallback to startedBy
        return instance.startedBy || 'Unassigned';
      };
      
      const assignedUser = getAssignedUserForCompleted();
      
      // Extract customer_name for completed workflows
      let customerName = null;
      if (instance.workflowId === 'technical_sales' && instance.stepData && instance.stepData.make_inquiry) {
        customerName = instance.stepData.make_inquiry.formData?.customer_name;
      } else if (instance.stepData) {
        // Check all steps for customer_name
        for (const stepId in instance.stepData) {
          const stepData = instance.stepData[stepId];
          if (stepData && stepData.formData && stepData.formData.customer_name) {
            customerName = stepData.formData.customer_name;
            break;
          }
        }
      }
      
      // Extract itemsRequested for completed store management workflows
      let itemsRequested = null;
      // Extract vendorName for completed store management workflows (company identifier)
      let vendorName = null;
      const isStoreManagement = instance.workflowId === 'store_management_combined' || 
                                instance.workflowId === 'store_management' ||
                                derivedWorkflowId === 'store_management_combined';
      
      if (isStoreManagement && instance.stepData) {
        // Try to get itemsRequested and vendorName from SM1 step
        if (instance.stepData.SM1 && instance.stepData.SM1.formData) {
          itemsRequested = instance.stepData.SM1.formData.itemsRequested;
          vendorName = instance.stepData.SM1.formData.vendorName;
        } else {
          // Fallback to first step
          const firstStepId = Object.keys(instance.stepData)[0];
          if (firstStepId && instance.stepData[firstStepId] && instance.stepData[firstStepId].formData) {
            if (!itemsRequested) {
              itemsRequested = instance.stepData[firstStepId].formData.itemsRequested;
            }
            if (!vendorName) {
              vendorName = instance.stepData[firstStepId].formData.vendorName;
            }
          }
        }
      }
      
      const completedTask = {
        id: `completed_${instance.id}`,
        instanceId: instance.id,
        workflowId: instance.workflowId,
        workflowName: workflowDef.name,
        stepName: `${workflowDef.name} - Completed`,
        stepDescription: `Completed workflow with ${instance.completedSteps?.length || 0} steps`,
        status: 'completed',
        assignedTo: assignedUser,
        completedAt: instance.completedAt,
        formData: instance.stepData,
        workflowType: 'completed',
        customer_name: customerName || null,
        itemsRequested: itemsRequested || null,
        vendorName: vendorName || null // Include vendorName for store management workflows (company identifier)
      };
      
      myTasks.push(completedTask);
      return;
    }
    
    // Debug: Log status for in-progress workflows
    if (instance.status) {
      console.log('📋 [GET_MY_TASKS] In-progress workflow status:', {
        instanceId: instance.id,
        status: instance.status,
        workflowId: instance.workflowId,
        statusType: typeof instance.status
      });
    }
    
    // For in-progress workflows, show as single workflow task (not individual steps)
    const getWorkflowIdentifier = () => {
      let formData = instance.formData;
      if (!formData && instance.stepData) {
        const firstStepId = Object.keys(instance.stepData)[0];
        if (firstStepId && instance.stepData[firstStepId]) {
          formData = instance.stepData[firstStepId].formData;
        }
      }
      
      if (formData) {
        switch (instance.workflowId) {
          case 'import':
            return formData.import_item || 'Import Item';
          case 'sales':
            return formData.deal_name || 'Sales Deal';
          case 'technical_sales':
            return formData.project_name || 'Technical Project';
          case 'store':
            return formData.shipment_reference || 'Shipment';
          case 'store_management':
            return formData.vendorName || 'Store Management';
          default:
            return 'Workflow';
        }
      }
      return 'Workflow';
    };

    const getAssignedUser = () => {
      // PRIORITY 1: Check stepData for assigned_user in any step
      if (instance.stepData) {
        // First, try to find the first step in the workflow definition that has assigned_user field
        // This works for both Technical Sales (make_inquiry) and Store Management (any first step)
        const workflowDef = workflowDefinitions[derivedWorkflowId];
        if (workflowDef && workflowDef.steps && workflowDef.steps.length > 0) {
          // Find the first step that has assigned_user field
          const firstStepWithAssignedUser = workflowDef.steps.find(step => 
            step.formFields && step.formFields.some(field => field.id === 'assigned_user')
          );
          
          if (firstStepWithAssignedUser) {
            const firstStepId = firstStepWithAssignedUser.id;
            const stepData = instance.stepData[firstStepId];
            if (stepData && stepData.formData && stepData.formData.assigned_user) {
              console.log('✅ [GET_MY_TASKS] Found assigned_user in first step', firstStepId, ':', stepData.formData.assigned_user);
              return stepData.formData.assigned_user;
            }
          }
        }
        
        // Fallback: Check all steps for assigned_user (in case it's in a different step)
        for (const stepId in instance.stepData) {
          const stepData = instance.stepData[stepId];
          if (stepData && stepData.formData && stepData.formData.assigned_user) {
            console.log('✅ [GET_MY_TASKS] Found assigned_user in step', stepId, ':', stepData.formData.assigned_user);
            return stepData.formData.assigned_user;
          }
        }
      }
      
      // PRIORITY 2: Check instance formData (legacy)
      if (instance.formData && instance.formData.assigned_user) {
        return instance.formData.assigned_user;
      }
      
      // PRIORITY 3: Check backend populated assignedTo (creator/owner) - only if no assigned_user found
      // (Don't show creator/owner if user specifically assigned to someone else)
      // Now handles array of assigned users
      if (instance.assignedTo) {
        if (Array.isArray(instance.assignedTo)) {
          const userNames = instance.assignedTo
            .map(user => {
              if (typeof user === 'object' && user.name) return user.name;
              if (typeof user === 'object' && user.email) return user.email;
              if (typeof user === 'string') return getUserNameFromUsername(user);
              return null;
            })
            .filter(Boolean);
          return userNames.length > 0 ? userNames.join(', ') : 'Unassigned';
        } else if (typeof instance.assignedTo === 'object' && instance.assignedTo.name) {
          return instance.assignedTo.name;
        } else if (typeof instance.assignedTo === 'object' && instance.assignedTo.email) {
          return instance.assignedTo.email;
        } else if (typeof instance.assignedTo === 'string') {
          return instance.assignedTo;
        }
      }
      
      // Final fallback to startedBy
      return instance.startedBy || 'Unassigned';
    };
    
    const assignedUser = getAssignedUser();
    const workflowIdentifier = getWorkflowIdentifier();
    
    // Get formData for project_name and customer_name
    let formData = instance.formData;
    if (!formData && instance.stepData) {
      const firstStepId = Object.keys(instance.stepData)[0];
      if (firstStepId && instance.stepData[firstStepId]) {
        formData = instance.stepData[firstStepId].formData;
      }
    }
    
    // Extract customer_name from formData or stepData
    // For technical_sales, customer_name is in the make_inquiry step
    let customerName = null;
    if (instance.workflowId === 'technical_sales') {
      // Try to get customer_name from make_inquiry step
      if (instance.stepData && instance.stepData.make_inquiry && instance.stepData.make_inquiry.formData) {
        customerName = instance.stepData.make_inquiry.formData.customer_name;
      }
      // Fallback to formData if available
      if (!customerName && formData) {
        customerName = formData.customer_name;
      }
    } else if (instance.stepData) {
      // For other workflows, check all steps for customer_name
      for (const stepId in instance.stepData) {
        const stepData = instance.stepData[stepId];
        if (stepData && stepData.formData && stepData.formData.customer_name) {
          customerName = stepData.formData.customer_name;
          break;
        }
      }
      // Fallback to formData
      if (!customerName && formData) {
        customerName = formData.customer_name;
      }
    } else if (formData) {
      customerName = formData.customer_name;
    }
    
    // Extract itemsRequested for store management workflows
    // For store_management_combined, itemsRequested is in the SM1 step
    let itemsRequested = null;
    // Extract vendorName for store management workflows (company identifier)
    let vendorName = null;
    const isStoreManagement = instance.workflowId === 'store_management_combined' || 
                              instance.workflowId === 'store_management' ||
                              derivedWorkflowId === 'store_management_combined';
    
    if (isStoreManagement) {
      // Try to get itemsRequested and vendorName from SM1 step (first step)
      if (instance.stepData && instance.stepData.SM1 && instance.stepData.SM1.formData) {
        itemsRequested = instance.stepData.SM1.formData.itemsRequested;
        vendorName = instance.stepData.SM1.formData.vendorName;
      }
      // Fallback to first step if SM1 doesn't exist
      if ((!itemsRequested || !vendorName) && instance.stepData) {
        const firstStepId = Object.keys(instance.stepData)[0];
        if (firstStepId && instance.stepData[firstStepId] && instance.stepData[firstStepId].formData) {
          if (!itemsRequested) {
            itemsRequested = instance.stepData[firstStepId].formData.itemsRequested;
          }
          if (!vendorName) {
            vendorName = instance.stepData[firstStepId].formData.vendorName;
          }
        }
      }
      // Fallback to formData if available
      if (!itemsRequested && formData) {
        itemsRequested = formData.itemsRequested;
      }
      if (!vendorName && formData) {
        vendorName = formData.vendorName;
      }
    }
    
    const taskId = `workflow_${instance.id}`;
    
    // Prevent duplicate task IDs
    if (seenTaskIds.has(taskId)) {
      console.warn('⚠️ [GET_MY_TASKS] Duplicate task ID found, skipping:', taskId);
      return;
    }
    seenTaskIds.add(taskId);
    
    const workflowTask = {
      id: taskId,
      instanceId: instance.id,
      workflowId: instance.workflowId,
      workflowName: workflowDef.name,
      stepName: workflowIdentifier,
      stepDescription: `In-progress workflow`,
      status: instance.status || 'in_progress',
      assignedTo: assignedUser,
      createdAt: instance.createdAt,
      updatedAt: instance.updatedAt,
      formData: instance.formData || instance.stepData,
      workflowType: instance.workflowId,
      project_name: formData?.project_name || null, // Include project_name for display
      customer_name: customerName || null, // Include customer_name for display
      itemsRequested: itemsRequested || null, // Include itemsRequested for store management workflows
      vendorName: vendorName || null // Include vendorName for store management workflows (company identifier)
    };
    
    myTasks.push(workflowTask);
  });
  
  return myTasks.sort((a, b) => {
    return new Date(a.dueAt || a.dueDate || 0) - new Date(b.dueAt || b.dueDate || 0);
  });
};

// Group workflows by company name
const groupWorkflowsByCompany = (tasks, workflowTypeTab, users = []) => {
  const getUserNameFromUsername = (username) => {
    if (!username || !users || users.length === 0) return username;
    const user = users.find(u => u.username === username || u.email === username);
    return user ? user.name : username;
  };

  const getAssignedUserDisplay = (task) => {
    if (task.assignedTo && task.assignedTo !== 'Unassigned') {
      // Handle array of assigned users
      if (Array.isArray(task.assignedTo)) {
        const userNames = task.assignedTo
          .map(user => {
            if (typeof user === 'object' && user.name) return user.name;
            if (typeof user === 'object' && user.email) return user.email;
            if (typeof user === 'string') return getUserNameFromUsername(user);
            return null;
          })
          .filter(Boolean);
        return userNames.length > 0 ? userNames.join(', ') : 'Unassigned';
      } else if (typeof task.assignedTo === 'object' && task.assignedTo.name) {
        return task.assignedTo.name;
      } else if (typeof task.assignedTo === 'object' && task.assignedTo.email) {
        return task.assignedTo.email;
      } else if (typeof task.assignedTo === 'string') {
        return getUserNameFromUsername(task.assignedTo);
      }
    }
    if (task.formData && task.formData.assigned_user) {
      return getUserNameFromUsername(task.formData.assigned_user);
    }
    if (task.assigned_user) {
      return getUserNameFromUsername(task.assigned_user);
    }
    return 'Unassigned';
  };

  const getCompanyIdentifier = (task) => {
    // For Technical Sales, use customer_name
    if (task.workflowId === 'technical_sales' || task.workflowType === 'technical_sales') {
      return task.customer_name;
    }
    // For Store Management, use vendorName
    const isStoreManagement = 
      task.workflowId === 'store_management_combined' ||
      task.workflowId === 'store_management' ||
      task.workflowType === 'store_management_combined' ||
      task.workflowType === 'store_management';
    if (isStoreManagement) {
      return task.vendorName;
    }
    return null;
  };

  const getCompanyStatus = (workflows) => {
    const hasInProgress = workflows.some(w => {
      const status = w.task.status || w.task.workflowType;
      return status === 'in_progress' || (status !== 'completed' && status !== 'closed');
    });
    return hasInProgress ? 'In Progress' : 'Completed';
  };

  const getPrimaryAssignee = (workflows) => {
    if (workflows.length === 0) return 'Unassigned';
    
    // Sort by date (most recent first)
    const sorted = [...workflows].sort((a, b) => {
      const getDate = (w) => {
        if (w.task.status === 'in_progress' || w.task.workflowType === 'in_progress') {
          return new Date(w.task.updatedAt || w.task.createdAt || 0);
        }
        return new Date(w.task.completedAt || w.task.updatedAt || w.task.createdAt || 0);
      };
      return getDate(b) - getDate(a);
    });
    
    // Prioritize in-progress workflows
    const inProgress = sorted.find(w => {
      const status = w.task.status || w.task.workflowType;
      return status === 'in_progress' || (status !== 'completed' && status !== 'closed');
    });
    
    if (inProgress) {
      return getAssignedUserDisplay(inProgress.task);
    }
    
    // Otherwise, most recent completed
    return getAssignedUserDisplay(sorted[0].task);
  };

  const getActivityDate = (workflow) => {
    const task = workflow.task;
    if (task.status === 'in_progress' || task.workflowType === 'in_progress') {
      return new Date(task.updatedAt || task.createdAt || 0);
    }
    return new Date(task.completedAt || task.updatedAt || task.createdAt || 0);
  };

  // Group tasks by company identifier
  const grouped = {};
  const seenInstanceIds = new Set(); // Track seen instance IDs to prevent duplicates
  
  tasks.forEach(task => {
    const companyName = getCompanyIdentifier(task);
    if (!companyName) return; // Skip if no company identifier
    
    // Deduplicate by instanceId - only add each workflow instance once per company
    const instanceId = task.instanceId;
    if (instanceId) {
      const instanceKey = `${companyName.trim()}_${instanceId}`;
      if (seenInstanceIds.has(instanceKey)) {
        console.warn('⚠️ [GROUP_WORKFLOWS] Duplicate workflow instance found, skipping:', {
          companyName,
          instanceId,
          taskId: task.id
        });
        return; // Skip duplicate instance
      }
      seenInstanceIds.add(instanceKey);
    }
    
    const normalizedName = companyName.trim();
    if (!grouped[normalizedName]) {
      grouped[normalizedName] = [];
    }
    
    // Determine workflow type
    let workflowType = 'technical_sales';
    const isStoreManagement = 
      task.workflowId === 'store_management_combined' ||
      task.workflowId === 'store_management' ||
      task.workflowType === 'store_management_combined' ||
      task.workflowType === 'store_management';
    if (isStoreManagement) {
      workflowType = 'store_management';
    }
    
    grouped[normalizedName].push({
      task,
      type: workflowType,
      status: task.status || task.workflowType
    });
  });

  // Convert to array and calculate company properties
  const companies = Object.keys(grouped).map(companyName => {
    let workflows = grouped[companyName];
    
    // Additional deduplication: If multiple workflows have the same instanceId, keep only one
    // Also, if multiple workflows have the same workflowId and customer_name, keep the most recent/complete one
    const workflowsByInstanceId = {};
    const workflowsByWorkflowId = {};
    
    workflows.forEach(workflow => {
      const instanceId = workflow.task?.instanceId;
      const workflowId = workflow.task?.workflowId;
      
      // Group by instanceId first (most specific)
      if (instanceId) {
        if (!workflowsByInstanceId[instanceId]) {
          workflowsByInstanceId[instanceId] = [];
        }
        workflowsByInstanceId[instanceId].push(workflow);
      }
      
      // Also group by workflowId + company for additional deduplication
      if (workflowId) {
        const key = `${workflowId}_${companyName}`;
        if (!workflowsByWorkflowId[key]) {
          workflowsByWorkflowId[key] = [];
        }
        workflowsByWorkflowId[key].push(workflow);
      }
    });
    
    // Deduplicate: For each instanceId, keep only the best workflow (most recent or most complete)
    const deduplicatedWorkflows = [];
    const processedInstanceIds = new Set();
    
    workflows.forEach(workflow => {
      const instanceId = workflow.task?.instanceId;
      
      if (instanceId && workflowsByInstanceId[instanceId] && workflowsByInstanceId[instanceId].length > 1) {
        // Multiple workflows with same instanceId - pick the best one
        if (!processedInstanceIds.has(instanceId)) {
          processedInstanceIds.add(instanceId);
          
          // Sort by: completed first, then by most recent update
          const sorted = workflowsByInstanceId[instanceId].sort((a, b) => {
            const aCompleted = a.task?.status === 'completed' ? 1 : 0;
            const bCompleted = b.task?.status === 'completed' ? 1 : 0;
            if (aCompleted !== bCompleted) return bCompleted - aCompleted;
            
            const aDate = new Date(a.task?.updatedAt || a.task?.createdAt || 0).getTime();
            const bDate = new Date(b.task?.updatedAt || b.task?.createdAt || 0).getTime();
            return bDate - aDate;
          });
          
          deduplicatedWorkflows.push(sorted[0]); // Take the best one
        }
      } else if (!instanceId || !processedInstanceIds.has(instanceId)) {
        // No instanceId or first time seeing this instanceId
        if (instanceId) processedInstanceIds.add(instanceId);
        deduplicatedWorkflows.push(workflow);
      }
    });
    
    // Additional check: If we still have duplicates by workflowId+company, deduplicate further
    const finalWorkflows = [];
    const seenWorkflowKeys = new Set();
    
    deduplicatedWorkflows.forEach(workflow => {
      const workflowId = workflow.task?.workflowId;
      const key = `${workflowId}_${companyName}`;
      
      if (workflowId && workflowsByWorkflowId[key] && workflowsByWorkflowId[key].length > 1) {
        if (!seenWorkflowKeys.has(key)) {
          seenWorkflowKeys.add(key);
          // Pick the most recent/complete one
          const sorted = workflowsByWorkflowId[key].sort((a, b) => {
            const aCompleted = a.task?.status === 'completed' ? 1 : 0;
            const bCompleted = b.task?.status === 'completed' ? 1 : 0;
            if (aCompleted !== bCompleted) return bCompleted - aCompleted;
            const aDate = new Date(a.task?.updatedAt || a.task?.createdAt || 0).getTime();
            const bDate = new Date(b.task?.updatedAt || b.task?.createdAt || 0).getTime();
            return bDate - aDate;
          });
          finalWorkflows.push(sorted[0]);
        }
      } else {
        finalWorkflows.push(workflow);
      }
    });
    
    workflows = finalWorkflows.length > 0 ? finalWorkflows : deduplicatedWorkflows;
    
    return {
      companyName,
      status: getCompanyStatus(workflows),
      primaryAssignee: getPrimaryAssignee(workflows),
      workflows,
      mostRecentActivity: Math.max(...workflows.map(w => getActivityDate(w).getTime()))
    };
  });

  // Sort: In Progress companies first (by most recent activity), then Completed
  companies.sort((a, b) => {
    if (a.status === 'In Progress' && b.status === 'Completed') return -1;
    if (a.status === 'Completed' && b.status === 'In Progress') return 1;
    return b.mostRecentActivity - a.mostRecentActivity;
  });

  return companies;
};

// Status Badge Renderer Function
const renderStatusBadge = (status) => {
  const normalized = (status || '').toUpperCase().trim();
  
  if (normalized === 'COMPLETED' || normalized === 'CLOSED') {
    return (
      <span className="status-badge status-badge--completed">COMPLETED</span>
    );
  }
  
  if (normalized === 'IN PROGRESS' || normalized === 'IN_PROGRESS' || normalized === 'OPEN') {
    return (
      <span className="status-badge status-badge--in-progress">IN PROGRESS</span>
    );
  }
  
  // Fallback for any other status
  return (
    <span className="status-badge">{normalized}</span>
  );
};

// Company Workflows Table Component
const CompanyWorkflowsTable = ({ companies, onCompanyClick, users = [] }) => {
  if (companies.length === 0) {
    return (
      <div className="empty-tasks-table">
        <FaClipboardList className="empty-icon" />
        <h3>No Companies Found</h3>
        <p>You don't have any workflows assigned at the moment.</p>
      </div>
    );
  }

  return (
    <div className="workflow-tasks-table-container">
      <table className="workflow-tasks-table">
        <thead>
          <tr>
            <th>Company Name</th>
            <th>Assigned To</th>
            <th className="status-header">Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {companies.map(company => (
            <tr key={company.companyName} className="company-row">
              <td className="identifier-cell">
                <span 
                  className="identifier-text clickable-company"
                  onClick={() => onCompanyClick(company.companyName, company)}
                  title="Click to view all workflows for this company"
                >
                  {company.companyName}
                </span>
              </td>
              <td className="assigned-cell">
                <FaUser className="assignment-icon-small" />
                <span>{company.primaryAssignee}</span>
              </td>
              <td className="status-cell">
                {renderStatusBadge(company.status)}
              </td>
              <td className="actions-cell">
                <button 
                  className="view-company-btn-table"
                  onClick={() => onCompanyClick(company.companyName, company)}
                  title="Open all workflows"
                >
                  <FaEye className="btn-icon" />
                  Open
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Workflow Tasks Table Component (kept for backward compatibility if needed)
const WorkflowTasksTable = ({ tasks, workflowType, onStartTask, onDeleteTask, users = [] }) => {
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  const canDeleteWorkflow = currentUser?.role === 'super';

  const getAssignedUserDisplay = (task) => {
    const getUserNameFromUsername = (username) => {
      if (!username || !users || users.length === 0) return username;
      const user = users.find(u => u.username === username || u.email === username);
      return user ? user.name : username;
    };
    
    if (task.assignedTo && task.assignedTo !== 'Unassigned') {
      if (typeof task.assignedTo === 'object' && task.assignedTo.name) {
        return task.assignedTo.name;
      }
      if (typeof task.assignedTo === 'object' && task.assignedTo.email) {
        return task.assignedTo.email;
      }
      if (typeof task.assignedTo === 'string') {
        return getUserNameFromUsername(task.assignedTo);
      }
    }
    
    if (task.formData && task.formData.assigned_user) {
      return getUserNameFromUsername(task.formData.assigned_user);
    }
    
    if (task.assigned_user) {
      return getUserNameFromUsername(task.assigned_user);
    }
    
    return 'Unassigned';
  };

  const getIdentifier = (task) => {
    // For Technical Sales, show customer_name
    if (workflowType === 'Technical Sales Workflow') {
      return task.customer_name || 'N/A';
    }
    // For Store Management, show itemsRequested
    if (workflowType === 'Store Management Workflow') {
      return task.itemsRequested || 'N/A';
    }
    return 'N/A';
  };

  const getStatusDisplay = (task) => {
    const isCompleted = task.status === 'completed' || task.workflowType === 'completed';
    return isCompleted ? 'COMPLETED' : 'IN PROGRESS';
  };

  if (tasks.length === 0) {
    return (
      <div className="empty-tasks-table">
        <FaClipboardList className="empty-icon" />
        <h3>No {workflowType} Tasks</h3>
        <p>You don't have any {workflowType.toLowerCase()} tasks assigned at the moment.</p>
      </div>
    );
  }

  return (
    <div className="workflow-tasks-table-container">
      <table className="workflow-tasks-table">
        <thead>
          <tr>
            <th>
              {workflowType === 'Technical Sales Workflow' ? 'Customer Name' : 'Items Requested'}
            </th>
            <th>Assigned To</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map(task => {
            const isCompleted = task.status === 'completed' || task.workflowType === 'completed';
            const assignedUser = getAssignedUserDisplay(task);
            const identifier = getIdentifier(task);
            const statusDisplay = getStatusDisplay(task);

            return (
              <tr key={task.id} className={`task-row ${isCompleted ? 'completed' : 'in-progress'}`}>
                <td className="identifier-cell">
                  <span className="identifier-text">{identifier}</span>
                </td>
                <td className="assigned-cell">
                  <FaUser className="assignment-icon-small" />
                  <span>{assignedUser}</span>
                </td>
                <td className="status-cell">
                  <span className={`status-badge-table ${isCompleted ? 'completed' : 'in-progress'}`}>
                    {statusDisplay}
                  </span>
                </td>
                <td className="actions-cell">
                  <div className="table-actions">
                    {isCompleted ? (
                      <>
                        <button 
                          className="view-workflow-btn-table"
                          onClick={() => onStartTask(task)}
                          title="Open Workflow"
                        >
                          <FaEye className="btn-icon" />
                          Open
                        </button>
                        <button 
                          className="edit-workflow-btn-table"
                          onClick={() => onStartTask({...task, editMode: true})}
                          title="Edit Workflow"
                        >
                          <FaEdit className="btn-icon" />
                          Edit
                        </button>
                        {canDeleteWorkflow && (
                          <button 
                            className="delete-task-btn-table"
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this workflow? This action cannot be undone.')) {
                                onDeleteTask && onDeleteTask(task);
                              }
                            }}
                            title="Delete Workflow"
                          >
                            <FaTrash className="btn-icon" />
                            Delete
                          </button>
                        )}
                      </>
                    ) : task.status === 'in_progress' ? (
                      <>
                        <button 
                          className="start-task-btn-table"
                          onClick={() => onStartTask(task)}
                          title="Continue Workflow"
                        >
                          <FaPlay className="btn-icon" />
                          Continue
                        </button>
                        {canDeleteWorkflow && (
                          <button 
                            className="delete-task-btn-table"
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
                                onDeleteTask && onDeleteTask(task);
                              }
                            }}
                            title="Delete Task"
                          >
                            <FaTrash className="btn-icon" />
                            Delete
                          </button>
                        )}
                      </>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Company Workflows Drawer Component
const CompanyWorkflowsDrawer = ({ 
  isOpen, 
  onClose, 
  companyName, 
  companyData, 
  onStartNewWorkflow, 
  onViewWorkflow, 
  onDeleteWorkflow,
  users = [],
  allTasks = []
}) => {
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  const canDeleteWorkflow = currentUser?.role === 'super';

  const getAssignedUserDisplay = (task) => {
    const getUserNameFromUsername = (username) => {
      if (!username || !users || users.length === 0) return username;
      const user = users.find(u => u.username === username || u.email === username);
      return user ? user.name : username;
    };
    
    if (task.assignedTo && task.assignedTo !== 'Unassigned') {
      if (typeof task.assignedTo === 'object' && task.assignedTo.name) {
        return task.assignedTo.name;
      }
      if (typeof task.assignedTo === 'object' && task.assignedTo.email) {
        return task.assignedTo.email;
      }
      if (typeof task.assignedTo === 'string') {
        return getUserNameFromUsername(task.assignedTo);
      }
    }
    if (task.formData && task.formData.assigned_user) {
      return getUserNameFromUsername(task.formData.assigned_user);
    }
    if (task.assigned_user) {
      return getUserNameFromUsername(task.assigned_user);
    }
    return 'Unassigned';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getWorkflowTypeName = (workflow) => {
    if (workflow.type === 'technical_sales') {
      return 'Technical Sales Workflow';
    }
    if (workflow.type === 'store_management') {
      return 'Store Management Workflow';
    }
    return workflow.task.workflowName || 'Workflow';
  };

  if (!isOpen || !companyData) return null;

  // Deduplicate workflows by instanceId to prevent showing the same workflow multiple times
  const workflowsRaw = companyData.workflows || [];
  const seenWorkflowIds = new Set();
  const workflows = workflowsRaw.filter(workflow => {
    const instanceId = workflow.task?.instanceId;
    if (!instanceId) return true; // Keep workflows without instanceId (shouldn't happen)
    
    if (seenWorkflowIds.has(instanceId)) {
      console.warn('⚠️ [DRAWER] Duplicate workflow instance found in drawer, skipping:', instanceId);
      return false; // Skip duplicate
    }
    seenWorkflowIds.add(instanceId);
    return true;
  });

  return (
    <>
      <div className={`company-drawer-overlay ${isOpen ? 'open' : ''}`} onClick={onClose}></div>
      <div className={`company-drawer ${isOpen ? 'open' : ''}`}>
        <div className="company-drawer-header">
          <h3>{companyName} - All Workflows</h3>
          <button className="company-drawer-close" onClick={onClose} title="Close">
            <span>&times;</span>
          </button>
        </div>
        
        <div className="company-drawer-content">
          <div className="company-drawer-actions">
            <button 
              className="start-new-workflow-btn"
              onClick={() => onStartNewWorkflow(companyName, companyData)}
            >
              <FaPlus className="btn-icon" />
              Start New Workflow
            </button>
          </div>

          {workflows.length === 0 ? (
            <div className="company-drawer-empty">
              <FaClipboardList className="empty-icon" />
              <h4>No workflows yet</h4>
              <p>Start the first workflow for this company.</p>
            </div>
          ) : (
            <div className="company-workflows-list">
              <h4>Workflows ({workflows.length})</h4>
              {workflows.map((workflow, index) => {
                const task = workflow.task;
                const isCompleted = task.status === 'completed' || task.workflowType === 'completed';
                const assignedUser = getAssignedUserDisplay(task);
                const workflowTypeName = getWorkflowTypeName(workflow);
                const dateLabel = isCompleted ? 'Completed' : 'Created';
                const dateValue = isCompleted ? task.completedAt : task.createdAt;

                return (
                  <div key={task.id || index} className="company-workflow-card">
                    <div className="workflow-card-header">
                      <h5>{workflowTypeName}</h5>
                      <span className={`workflow-status-badge ${isCompleted ? 'completed' : 'in-progress'}`}>
                        {isCompleted ? 'COMPLETED' : 'IN PROGRESS'}
                      </span>
                    </div>
                    <div className="workflow-card-details">
                      <div className="workflow-detail-item">
                        <FaUser className="detail-icon" />
                        <span>Assigned: {assignedUser}</span>
                      </div>
                      <div className="workflow-detail-item">
                        <FaClock className="detail-icon" />
                        <span>{dateLabel}: {formatDate(dateValue)}</span>
                      </div>
                    </div>
                    <div className="workflow-card-actions">
                      {isCompleted ? (
                        <>
                          <button 
                            className="view-workflow-btn"
                            onClick={() => {
                              onClose();
                              // Small delay to ensure drawer closes before workflow opens
                              setTimeout(() => {
                                onViewWorkflow(task);
                              }, 100);
                            }}
                            title="Open Workflow"
                          >
                            <FaEye className="btn-icon" />
                            Open
                          </button>
                          <button 
                            className="edit-workflow-btn"
                            onClick={() => {
                              onClose();
                              // Small delay to ensure drawer closes before workflow opens
                              setTimeout(() => {
                                onViewWorkflow({...task, editMode: true});
                              }, 100);
                            }}
                            title="Edit Workflow"
                          >
                            <FaEdit className="btn-icon" />
                            Edit
                          </button>
                          {canDeleteWorkflow && (
                            <button 
                              className="delete-workflow-btn"
                              onClick={() => {
                                console.log('🗑️ [DELETE_BUTTON] Delete button clicked, task object:', task);
                                if (window.confirm('Are you sure you want to delete this workflow? This action cannot be undone.')) {
                                  if (onDeleteWorkflow) {
                                    onDeleteWorkflow(task);
                                  } else {
                                    console.error('❌ [DELETE_BUTTON] onDeleteWorkflow handler is not defined!');
                                    alert('Error: Delete handler is not available. Please refresh the page.');
                                  }
                                }
                              }}
                              title="Delete Workflow"
                            >
                              <FaTrash className="btn-icon" />
                              Delete
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <button 
                            className="start-task-btn"
                            onClick={() => {
                              onClose();
                              // Small delay to ensure drawer closes before workflow opens
                              setTimeout(() => {
                                onViewWorkflow(task);
                              }, 100);
                            }}
                            title="Continue Workflow"
                          >
                            <FaPlay className="btn-icon" />
                            Continue
                          </button>
                          {canDeleteWorkflow && (
                            <button 
                              className="delete-workflow-btn"
                              onClick={() => {
                                console.log('🗑️ [DELETE_BUTTON] Delete button clicked, task object:', task);
                                if (window.confirm('Are you sure you want to delete this workflow? This action cannot be undone.')) {
                                  if (onDeleteWorkflow) {
                                    onDeleteWorkflow(task);
                                  } else {
                                    console.error('❌ [DELETE_BUTTON] onDeleteWorkflow handler is not defined!');
                                    alert('Error: Delete handler is not available. Please refresh the page.');
                                  }
                                }
                              }}
                              title="Delete Workflow"
                            >
                              <FaTrash className="btn-icon" />
                              Delete
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Task Card Component
const TaskCard = ({ task, onStartTask, onDeleteTask, users = [] }) => {
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  const canDeleteWorkflow = currentUser?.role === 'super';
  const isOverdue = task.dueAt && new Date(task.dueAt) < new Date();
  const finalStatus = isOverdue && task.status === 'pending' ? 'overdue' : task.status;

  const getWorkflowIdentifier = () => {
    if (task.formData) {
      switch (task.workflowId) {
        case 'import':
          return task.formData.import_item || 'Import Item';
        case 'sales':
          return task.formData.deal_name || 'Sales Deal';
        case 'technical_sales':
          return task.formData.project_name || 'Technical Project';
        case 'store':
          return task.formData.shipment_reference || 'Shipment';
        default:
          return 'Workflow';
      }
    }
    return 'Workflow';
  };

  const getAssignedUserDisplay = () => {
    // Helper function to look up user name from username
    const getUserNameFromUsername = (username) => {
      if (!username || !users || users.length === 0) return username;
      const user = users.find(u => u.username === username || u.email === username);
      return user ? user.name : username;
    };
    
    // PRIORITY 1: Check task.assignedTo - this should contain the user(s) selected in step 1
    // If it's a username (string), look up the actual name from users list
    // Now handles array of assigned users
    if (task.assignedTo && task.assignedTo !== 'Unassigned') {
      // Handle array of assigned users
      if (Array.isArray(task.assignedTo)) {
        const userNames = task.assignedTo
          .map(user => {
            if (typeof user === 'object' && user.name) return user.name;
            if (typeof user === 'object' && user.email) return user.email;
            if (typeof user === 'string') return getUserNameFromUsername(user);
            return null;
          })
          .filter(Boolean);
        return userNames.length > 0 ? userNames.join(', ') : 'Unassigned';
      } else if (typeof task.assignedTo === 'object' && task.assignedTo.name) {
        return task.assignedTo.name;
      } else if (typeof task.assignedTo === 'object' && task.assignedTo.email) {
        return task.assignedTo.email;
      }
      // Handle string (username) - look up the actual name
      if (typeof task.assignedTo === 'string') {
        return getUserNameFromUsername(task.assignedTo);
      }
    }
    
    // PRIORITY 2: Check if it's in formData (from step 1)
    if (task.formData && task.formData.assigned_user) {
      return getUserNameFromUsername(task.formData.assigned_user);
    }
    
    // PRIORITY 3: Check if it's in the task itself
    if (task.assigned_user) {
      return getUserNameFromUsername(task.assigned_user);
    }
    
    return 'Unassigned';
  };

  const workflowIdentifier = getWorkflowIdentifier();
  const assignedUser = getAssignedUserDisplay();
  
  // Debug: Log task status for completed workflows
  const isCompleted = task.status === 'completed' || task.workflowType === 'completed';
  if (isCompleted) {
    console.log('✅ [TASK_CARD] Completed workflow detected:', {
      taskId: task.id,
      taskStatus: task.status,
      workflowType: task.workflowType,
      instanceId: task.instanceId
    });
  }

  return (
    <div className={`task-card ${finalStatus}`}>
      <div className="task-header">
        <div className="task-icon">
          <FaClock className="status-icon" />
        </div>
        <div className="task-info">
          <h3 className="task-title">
            {task.workflowName || task.stepName}
            {task.customer_name && (
              <span className="customer-name-badge"> - {task.customer_name}</span>
            )}
            {task.itemsRequested && (
              <span className="items-requested-badge"> - {task.itemsRequested}</span>
            )}
          </h3>
          <p className="task-description">{task.stepDescription}</p>
        </div>
        <div className="task-status">
          <span className={`status-badge ${finalStatus}`}>
            {finalStatus.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </div>
      
      <div className="task-details">
        <div className="task-meta">
          {task.project_name && task.workflowId === 'technical_sales' && (
            <span className="task-type">Project: {task.project_name}</span>
          )}
        </div>
        <div className="task-assignment">
          <FaUser className="assignment-icon" />
          <span>Assigned: {assignedUser}</span>
        </div>
      </div>

      <div className="task-actions">
        {isCompleted ? (
          <>
            <button 
              className="view-workflow-btn"
              onClick={() => onStartTask(task)}
            >
              <FaEye className="btn-icon" />
              Open
            </button>
            <button 
              className="edit-workflow-btn"
              onClick={() => onStartTask({...task, editMode: true})}
            >
              <FaEdit className="btn-icon" />
              Edit
            </button>
            {canDeleteWorkflow && (
              <button 
                className="delete-task-btn"
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this workflow? This action cannot be undone.')) {
                    onDeleteTask && onDeleteTask(task);
                  }
                }}
                title="Delete Workflow"
              >
                <FaTrash className="btn-icon" />
                Delete
              </button>
            )}
          </>
        ) : task.status === 'in_progress' ? (
          <>
            <button 
              className="start-task-btn"
              onClick={() => onStartTask(task)}
            >
              <FaPlay className="btn-icon" />
              {task.instanceId && task.status === 'in_progress' ? 'Continue' : 'Start Task'}
            </button>
            {canDeleteWorkflow && (
              <button 
                className="delete-task-btn"
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
                    onDeleteTask && onDeleteTask(task);
                  }
                }}
                title="Delete Task"
              >
                <FaTrash className="btn-icon" />
                Delete
              </button>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};


const WorkflowSummaryView = ({ workflow, stepData, onEdit }) => {
  const formatValue = (value) => {
    if (value === null || value === undefined) return 'Not provided';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    return value.toString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatFieldValue = (field, value, formData) => {
    if (value === null || value === undefined || value === '') return 'Not provided';
    
    // Handle different field types
    if (field.type === 'date' && value) {
      try {
        return new Date(value).toLocaleDateString();
      } catch (e) {
        return value;
      }
    }
    
    if (field.type === 'time' && value) {
      return value;
    }
    
    if (field.type === 'number' && value !== '') {
      return value.toString();
    }
    
    if (field.type === 'select' && value) {
      return value;
    }
    
    if (field.type === 'textarea' && value) {
      return value;
    }
    
    if (field.type === 'checkbox' || field.type === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    if (field.type === 'user_select' && value) {
      // If it's an object with name, use that; otherwise use the value
      if (typeof value === 'object' && value.name) {
        return value.name;
      }
      // If it's a string, try to find the user in the formData or just return it
      if (typeof value === 'string') {
        // Try to find user name from users list if available
        return value;
      }
      return value.toString();
    }
    
    return value.toString();
  };
  
  // Helper function to get user name from username
  const getUserNameFromUsername = (username) => {
    if (!username) return 'Unassigned';
    // Try to find user in workflow.users if available
    if (workflow && workflow.users && Array.isArray(workflow.users)) {
      const user = workflow.users.find(u => u.username === username || u.email === username);
      if (user && user.name) return user.name;
    }
    return username;
  };
  
  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };
  
  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return 'Not provided';
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return timeString;
    }
  };
  
  const handleSummaryFileDownload = async (file) => {
    try {
      await downloadWorkflowFile(file);
    } catch (error) {
      console.error('Unable to download file from summary view:', error);
      alert('Unable to download file. Please try again.');
    }
  };
  
  // Helper function to render file list
  const renderFileList = (files, fieldLabel) => {
    if (!files || files.length === 0) return <span>No files uploaded</span>;
    
    return (
      <div className="file-list">
        {files.map((file, index) => {
          const fileName = file.name || file.filename || file.originalName || `File ${index + 1}`;
          const fileSize = file.size ? formatFileSize(file.size) : '';
          let fileUrl = file.url || file.path || file.filename;
          
          // Construct full URL if needed
          if (fileUrl && !fileUrl.startsWith('http')) {
            const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://intranet.nesimltd.co.ke' : 'http://localhost:5000');
            fileUrl = `${API_BASE}${fileUrl.startsWith('/') ? fileUrl : '/' + fileUrl}`;
          }
          
          return (
            <div key={index} className="file-item">
              <FaPaperclip className="file-icon" />
              <span className="file-name">{fileName}</span>
              {fileSize && <span className="file-size">({fileSize})</span>}
              {fileUrl && (
                <div className="file-actions">
                  <button 
                    className="view-file-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(fileUrl, '_blank');
                    }}
                    title="View file"
                  >
                    <FaEye className="btn-icon" />
                  </button>
                  <button 
                    className="download-file-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    handleSummaryFileDownload(file);
                    }}
                    title="Download file"
                  >
                    <FaDownload className="btn-icon" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };
  
  return (
    <div className="workflow-summary-view">
      <div className="summary-header">
        <h2>{workflow.name} - Summary</h2>
        <p className="summary-description">{workflow.description}</p>
        <div className="summary-actions">
          <button onClick={onEdit} className="edit-summary-btn">
            <FaEdit className="btn-icon" />
            Edit Workflow
          </button>
        </div>
      </div>

      <div className="summary-content">
        {(workflow.steps || []).map((step, stepIndex) => {
          const stepDataForStep = stepData[step.id] || {};
          const formData = stepDataForStep.formData || {};
          const attachments = stepDataForStep.attachments || {};
          const checklist = stepDataForStep.checklist || {};
          const comments = stepDataForStep.comments || '';

          return (
            <div key={step.id} className="summary-step">
              <div className="step-header">
                <h3>
                  <span className="step-number">{stepIndex + 1}</span>
                  {step.name}
                </h3>
                <p className="step-description">{step.description}</p>
                <div className="step-status">
                  <FaCheckCircle className="status-icon completed" />
                  <span>Completed</span>
                </div>
              </div>

              <div className="step-content">
                {/* Form Data - All Fields */}
                {step.formFields && Array.isArray(step.formFields) && step.formFields.length > 0 && (
                  <div className="summary-section">
                    <h4>Form Data</h4>
                    <div className="form-data-grid">
                      {(step.formFields || []).map(field => {
                        if (!field || !field.id) return null;
                        const value = formData && formData[field.id] !== undefined ? formData[field.id] : undefined;
                        
                        // Skip comments field (handled separately)
                        if (field.type === 'comments') return null;
                        
                        // Skip file upload fields (handled in attachments section)
                        if (field.type === 'file' || field.type === 'file_upload') return null;
                        
                        // Handle dynamic_list fields
                        if (field.type === 'dynamic_list') {
                          const listData = Array.isArray(value) ? value : [];
                          if (listData.length === 0) return null;
                          
                          return (
                            <div key={field.id} className="form-field-summary dynamic-list-summary">
                              <label className="field-label-list">{field.label}:</label>
                              <div className="dynamic-list-items-summary">
                                {listData.map((item, itemIndex) => {
                                  if (!item || typeof item !== 'object') return null;
                                  
                                  return (
                                    <div key={itemIndex} className="dynamic-list-item-summary">
                                      <div className="item-number">Item {itemIndex + 1}</div>
                                      <div className="item-fields">
                                        {field.fields && Array.isArray(field.fields) && field.fields.map(subField => {
                                          if (!subField || !subField.id) return null;
                                          const subValue = item[subField.id];
                                          if (subField.type === 'file' || subField.type === 'file_upload') {
                                            // Skip file fields in dynamic list (can be shown in attachments)
                                            return null;
                                          }
                                          return (
                                            <div key={subField.id} className="item-field">
                                              <span className="item-field-label">{subField.label || subField.id}:</span>
                                              <span className="item-field-value">{formatFieldValue(subField, subValue, item)}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                        
                        // Handle activities_manager fields
                        if (field.type === 'activities_manager') {
                          const activities = value || {};
                          const openActivities = activities.open || [];
                          const closedActivities = activities.closed || [];
                          const allActivities = [...openActivities, ...closedActivities];
                          if (allActivities.length === 0) return null;
                          
                          return (
                            <div key={field.id} className="form-field-summary activities-summary">
                              <label className="field-label-activities">{field.label}:</label>
                              <div className="activities-list-summary">
                                {allActivities.map((activity, activityIndex) => {
                                  if (!activity || typeof activity !== 'object') return null;
                                  
                                  const activityType = activity.type || 'task';
                                  const activityIcon = activityType === 'task' ? <FaCheckSquare className="activity-icon" /> :
                                                       activityType === 'meeting' ? <FaUsers className="activity-icon" /> :
                                                       <FaPhone className="activity-icon" />;
                                  const activityStatus = activity.status || 'open';
                                  
                                  return (
                                    <div key={activityIndex} className={`activity-item-summary ${activityStatus}`}>
                                      <div className="activity-header-summary">
                                        <span className="activity-type-summary">{activityIcon}</span>
                                        <span className={`activity-status-summary ${activityStatus}`}>
                                          {activityStatus === 'open' ? 'In Progress' : 'Completed'}
                                        </span>
                                      </div>
                                      <div className="activity-detail">
                                        <strong>Subject:</strong> {activity.subject || 'N/A'}<br />
                                        {activity.description && <><strong>Description:</strong> {activity.description}<br /></>}
                                        {activity.priority && <><strong>Priority:</strong> {activity.priority}<br /></>}
                                        {activity.dueDate && <><strong>Due Date:</strong> {formatDate(activity.dueDate)}<br /></>}
                                        {activity.dueTime && <><strong>Due Time:</strong> {formatTime(activity.dueTime)}<br /></>}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                        
                        // Handle trials_manager fields
                        if (field.type === 'trials_manager') {
                          const trials = Array.isArray(value) ? value : [];
                          if (trials.length === 0) return null;
                          
                          return (
                            <div key={field.id} className="form-field-summary trials-summary">
                              <label className="field-label-trials">{field.label}:</label>
                              <div className="trials-list-summary">
                                {trials.map((trial, trialIndex) => {
                                  if (!trial || typeof trial !== 'object') return null;
                                  
                                  return (
                                    <div key={trialIndex} className="trial-item-summary">
                                      <div className="trial-header-summary">
                                        <span className="trial-number">Trial {trialIndex + 1}</span>
                                        {trial.proposedTrialStartDate && (
                                          <span className="trial-date">Start Date: {formatDate(trial.proposedTrialStartDate)}</span>
                                        )}
                                      </div>
                                      <div className="trial-detail">
                                        {trial.trialOutcome && <><strong>Outcome:</strong> {trial.trialOutcome}<br /></>}
                                        {trial.decision && <><strong>Decision:</strong> {trial.decision}<br /></>}
                                        {trial.remarks && <><strong>Remarks:</strong> {trial.remarks}<br /></>}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                        
                        // Regular field rendering
                        return (
                          <div key={field.id} className="form-field-summary">
                            <label>{field.label || field.id}:</label>
                            <span className="field-value">{formatFieldValue(field, value, formData)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Attachments Section */}
                {attachments && Object.keys(attachments).length > 0 && (
                  <div className="summary-section">
                    <h4>Attachments</h4>
                    {Object.entries(attachments).map(([fieldId, files]) => {
                      if (!files || files.length === 0) return null;
                      const field = step.formFields?.find(f => f.id === fieldId);
                      return (
                        <div key={fieldId} className="attachment-group">
                          <h5>{field?.label || fieldId}:</h5>
                          {renderFileList(files, field?.label || fieldId)}
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Checklist Section */}
                {checklist && Object.keys(checklist).length > 0 && (
                  <div className="summary-section">
                    <h4>Checklist</h4>
                    <div className="checklist-summary">
                      {Object.entries(checklist).map(([fieldId, items]) => {
                        if (!items || items.length === 0) return null;
                        const field = step.formFields?.find(f => f.id === fieldId);
                        return (
                          <div key={fieldId} className="checklist-group">
                            <h5>{field?.label || fieldId}:</h5>
                            <ul className="checklist-items-summary">
                              {items.map((item, itemIndex) => (
                                <li key={itemIndex} className="checklist-item-summary">
                                  <FaCheckCircle className="check-icon" />
                                  {item.label || item.text || `Item ${itemIndex + 1}`}
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Comments Section */}
                {comments && (
                  <div className="summary-section">
                    <h4>Comments</h4>
                    <div className="comments-summary">
                      <p>{comments}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DynamicListField = ({ field, formData, attachments, onInputChange, onAttachmentChange, isReadOnly, currentStepId, minDateString }) => {
  const listData = formData[field.id] || [];
  const [expandedItems, setExpandedItems] = useState(new Set());
  
  const toggleItem = (itemIndex) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemIndex)) {
        newSet.delete(itemIndex);
      } else {
        newSet.add(itemIndex);
      }
      return newSet;
    });
  };
  
  const isItemExpanded = (itemIndex) => {
    return expandedItems.has(itemIndex);
  };
  
  const getItemSummary = (item, itemIndex) => {
    // Priority 1: Task field (for tasks_before)
    if (item.task && item.task.trim()) {
      return item.task.length > 60 ? item.task.substring(0, 60) + '...' : item.task;
    }
    // Priority 2: Outcome field (for tasks_after)
    if (item.outcome && item.outcome.trim()) {
      return item.outcome.length > 60 ? item.outcome.substring(0, 60) + '...' : item.outcome;
    }
    // Priority 3: First text field value
    const firstTextField = field.fields.find(f => f.type === 'text' && f.id !== 'task' && item[f.id]);
    if (firstTextField && item[firstTextField.id]) {
      const value = item[firstTextField.id].trim();
      if (value) {
        return value.length > 60 ? value.substring(0, 60) + '...' : value;
      }
    }
    // Priority 4: First textarea field value
    const firstTextareaField = field.fields.find(f => f.type === 'textarea' && f.id !== 'outcome' && item[f.id]);
    if (firstTextareaField && item[firstTextareaField.id]) {
      const value = item[firstTextareaField.id].trim();
      if (value) {
        return value.length > 60 ? value.substring(0, 60) + '...' : value;
      }
    }
    // Priority 5: Date/time combination
    if (item.date || item.time) {
      const dateStr = item.date ? new Date(item.date).toLocaleDateString() : '';
      const timeStr = item.time || '';
      const combined = `${dateStr} ${timeStr}`.trim();
      return combined || 'No details';
    }
    return 'Click to add details';
  };
  
  const addItem = () => {
    const newItem = {};
    field.fields.forEach(f => {
      if (f.type === 'file') {
        // Files are handled separately in attachments
        return;
      }
      newItem[f.id] = '';
    });
    const updatedList = [...listData, newItem];
    onInputChange(field.id, updatedList);
  };
  
  const removeItem = (index) => {
    const updatedList = listData.filter((_, i) => i !== index);
    onInputChange(field.id, updatedList);
    
    // Also remove attachments for this item if they exist
    const attachmentKey = `${field.id}_${index}`;
    if (attachments[attachmentKey]) {
      // Clear attachments for this item
      onAttachmentChange(attachmentKey, []);
    }
  };
  
  const updateItemField = (itemIndex, fieldId, value) => {
    const updatedList = [...listData];
    if (!updatedList[itemIndex]) {
      updatedList[itemIndex] = {};
    }
    updatedList[itemIndex][fieldId] = value;
    onInputChange(field.id, updatedList);
  };
  
  const handleFileChange = async (itemIndex, files) => {
    const attachmentKey = `${field.id}_${itemIndex}`;
    const fileArray = Array.isArray(files) ? files : [];
    await onAttachmentChange(attachmentKey, fileArray);
  };
  
  const getItemAttachments = (itemIndex) => {
    const attachmentKey = `${field.id}_${itemIndex}`;
    return attachments[attachmentKey] || [];
  };

  const handleFileView = (file) => {
    console.log('handleFileView called with file:', file);
    let fileUrl = null;
    
    // Try different URL sources in order of preference
    if (file.url) {
      fileUrl = file.url;
    } else if (file.path) {
      fileUrl = file.path;
    } else if (file.filename) {
      fileUrl = `/uploads/chat/${file.filename}`;
    }
    
    if (!fileUrl) {
      console.error('No valid file URL found');
      alert('Unable to view file: No valid URL found');
      return;
    }
    
    // Construct full URL if relative
    const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://intranet.nesimltd.co.ke' : 'http://localhost:5000');
    const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API_BASE}${fileUrl}`;
    
    console.log('Opening file URL:', fullUrl);
    window.open(fullUrl, '_blank');
  };

  const handleFileDownload = async (file) => {
    try {
      await downloadWorkflowFile(file);
    } catch (error) {
      alert('Unable to download file. Please try again.');
    }
  };
  
  return (
    <div className="dynamic-list-field">
      <div className="dynamic-list-header">
        <h4>{field.label}</h4>
        {!isReadOnly && (
          <button
            type="button"
            onClick={addItem}
            className="add-item-btn"
            title="Add new item"
          >
            <FaPlus /> Add Item
          </button>
        )}
      </div>
      
      <div className="dynamic-list-items">
        {listData.length === 0 ? (
          <div className="empty-list-message">
            <p>No items added yet. Click "Add Item" to create your first item.</p>
          </div>
        ) : (
          listData.map((item, itemIndex) => {
            const isExpanded = isItemExpanded(itemIndex);
            const summary = getItemSummary(item, itemIndex);
            
            return (
              <div key={itemIndex} className={`dynamic-list-item ${isExpanded ? 'expanded' : 'collapsed'}`}>
                <div className="list-item-header" onClick={() => toggleItem(itemIndex)}>
                  <div className="list-item-header-left">
                    <button
                      type="button"
                      className="expand-toggle-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleItem(itemIndex);
                      }}
                      title={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    <h5>Item {itemIndex + 1}</h5>
                    {!isExpanded && (
                      <span className="item-summary">{summary}</span>
                    )}
                  </div>
                  {!isReadOnly && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeItem(itemIndex);
                      }}
                      className="remove-item-btn"
                      title="Remove item"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              
                {isExpanded && (
                  <div className="list-item-fields">
                {field.fields.map(subField => {
                  if (subField.type === 'file') {
                    const itemAttachments = getItemAttachments(itemIndex);
                    return (
                      <div key={subField.id} className="list-item-field">
                        <label className="field-label">
                          {subField.label}
                          {subField.required && <span className="required">*</span>}
                        </label>
                        <div className="file-upload-section">
                          {itemAttachments.length > 0 ? (
                            <div className="uploaded-files">
                              {itemAttachments.map((file, fileIndex) => (
                                <div key={fileIndex} className="uploaded-file">
                                  <div className="file-info">
                                    <FaFileAlt className="file-icon" />
                                    <span className="file-name">{file.name || file.filename}</span>
                                    {file.size && (
                                      <span className="file-size">
                                        ({(file.size / 1024).toFixed(1)} KB)
                                      </span>
                                    )}
                                  </div>
                                  <div className="file-actions-list">
                                    <button
                                      type="button"
                                      className="file-action-btn view-btn"
                                      onClick={() => handleFileView(file)}
                                      title="View file"
                                    >
                                      <FaEye />
                                    </button>
                                    <button
                                      type="button"
                                      className="file-action-btn download-btn"
                                      onClick={() => handleFileDownload(file)}
                                      title="Download file"
                                    >
                                      <FaDownload />
                                    </button>
                                    {!isReadOnly && (
                                      <button
                                        type="button"
                                        className="file-action-btn remove-btn"
                                        onClick={async () => {
                                          const remaining = itemAttachments.filter((_, i) => i !== fileIndex);
                                          await handleFileChange(itemIndex, remaining);
                                        }}
                                        title="Remove file"
                                      >
                                        <FaTrash />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="file-upload-prompt">
                              <p>No files uploaded yet.</p>
                            </div>
                          )}
                          {!isReadOnly && (
                            <div className="file-input-wrapper">
                              <input
                                type="file"
                                multiple
                                id={`file-input-${field.id}-${itemIndex}`}
                                onChange={async (e) => {
                                  const newFiles = Array.from(e.target.files);
                                  if (newFiles.length > 0) {
                                    // Get existing attachments for this item
                                    const existing = itemAttachments || [];
                                    // Pass both existing and new files so they can be merged
                                    await handleFileChange(itemIndex, [...existing, ...newFiles]);
                                    // Reset input
                                    e.target.value = '';
                                  }
                                }}
                                className="form-file-input-hidden"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                              />
                              <label htmlFor={`file-input-${field.id}-${itemIndex}`} className="file-input-label-list">
                                <FaUpload /> {itemAttachments.length > 0 ? 'Add More Files' : 'Choose Files'}
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div key={subField.id} className="list-item-field">
                      <label className="field-label">
                        {subField.label}
                        {subField.required && <span className="required">*</span>}
                      </label>
                      {subField.type === 'text' && (
                        <input
                          type="text"
                          value={item[subField.id] || ''}
                          onChange={(e) => updateItemField(itemIndex, subField.id, e.target.value)}
                          readOnly={isReadOnly}
                          disabled={isReadOnly}
                          className="form-input"
                          placeholder={`Enter ${subField.label.toLowerCase()}`}
                        />
                      )}
                      {subField.type === 'textarea' && (
                        <textarea
                          value={item[subField.id] || ''}
                          onChange={(e) => updateItemField(itemIndex, subField.id, e.target.value)}
                          readOnly={isReadOnly}
                          disabled={isReadOnly}
                          className="form-textarea"
                          rows={3}
                          placeholder={`Enter ${subField.label.toLowerCase()}`}
                        />
                      )}
                      {subField.type === 'date' && (
                        <input
                          type="date"
                          value={item[subField.id] || ''}
                          onChange={(e) => updateItemField(itemIndex, subField.id, e.target.value)}
                          readOnly={isReadOnly}
                          disabled={isReadOnly}
                          className="form-input"
                          min={minDateString}
                        />
                      )}
                      {subField.type === 'time' && (
                        <input
                          type="time"
                          value={item[subField.id] || ''}
                          onChange={(e) => updateItemField(itemIndex, subField.id, e.target.value)}
                          readOnly={isReadOnly}
                          disabled={isReadOnly}
                          className="form-input"
                        />
                      )}
                    </div>
                  );
                })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// Activities Manager Component
const ActivitiesManager = ({ formData, onInputChange, isReadOnly, users = [], onAutoSave, minDateString, minDateTimeString }) => {
  const [activeTab, setActiveTab] = useState('open');
  const [activeType, setActiveType] = useState(null); // 'task', 'meeting', 'call'
  const [callMode, setCallMode] = useState('schedule'); // 'schedule' or 'log'
  const [selectedActivity, setSelectedActivity] = useState(null); // Selected activity for detail view
  const [activityToEdit, setActivityToEdit] = useState(null); // Activity data to edit (for editing existing activities)
  const [activityPrefillData, setActivityPrefillData] = useState(null); // Prefill data for new activities from calendar
  
  // Use state to track activities and sync with formData changes
  const [activities, setActivities] = useState(() => {
    return formData['activities'] || { open: [], closed: [] };
  });
  
  // Sync activities state with formData prop changes (e.g., when workflow is loaded from backend)
  // Use a ref to track if we're in the middle of a save operation to avoid overwriting during saves
  const isSavingRef = useRef(false);
  
  useEffect(() => {
    // Don't sync if we're currently saving (to avoid race conditions)
    if (isSavingRef.current) {
      return;
    }
    
    const formActivities = formData['activities'] || { open: [], closed: [] };
    // Only update if the data has actually changed (deep comparison)
    const currentActivitiesStr = JSON.stringify(activities);
    const formActivitiesStr = JSON.stringify(formActivities);
    if (formActivitiesStr !== currentActivitiesStr && formActivitiesStr !== '{"open":[],"closed":[]}') {
      setActivities(formActivities);
    }
  }, [formData['activities']]);
  
  const addActivity = (type, prefillData = null) => {
    // Clear any existing edit data
    setActivityToEdit(null);
    // Set prefill data if provided (for new activities from calendar)
    setActivityPrefillData(prefillData || null);
    setActiveType(type);
    if (type === 'call') {
      setCallMode('schedule');
    }
  };
  
  const closeActivityForm = () => {
    setActiveType(null);
    setCallMode('schedule');
    setActivityToEdit(null); // Clear edit data when closing form
    setActivityPrefillData(null); // Clear prefill data when closing form
  };
  
  const saveActivity = (activityData) => {
    // Use local activities state as the source of truth to avoid race conditions
    // This ensures we always work with the most up-to-date local state, not stale formData
    isSavingRef.current = true;
    
    try {
      if (activityToEdit) {
        // Update existing activity
        const updatedActivity = {
          ...activityToEdit,
          ...activityData,
          updatedAt: new Date().toISOString()
        };
        
        // Find and update the activity in the appropriate list
        let updatedActivities = { ...activities };
        const inOpen = (activities.open || []).find(a => a.id === activityToEdit.id);
        const inClosed = (activities.closed || []).find(a => a.id === activityToEdit.id);
        
        if (inOpen) {
          updatedActivities.open = (activities.open || []).map(a => 
            a.id === activityToEdit.id ? updatedActivity : a
          );
        } else if (inClosed) {
          updatedActivities.closed = (activities.closed || []).map(a => 
            a.id === activityToEdit.id ? updatedActivity : a
          );
        }
        
        // Update local state first, then trigger onInputChange
        setActivities(updatedActivities);
        onInputChange('activities', updatedActivities);
        setActivityToEdit(null);
        setActivityPrefillData(null); // Clear prefill data after saving
        setSelectedActivity(updatedActivity); // Show the updated activity in detail view
        closeActivityForm();
      } else {
        // Create new activity - use local activities state to preserve existing activities
        const newActivity = {
          id: Date.now().toString(),
          type: activeType,
          ...activityData,
          createdAt: new Date().toISOString(),
          status: 'open'
        };
        
        // Ensure we have arrays to work with
        const currentOpen = activities.open || [];
        const currentClosed = activities.closed || [];
        
        const updatedActivities = {
          open: [...currentOpen, newActivity],
          closed: currentClosed
        };
        
        console.log('📝 [ACTIVITIES] Saving new activity:', {
          activityId: newActivity.id,
          activityType: newActivity.type,
          currentOpenCount: currentOpen.length,
          newOpenCount: updatedActivities.open.length,
          allActivityIds: updatedActivities.open.map(a => a.id)
        });
        
        // Update local state first, then trigger onInputChange
        setActivities(updatedActivities);
        onInputChange('activities', updatedActivities);
        setActivityPrefillData(null); // Clear prefill data after saving
        setSelectedActivity(newActivity); // Show the newly created activity in detail view
        closeActivityForm();
      }
    } finally {
      // Clear the saving flag after a short delay to allow state to propagate
      setTimeout(() => {
        isSavingRef.current = false;
      }, 100);
    }
  };
  
  const moveToClosed = (activityId) => {
    // Use local activities state
    const activity = activities.open.find(a => a.id === activityId);
    if (activity) {
      const updatedActivities = {
        open: activities.open.filter(a => a.id !== activityId),
        closed: [...(activities.closed || []), { ...activity, status: 'closed', closedAt: new Date().toISOString() }]
      };
      setActivities(updatedActivities);
      onInputChange('activities', updatedActivities);
      
      // Auto-save to backend immediately
      if (onAutoSave) {
        console.log('💾 [ACTIVITIES] Auto-saving closed activity:', activityId);
        onAutoSave(updatedActivities);
      }
    }
  };
  
  const moveToOpen = (activityId) => {
    // Use local activities state
    const activity = activities.closed.find(a => a.id === activityId);
    if (activity) {
      // Remove closedAt and reset status
      const { closedAt, ...activityWithoutClosedAt } = activity;
      const updatedActivities = {
        closed: activities.closed.filter(a => a.id !== activityId),
        open: [...(activities.open || []), { ...activityWithoutClosedAt, status: 'open' }]
      };
      setActivities(updatedActivities);
      onInputChange('activities', updatedActivities);
      
      // Auto-save to backend immediately
      if (onAutoSave) {
        console.log('💾 [ACTIVITIES] Auto-saving reopened activity:', activityId);
        onAutoSave(updatedActivities);
      }
    }
  };
  
  const deleteActivity = (activityId, fromTab) => {
    // Use local activities state
    const updatedActivities = { ...activities };
    if (fromTab === 'open') {
      updatedActivities.open = (activities.open || []).filter(a => a.id !== activityId);
    } else {
      updatedActivities.closed = (activities.closed || []).filter(a => a.id !== activityId);
    }
    setActivities(updatedActivities);
    onInputChange('activities', updatedActivities);
  };
  
  return (
    <div className="activities-manager">
      {selectedActivity && !activeType ? (
        <ActivityDetailView
          activity={selectedActivity}
          users={users}
          onClose={() => setSelectedActivity(null)}
          onEdit={(activityToEdit) => {
            // Store the activity data to edit first
            setActivityToEdit(activityToEdit);
            setActiveType(activityToEdit.type);
            if (activityToEdit.type === 'call') {
              // Determine call mode based on whether duration exists (log mode) or not (schedule mode)
              setCallMode(activityToEdit.callDurationMinutes !== undefined || activityToEdit.callDurationSeconds !== undefined ? 'log' : 'schedule');
            }
            setSelectedActivity(null); // Hide detail view to show edit form
          }}
          isReadOnly={isReadOnly}
        />
      ) : activeType ? (
        <ActivityForm
          type={activeType}
          callMode={callMode}
          setCallMode={setCallMode}
          initialData={activityToEdit || activityPrefillData || null}
          onSave={saveActivity}
          onCancel={closeActivityForm}
          users={users}
          isReadOnly={isReadOnly}
          minDateString={minDateString}
          minDateTimeString={minDateTimeString}
        />
      ) : (
        <>
          {/* Tab Navigation */}
          <div className="activities-tabs">
            <button
              className={`activity-tab ${activeTab === 'open' ? 'active' : ''}`}
              onClick={() => setActiveTab('open')}
            >
              In Progress Activities
            </button>
            <button
              className={`activity-tab ${activeTab === 'closed' ? 'active' : ''}`}
              onClick={() => setActiveTab('closed')}
            >
              Completed Activities
            </button>
            <button
              className={`activity-tab ${activeTab === 'calendar' ? 'active' : ''}`}
              onClick={() => setActiveTab('calendar')}
            >
              <FaCalendarAlt /> Calendar
            </button>
          </div>
          
          {/* Content */}
          {activeTab === 'open' && (
            <div className="open-activities">
              <div className="activity-type-buttons">
                <button
                  className="activity-type-btn"
                  onClick={() => addActivity('task')}
                  disabled={isReadOnly}
                >
                  <FaClipboardList /> New Task
                </button>
                <button
                  className="activity-type-btn"
                  onClick={() => addActivity('meeting')}
                  disabled={isReadOnly}
                >
                  <FaCalendarAlt /> New Meeting
                </button>
                <button
                  className="activity-type-btn"
                  onClick={() => addActivity('call')}
                  disabled={isReadOnly}
                >
                  <FaPhone /> A call to be made
                </button>
              </div>
              
              <div className="activities-list">
                {(activities.open || []).length === 0 ? (
                  <div className="empty-activities">No in progress activities yet</div>
                ) : (
                  <ActivitiesTable
                    activities={activities.open || []}
                    onClose={(id) => moveToClosed(id)}
                    onDelete={(id) => deleteActivity(id, 'open')}
                    onView={(activity) => setSelectedActivity(activity)}
                    isReadOnly={isReadOnly}
                    users={users}
                    isClosed={false}
                  />
                )}
              </div>
            </div>
          )}
          
          {activeTab === 'closed' && (
            <div className="closed-activities">
              <div className="activities-list">
                {(activities.closed || []).length === 0 ? (
                  <div className="empty-activities">No completed activities yet</div>
                ) : (
                  <ActivitiesTable
                    activities={activities.closed || []}
                    onOpen={(id) => moveToOpen(id)}
                    onDelete={(id) => deleteActivity(id, 'closed')}
                    onView={(activity) => setSelectedActivity(activity)}
                    isReadOnly={isReadOnly}
                    users={users}
                    isClosed={true}
                  />
                )}
              </div>
            </div>
          )}
          
          {activeTab === 'calendar' && (
            <ActivitiesCalendar
              activities={activities}
              onView={(activity) => setSelectedActivity(activity)}
              users={users}
              onAddActivity={addActivity}
              minDateString={minDateString}
              minDateTimeString={minDateTimeString}
            />
          )}
        </>
      )}
    </div>
  );
};

    // Activities Calendar Component - Google Calendar-like month view
const ActivitiesCalendar = ({ activities, onView, users, onAddActivity, minDateString, minDateTimeString }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    // Use date string (YYYY-MM-DD) instead of Date object for reliable state updates
  const [selectedDateString, setSelectedDateString] = useState(null);
    const [activityMenu, setActivityMenu] = useState(null); // { hour, x, y }
  const minDate = useMemo(() => {
    if (!minDateString) return startOfDay(new Date());
    const parsed = safeParseDate(minDateString);
    return parsed ? startOfDay(parsed) : startOfDay(new Date());
  }, [minDateString]);
  const minDateTimeValue = useMemo(() => {
    if (!minDateTimeString) return minDate;
    const parsed = safeParseDate(minDateTimeString);
    return parsed ? parsed : minDate;
  }, [minDateTimeString, minDate]);
    
    // Cleanup on mount - override any stale global functions to prevent errors
    useEffect(() => {
      // Override any potential global removeMenu function that might exist from old cached code
      // This prevents errors from cached code trying to call removeChild
      try {
        // Override window.removeMenu if it exists
        if (typeof window.removeMenu === 'function') {
          window.removeMenu = function() {
            // Do nothing - just prevent errors
            console.log('Stale removeMenu called - ignoring');
          };
        }
        // Override document.removeMenu if it exists
        if (typeof document.removeMenu === 'function') {
          document.removeMenu = function() {
            // Do nothing - just prevent errors
            console.log('Stale removeMenu called - ignoring');
          };
        }
        // Also override HTMLDocument.prototype.removeMenu if it exists
        if (typeof HTMLDocument.prototype.removeMenu === 'function') {
          HTMLDocument.prototype.removeMenu = function() {
            // Do nothing - just prevent errors
            console.log('Stale removeMenu prototype called - ignoring');
          };
        }
        // Remove any stale menu elements that might exist
        const staleMenus = document.querySelectorAll('.calendar-hour-activity-menu, [data-calendar-menu]');
        staleMenus.forEach(el => {
          try {
            el.remove();
          } catch (err) {
            // Ignore errors
          }
        });
      } catch (err) {
        // Ignore cleanup errors
        console.warn('Cleanup warning:', err);
      }
    }, []);
    
    // Cleanup: Close menu when selected date changes
    useEffect(() => {
      setActivityMenu(null);
    }, [selectedDateString]);
    
    // Cleanup on unmount - ensure no event listeners are left
    useEffect(() => {
      return () => {
        setActivityMenu(null);
        // Remove any potential stale event listeners that might exist
        try {
          const allListeners = document.querySelectorAll('[data-calendar-menu]');
          allListeners.forEach(el => {
            try {
              el.remove();
            } catch (err) {
              // Ignore errors
            }
          });
        } catch (err) {
          // Ignore cleanup errors
        }
      };
    }, []);
    
    // Close menu when clicking outside (using React event handling)
    useEffect(() => {
      if (!activityMenu) return;
      
      const handleClickOutside = (e) => {
        try {
          const menuElement = document.querySelector('.calendar-hour-activity-menu');
          if (menuElement && !menuElement.contains(e.target)) {
            setActivityMenu(null);
          }
        } catch (err) {
          // If there's any error, just close the menu
          setActivityMenu(null);
        }
      };
      
      // Use capture phase to catch events early
      document.addEventListener('click', handleClickOutside, true);
      
      return () => {
        try {
          document.removeEventListener('click', handleClickOutside, true);
        } catch (err) {
          // Ignore errors during cleanup
        }
      };
    }, [activityMenu]);
    
  
  // Get all activities from both open and closed lists
  const allActivities = [
    ...(activities.open || []).map(act => ({ ...act, status: 'open' })),
    ...(activities.closed || []).map(act => ({ ...act, status: 'closed' }))
  ];
  
  // Get current month and year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  
  // Get first day of month and number of days
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();
  
  // Get activities for a specific date
  const getActivitiesForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return allActivities.filter(activity => {
      let activityDate = null;
      
      try {
        // For tasks, use dueDate
        if (activity.type === 'task' && activity.dueDate) {
          activityDate = new Date(activity.dueDate);
          if (isNaN(activityDate.getTime())) return false;
        }
        // For meetings, use 'from' date
        else if (activity.type === 'meeting' && activity.from) {
          activityDate = new Date(activity.from);
          if (isNaN(activityDate.getTime())) return false;
        }
        // For calls, use scheduledTime or callStartTime
        else if (activity.type === 'call' && (activity.scheduledTime || activity.callStartTime)) {
          activityDate = new Date(activity.scheduledTime || activity.callStartTime);
          if (isNaN(activityDate.getTime())) return false;
        }
        // Fallback to date field if available
        else if (activity.date) {
          activityDate = new Date(activity.date);
          if (isNaN(activityDate.getTime())) return false;
        }
        
        if (!activityDate) return false;
        
        const activityDateStr = activityDate.toISOString().split('T')[0];
        return activityDateStr === dateStr;
      } catch (e) {
        console.warn('Error parsing activity date:', activity, e);
        return false;
      }
    });
  };
  
  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };
  
  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setSelectedDateString(todayString);
  };
  
  // Month and year names
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Generate calendar days
  const calendarDays = [];
  
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(currentYear, currentMonth, day);
    calendarDays.push(date);
  }
  
  // Get activity type color
  const getActivityColor = (type) => {
    switch (type) {
      case 'task':
        return '#4285f4'; // Google blue
      case 'meeting':
        return '#0b8043'; // Google green
      case 'call':
        return '#ea4335'; // Google red
      default:
        return '#80868b'; // Google gray
    }
  };
  
  // Get activity icon
  const getActivityIcon = (type) => {
    switch (type) {
      case 'task':
        return <FaTasks />;
      case 'meeting':
        return <FaCalendarAlt />;
      case 'call':
        return <FaPhone />;
      default:
        return <FaCircle />;
    }
  };
  
  // Check if date is today
  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };
  
  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  // Convert date string back to Date object for getActivitiesForDate
  const getSelectedDate = () => {
    if (!selectedDateString) return null;
    const [year, month, day] = selectedDateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };
  
  // Get activities for selected date
  const selectedDateActivities = selectedDateString ? getActivitiesForDate(getSelectedDate()) : [];
  
  // Handle add activity with pre-filled date and optional time
  const handleAddActivity = (type, hour = null) => {
    let prefillData = null;
    
    if (selectedDateString) {
      const effectiveDateString =
        minDateString && selectedDateString < minDateString
          ? minDateString
          : selectedDateString;
      const [year, month, day] = effectiveDateString.split('-').map(Number);
      
      if (hour !== null) {
        // Create datetime with specific hour
        let dateTime = new Date(year, month - 1, day, hour, 0, 0);
        if (dateTime < minDateTimeValue) {
          dateTime = new Date(minDateTimeValue);
        }
        
        // Format based on activity type - use ISO strings that will be converted by the form
        if (type === 'task') {
          prefillData = {
            dueDate: formatDateOnly(dateTime)
          };
        } else if (type === 'meeting') {
          const fromTime = new Date(dateTime);
          const toTime = new Date(dateTime);
          toTime.setHours(toTime.getHours() + 1); // Default 1 hour duration
          
          prefillData = {
            from: fromTime.toISOString(),
            to: toTime.toISOString()
          };
        } else if (type === 'call') {
          prefillData = {
            scheduledTime: dateTime.toISOString()
          };
        }
      } else {
        // Just date, no specific time - use default times
        if (type === 'task') {
          prefillData = {
            dueDate: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
          };
        } else if (type === 'meeting') {
          let dateTime = new Date(year, month - 1, day, 9, 0, 0); // Default 9 AM
          if (dateTime < minDateTimeValue) {
            dateTime = new Date(minDateTimeValue);
          }
          const toTime = new Date(dateTime);
          toTime.setHours(toTime.getHours() + 1); // Default 1 hour duration
          
          prefillData = {
            from: dateTime.toISOString(),
            to: toTime.toISOString()
          };
        } else if (type === 'call') {
          let dateTime = new Date(year, month - 1, day, 9, 0, 0); // Default 9 AM
          if (dateTime < minDateTimeValue) {
            dateTime = new Date(minDateTimeValue);
          }
          prefillData = {
            scheduledTime: dateTime.toISOString()
          };
        }
      }
    }
    
    // Call onAddActivity - this will trigger the form to show in ActivitiesManager
    // DO NOT close the modal - let the form appear and the user can close it manually
    if (onAddActivity) {
      onAddActivity(type, prefillData);
      // Close the menu but keep the modal open
      setActivityMenu(null);
    }
  };
  
  // Simple date click handler using date strings
  const handleDateClick = (date) => {
    if (!date) return;
    if (startOfDay(date) < minDate) return;
    
    // Convert date to YYYY-MM-DD string
    const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    
    // Toggle: if same date clicked, close it; otherwise select it
    if (selectedDateString === dateString) {
      setSelectedDateString(null);
    } else {
      setSelectedDateString(dateString);
    }
  };
  
  return (
    <div className="activities-calendar">
      
      {/* Calendar Header */}
      <div className="calendar-header">
        <div className="calendar-nav">
          <button onClick={goToPreviousMonth} className="calendar-nav-btn" title="Previous month">
            <FaChevronLeft />
          </button>
          <button onClick={goToToday} className="calendar-today-btn" title="Today">
            Today
          </button>
          <button onClick={goToNextMonth} className="calendar-nav-btn" title="Next month">
            <FaChevronRight />
          </button>
        </div>
        <h2 className="calendar-month-year">
          {monthNames[currentMonth]} {currentYear}
        </h2>
      </div>
      
      {/* Calendar Grid */}
      <div className="calendar-grid">
        {/* Day headers */}
        <div className="calendar-weekdays">
          {dayNames.map(day => (
            <div key={day} className="calendar-weekday">{day}</div>
          ))}
        </div>
        
        {/* Calendar days */}
        <div className="calendar-days">
          {calendarDays.map((date, index) => {
            const dayActivities = date ? getActivitiesForDate(date) : [];
            const isTodayDate = isToday(date);
            
            // Check if this date is selected using date string comparison
            const dateString = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : null;
            const isSelected = selectedDateString === dateString;
            
            return (
              <div
                key={index}
                className={`calendar-day ${!date ? 'empty' : ''} ${isTodayDate ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={(e) => {
                  if (date) {
                    const target = e.target;
                    // Check if click is on an activity item or more button
                    const isActivityItem = target.closest('.calendar-activity-item');
                    const isActivityMore = target.closest('.calendar-activity-more');
                    
                    // Only handle if clicking on the day itself, not on activity items
                    if (!isActivityItem && !isActivityMore) {
                      handleDateClick(date);
                    }
                  }
                }}
                style={{ cursor: date ? 'pointer' : 'default' }}
              >
                {date ? (
                  <>
                    <div className="calendar-day-number">{date.getDate()}</div>
                    <div className="calendar-day-activities">
                      {dayActivities.slice(0, 3).map((activity, actIndex) => (
                        <div
                          key={actIndex}
                          className="calendar-activity-item"
                          style={{ 
                            backgroundColor: getActivityColor(activity.type),
                            borderLeft: `3px solid ${getActivityColor(activity.type)}`
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onView(activity);
                          }}
                          title={activity.subject || activity.title || `${activity.type} activity`}
                        >
                          <span className="calendar-activity-icon">
                            {getActivityIcon(activity.type)}
                          </span>
                          <span className="calendar-activity-title">
                            {activity.subject || activity.title || activity.type}
                          </span>
                          {(() => {
                            // Extract time from various date fields
                            let timeStr = null;
                            if (activity.time) {
                              timeStr = activity.time;
                            } else if (activity.from) {
                              try {
                                const fromDate = new Date(activity.from);
                                if (!isNaN(fromDate.getTime())) {
                                  timeStr = fromDate.toLocaleTimeString('en-US', { 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: true 
                                  });
                                }
                              } catch (e) {}
                            } else if (activity.scheduledTime || activity.callStartTime) {
                              try {
                                const timeDate = new Date(activity.scheduledTime || activity.callStartTime);
                                if (!isNaN(timeDate.getTime())) {
                                  timeStr = timeDate.toLocaleTimeString('en-US', { 
                                    hour: '2-digit', 
                                    minute: '2-digit',
                                    hour12: true 
                                  });
                                }
                              } catch (e) {}
                            }
                            return timeStr ? (
                              <span className="calendar-activity-time">
                                {timeStr}
                              </span>
                            ) : null;
                          })()}
                        </div>
                      ))}
                      {dayActivities.length > 3 && (
                        <div 
                          className="calendar-activity-more"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (date) {
                              handleDateClick(date);
                            }
                          }}
                        >
                          +{dayActivities.length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="calendar-day-number empty-day"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Legend */}
      <div className="calendar-legend">
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#4285f4' }}></div>
          <span>Tasks</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#0b8043' }}></div>
          <span>Meetings</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ backgroundColor: '#ea4335' }}></div>
          <span>Calls</span>
        </div>
      </div>
      
      {/* Day View Modal Popup */}
      {selectedDateString && (
        <div 
          className="calendar-day-modal-overlay"
          onClick={(e) => {
            if (e.target.className === 'calendar-day-modal-overlay') {
              setSelectedDateString(null);
            }
          }}
        >
          <div 
            className="calendar-day-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="calendar-day-modal-header">
              <div className="calendar-day-modal-nav">
                <button 
                  onClick={() => {
                    const [year, month, day] = selectedDateString.split('-').map(Number);
                    const prevDate = new Date(year, month - 1, day - 1);
                    setSelectedDateString(`${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}-${String(prevDate.getDate()).padStart(2, '0')}`);
                  }}
                  className="calendar-day-nav-btn"
                  title="Previous day"
                >
                  <FaChevronLeft />
                </button>
                <button 
                  onClick={goToToday}
                  className="calendar-day-today-btn"
                  title="Today"
                >
                  Today
                </button>
                <button 
                  onClick={() => {
                    const [year, month, day] = selectedDateString.split('-').map(Number);
                    const nextDate = new Date(year, month - 1, day + 1);
                    setSelectedDateString(`${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}-${String(nextDate.getDate()).padStart(2, '0')}`);
                  }}
                  className="calendar-day-nav-btn"
                  title="Next day"
                >
                  <FaChevronRight />
                </button>
              </div>
              <h2 className="calendar-day-modal-title">
                {formatDateForDisplay(selectedDateString)}
              </h2>
              <div className="calendar-day-modal-actions">
                <div className="calendar-day-new-activity-dropdown">
                  <button 
                    className="calendar-day-new-activity-btn"
                    title="New Activity"
                  >
                    <FaPlus /> New Activity
                  </button>
                  <div className="calendar-day-activity-options">
                    <button onClick={() => handleAddActivity('task')}>
                      <FaClipboardList /> New Task
                    </button>
                    <button onClick={() => handleAddActivity('meeting')}>
                      <FaCalendarAlt /> New Meeting
                    </button>
                    <button onClick={() => handleAddActivity('call')}>
                      <FaPhone /> New Call
                    </button>
                  </div>
                </div>
                <button 
                  className="calendar-day-modal-close"
                  onClick={() => setSelectedDateString(null)}
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Hourly Timeline View */}
            <div className="calendar-day-timeline-container">
              <div className="calendar-day-timeline-hours">
                {Array.from({ length: 24 }, (_, i) => {
                  const hour = i;
                  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                  const ampm = hour < 12 ? 'AM' : 'PM';
                  return (
                    <div key={i} className="calendar-day-hour-row">
                      <div className="calendar-day-hour-label">
                        {hour12}{ampm}
                      </div>
                      <div className="calendar-day-hour-line"></div>
                    </div>
                  );
                })}
              </div>
              
              <div className="calendar-day-activities-container">
                {selectedDateActivities.length === 0 ? (
                  <div className="calendar-day-empty">
                    <p>No activities scheduled for this day</p>
                    <div className="calendar-day-create-buttons">
                      <button 
                        className="create-activity-btn task-btn"
                        onClick={() => handleAddActivity('task')}
                      >
                        <FaClipboardList /> New Task
                      </button>
                      <button 
                        className="create-activity-btn meeting-btn"
                        onClick={() => handleAddActivity('meeting')}
                      >
                        <FaCalendarAlt /> New Meeting
                      </button>
                      <button 
                        className="create-activity-btn call-btn"
                        onClick={() => handleAddActivity('call')}
                      >
                        <FaPhone /> New Call
                      </button>
                    </div>
                  </div>
                ) : (
                  Array.from({ length: 24 }, (_, hour) => {
                    // Get activities for this hour
                    const hourActivities = selectedDateActivities.filter(activity => {
                      let activityHour = null;
                      try {
                        if (activity.time) {
                          // Parse time string like "7:15 AM" or "14:30"
                          const timeMatch = activity.time.match(/(\d+):(\d+)\s*(AM|PM)?/i);
                          if (timeMatch) {
                            let h = parseInt(timeMatch[1]);
                            if (timeMatch[3] && timeMatch[3].toUpperCase() === 'PM' && h !== 12) h += 12;
                            if (timeMatch[3] && timeMatch[3].toUpperCase() === 'AM' && h === 12) h = 0;
                            activityHour = h;
                          }
                        } else if (activity.from) {
                          const fromDate = new Date(activity.from);
                          if (!isNaN(fromDate.getTime())) {
                            activityHour = fromDate.getHours();
                          }
                        } else if (activity.scheduledTime || activity.callStartTime) {
                          const timeDate = new Date(activity.scheduledTime || activity.callStartTime);
                          if (!isNaN(timeDate.getTime())) {
                            activityHour = timeDate.getHours();
                          }
                        } else if (activity.dueDate) {
                          const dueDate = new Date(activity.dueDate);
                          if (!isNaN(dueDate.getTime())) {
                            activityHour = dueDate.getHours();
                          }
                        }
                        return activityHour === hour;
                      } catch (e) {
                        return false;
                      }
                    });

                    return (
                      <div 
                        key={hour} 
                        className="calendar-day-hour-slot"
                        onClick={(e) => {
                          // Only trigger if clicking on empty space (not on an activity block)
                          if (!e.target.closest('.calendar-day-activity-block')) {
                            e.stopPropagation();
                            setActivityMenu({ hour, x: e.clientX, y: e.clientY });
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {hourActivities.map((activity, idx) => {
                          const getActivityTime = () => {
                            if (activity.time) return activity.time;
                            try {
                              let date = null;
                              if (activity.from) date = new Date(activity.from);
                              else if (activity.scheduledTime) date = new Date(activity.scheduledTime);
                              else if (activity.callStartTime) date = new Date(activity.callStartTime);
                              else if (activity.dueDate) date = new Date(activity.dueDate);
                              
                              if (date && !isNaN(date.getTime())) {
                                return date.toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  hour12: true
                                });
                              }
                            } catch (e) {}
                            return null;
                          };

                          return (
                            <div
                              key={idx}
                              className="calendar-day-activity-block"
                              style={{
                                backgroundColor: getActivityColor(activity.type),
                                borderLeft: `4px solid ${getActivityColor(activity.type)}`
                              }}
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent hour slot click
                                onView(activity);
                              }}
                            >
                              <div className="calendar-day-activity-time">
                                {getActivityTime()}
                              </div>
                              <div className="calendar-day-activity-title">
                                {activity.subject || activity.title || `${activity.type} activity`}
                              </div>
                              {activity.description && (
                                <div className="calendar-day-activity-description">
                                  {activity.description}
                                </div>
                              )}
                              <div className="calendar-day-activity-icon">
                                {getActivityIcon(activity.type)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Activity Type Menu (for hour slots) */}
      {activityMenu && activityMenu.hour !== undefined && activityMenu.x !== undefined && activityMenu.y !== undefined && (
        <div 
          className="calendar-hour-activity-menu"
          style={{
            position: 'fixed',
            left: `${activityMenu.x}px`,
            top: `${activityMenu.y}px`,
            zIndex: 10001
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const hour = activityMenu.hour;
              setActivityMenu(null);
              handleAddActivity('task', hour);
            }}
          >
            <FaClipboardList /> New Task
          </button>
          <button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const hour = activityMenu.hour;
              setActivityMenu(null);
              handleAddActivity('meeting', hour);
            }}
          >
            <FaCalendarAlt /> New Meeting
          </button>
          <button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const hour = activityMenu.hour;
              setActivityMenu(null);
              handleAddActivity('call', hour);
            }}
          >
            <FaPhone /> New Call
          </button>
        </div>
      )}
    </div>
  );
};

// Activities Table Component - Groups activities by type and displays in tables
const ActivitiesTable = ({ activities, onClose, onOpen, onDelete, onView, isReadOnly, users, isClosed = false }) => {
  const getOwnerName = (ownerId) => {
    if (!ownerId || !users || users.length === 0) return 'Unassigned';
    const user = users.find(u => u.id === ownerId || u._id === ownerId);
    return user ? (user.name || user.email) : 'Unassigned';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return dateString;
    }
  };

  // Group activities by type
  const tasks = activities.filter(a => a.type === 'task');
  const meetings = activities.filter(a => a.type === 'meeting');
  const calls = activities.filter(a => a.type === 'call');

  return (
    <div className="activities-tables-container">
      {/* Tasks Table */}
      {tasks.length > 0 && (
        <div className="activity-type-section tasks-section">
          <div className="activity-type-header">
            <FaClipboardList className="activity-type-icon task-icon" />
            <h4>Tasks ({tasks.length})</h4>
          </div>
          <table className="activity-table task-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Due Date</th>
                <th>Priority</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map(activity => (
                <tr key={activity.id} className="activity-row task-row" onClick={() => onView(activity)}>
                  <td className="activity-subject">{activity.subject || 'Untitled Task'}</td>
                  <td>{formatDate(activity.dueDate)}</td>
                  <td>
                    <span className={`priority-badge priority-${activity.priority?.toLowerCase() || 'normal'}`}>
                      {activity.priority || 'Normal'}
                    </span>
                  </td>
                  <td>{getOwnerName(activity.owner)}</td>
                  <td className="status-cell">
                    <span className={`status-badge ${(activity.status || 'open') === 'open' ? 'in_progress' : 'completed'}`}>
                      {(activity.status || 'open') === 'open' ? 'In Progress' : 'Completed'}
                    </span>
                  </td>
                  <td className="activity-actions-cell" onClick={(e) => e.stopPropagation()}>
                    {!isClosed ? (
                      <>
                        <button className="view-btn" onClick={() => onView(activity)} title="View">
                          <FaEye /> <span>View</span>
                        </button>
                        <button className="close-btn" onClick={() => onClose(activity.id)} disabled={isReadOnly} title="Close">
                          <FaCheckCircle /> <span>Close</span>
                        </button>
                        <button className="delete-btn" onClick={() => onDelete(activity.id)} disabled={isReadOnly} title="Delete">
                          <FaTrash /> <span>Delete</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="view-btn" onClick={() => onView(activity)} title="View">
                          <FaEye /> <span>View</span>
                        </button>
                        <button className="open-btn" onClick={() => onOpen(activity.id)} disabled={isReadOnly} title="Reopen">
                          <FaCheckCircle /> <span>Reopen</span>
                        </button>
                        <button className="delete-btn" onClick={() => onDelete(activity.id)} disabled={isReadOnly} title="Delete">
                          <FaTrash /> <span>Delete</span>
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Meetings Table */}
      {meetings.length > 0 && (
        <div className="activity-type-section meetings-section">
          <div className="activity-type-header">
            <FaCalendarAlt className="activity-type-icon meeting-icon" />
            <h4>Meetings ({meetings.length})</h4>
          </div>
          <table className="activity-table meeting-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Location</th>
                <th>Start Date & Time</th>
                <th>End Date & Time</th>
                <th>Host</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {meetings.map(activity => (
                <tr key={activity.id} className="activity-row meeting-row" onClick={() => onView(activity)}>
                  <td className="activity-subject">{activity.title || 'Untitled Meeting'}</td>
                  <td>{activity.location || 'N/A'}</td>
                  <td>{formatDateTime(activity.from)}</td>
                  <td>{formatDateTime(activity.to)}</td>
                  <td>{getOwnerName(activity.host)}</td>
                  <td className="activity-actions-cell" onClick={(e) => e.stopPropagation()}>
                    {!isClosed ? (
                      <>
                        <button className="view-btn" onClick={() => onView(activity)} title="View">
                          <FaEye /> <span>View</span>
                        </button>
                        <button className="close-btn" onClick={() => onClose(activity.id)} disabled={isReadOnly} title="Close">
                          <FaCheckCircle /> <span>Close</span>
                        </button>
                        <button className="delete-btn" onClick={() => onDelete(activity.id)} disabled={isReadOnly} title="Delete">
                          <FaTrash /> <span>Delete</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="view-btn" onClick={() => onView(activity)} title="View">
                          <FaEye /> <span>View</span>
                        </button>
                        <button className="open-btn" onClick={() => onOpen(activity.id)} disabled={isReadOnly} title="Reopen">
                          <FaCheckCircle /> <span>Reopen</span>
                        </button>
                        <button className="delete-btn" onClick={() => onDelete(activity.id)} disabled={isReadOnly} title="Delete">
                          <FaTrash /> <span>Delete</span>
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Calls Table */}
      {calls.length > 0 && (
        <div className="activity-type-section calls-section">
          <div className="activity-type-header">
            <FaPhone className="activity-type-icon call-icon" />
            <h4>Calls ({calls.length})</h4>
          </div>
          <table className="activity-table call-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Type</th>
                <th>Date & Time</th>
                <th>Contact</th>
                <th>Duration</th>
                <th>Owner</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {calls.map(activity => (
                <tr key={activity.id} className="activity-row call-row" onClick={() => onView(activity)}>
                  <td className="activity-subject">{activity.subject || 'Untitled Call'}</td>
                  <td>
                    <span className={`call-type-badge ${activity.callDurationMinutes || activity.callDurationSeconds ? 'logged' : 'scheduled'}`}>
                      {activity.callDurationMinutes || activity.callDurationSeconds ? 'Logged' : 'Scheduled'}
                    </span>
                  </td>
                  <td>{formatDateTime(activity.callStartTime || activity.scheduledTime)}</td>
                  <td>{activity.contactName || activity.contactPhone || 'N/A'}</td>
                  <td>
                    {activity.callDurationMinutes 
                      ? `${activity.callDurationMinutes} min${activity.callDurationSeconds ? ` ${activity.callDurationSeconds} sec` : ''}`
                      : 'N/A'}
                  </td>
                  <td>{getOwnerName(activity.callOwner)}</td>
                  <td className="activity-actions-cell" onClick={(e) => e.stopPropagation()}>
                    {!isClosed ? (
                      <>
                        <button className="view-btn" onClick={() => onView(activity)} title="View">
                          <FaEye /> <span>View</span>
                        </button>
                        <button className="close-btn" onClick={() => onClose(activity.id)} disabled={isReadOnly} title="Close">
                          <FaCheckCircle /> <span>Close</span>
                        </button>
                        <button className="delete-btn" onClick={() => onDelete(activity.id)} disabled={isReadOnly} title="Delete">
                          <FaTrash /> <span>Delete</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="view-btn" onClick={() => onView(activity)} title="View">
                          <FaEye /> <span>View</span>
                        </button>
                        <button className="open-btn" onClick={() => onOpen(activity.id)} disabled={isReadOnly} title="Reopen">
                          <FaCheckCircle /> <span>Reopen</span>
                        </button>
                        <button className="delete-btn" onClick={() => onDelete(activity.id)} disabled={isReadOnly} title="Delete">
                          <FaTrash /> <span>Delete</span>
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Trials Manager Component
const TrialsManager = ({ formData, onInputChange, isReadOnly, users = [], onAutoSave, minDateString }) => {
  const [trials, setTrials] = useState(() => {
    return formData['trials'] || [];
  });
  
  const [editingTrial, setEditingTrial] = useState(null);
  const [showTrialForm, setShowTrialForm] = useState(false);
  
  // Sync trials state with formData prop changes
  const isSavingRef = useRef(false);
  
  useEffect(() => {
    if (isSavingRef.current) {
      return;
    }
    
    const formTrials = formData['trials'] || [];
    const currentTrialsStr = JSON.stringify(trials);
    const formTrialsStr = JSON.stringify(formTrials);
    if (formTrialsStr !== currentTrialsStr && formTrialsStr !== '[]') {
      setTrials(formTrials);
    }
  }, [formData['trials']]);
  
  const addTrial = () => {
    setEditingTrial(null);
    setShowTrialForm(true);
  };
  
  const editTrial = (trial) => {
    setEditingTrial(trial);
    setShowTrialForm(true);
  };
  
  const closeTrialForm = () => {
    setShowTrialForm(false);
    setEditingTrial(null);
  };
  
  const saveTrial = (trialData) => {
    isSavingRef.current = true;
    
    try {
      let updatedTrials;
      
      if (editingTrial) {
        // Update existing trial
        updatedTrials = trials.map(t => 
          t.id === editingTrial.id 
            ? { ...t, ...trialData, updatedAt: new Date().toISOString() }
            : t
        );
      } else {
        // Add new trial
        const newTrial = {
          id: `trial_${Date.now()}`,
          ...trialData,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        updatedTrials = [...trials, newTrial];
      }
      
      setTrials(updatedTrials);
      onInputChange('trials', updatedTrials);
      
      // Auto-save to backend if available
      if (onAutoSave) {
        onAutoSave(updatedTrials);
      }
      
      closeTrialForm();
    } catch (error) {
      console.error('Error saving trial:', error);
      alert('Failed to save trial. Please try again.');
    } finally {
      isSavingRef.current = false;
    }
  };
  
  const deleteTrial = (trialId) => {
    if (window.confirm('Are you sure you want to delete this trial?')) {
      const updatedTrials = trials.filter(t => t.id !== trialId);
      setTrials(updatedTrials);
      onInputChange('trials', updatedTrials);
      
      if (onAutoSave) {
        onAutoSave(updatedTrials);
      }
    }
  };
  
  const handleFileView = (file) => {
    const fileUrl = file.url || file.path || (file.id ? `/api/files/${file.id}` : null);
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    } else {
      alert('Unable to view file. File URL not available.');
    }
  };
  
  const handleFileDownload = async (file) => {
    try {
      await downloadWorkflowFile(file);
    } catch (error) {
      alert('Unable to download file. Please try again.');
    }
  };
  
  return (
    <div className="trials-manager">
      <div className="trials-header">
        <h3>Trials Management</h3>
        {!isReadOnly && (
          <button className="add-trial-btn" onClick={addTrial}>
            <FaPlus /> New Trial
          </button>
        )}
      </div>
      
      {showTrialForm && (
        <TrialForm
          trial={editingTrial}
          onSave={saveTrial}
          onCancel={closeTrialForm}
          isReadOnly={isReadOnly}
          users={users}
          formData={formData}
          minDateString={minDateString}
        />
      )}
      
      {!showTrialForm && (
        <div className="trials-list">
          {trials.length === 0 ? (
            <div className="no-trials">
              <p>No trials added yet. Click "New Trial" to add one.</p>
            </div>
          ) : (
            <div className="trials-grid">
              {trials.map((trial) => (
                <TrialCard
                  key={trial.id}
                  trial={trial}
                  onEdit={editTrial}
                  onDelete={deleteTrial}
                  isReadOnly={isReadOnly}
                  onFileView={handleFileView}
                  onFileDownload={handleFileDownload}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Trial Form Component
const TrialForm = ({ trial, onSave, onCancel, isReadOnly, users, formData, minDateString }) => {
  const effectiveMinDateString = minDateString || formatDateOnly(startOfDay(new Date()));
  const [formDataState, setFormDataState] = useState({
    // Section 1: Trial Setup
    proposedTrialStartDate: trial?.proposedTrialStartDate || '',
    proposedTrialEndDate: trial?.proposedTrialEndDate || '',
    numberOfTrials: trial?.numberOfTrials || '1',
    customNumberOfTrials: trial?.customNumberOfTrials || '',
    trialObjectives: trial?.trialObjectives || '',
    materialsProductsTested: trial?.materialsProductsTested || '',
    trialPlanSteps: trial?.trialPlanSteps || [],
    
    // Section 2: Trial Execution
    trialConductedOn: trial?.trialConductedOn || '',
    siteLocation: trial?.siteLocation || '',
    trialSummary: trial?.trialSummary || '',
    conductedPerAgreedParameters: trial?.conductedPerAgreedParameters || false,
    photosVideosLinks: trial?.photosVideosLinks || [],
    dataSheetTestResults: trial?.dataSheetTestResults || [],
    
    // Section 3: Evaluation & Client Feedback
    clientSatisfactionLevel: trial?.clientSatisfactionLevel || '',
    clientComments: trial?.clientComments || '',
    nesimTeamObservations: trial?.nesimTeamObservations || '',
    trialMeetsClientExpectations: trial?.trialMeetsClientExpectations || false,
    adjustmentsNeeded: trial?.adjustmentsNeeded || '',
    feedbackFormMinutes: trial?.feedbackFormMinutes || [],
    
    // Section 4: Decision & Follow-up
    proceedToProcurement: trial?.proceedToProcurement || '',
    followUpNotes: trial?.followUpNotes || '',
    followUpDate: trial?.followUpDate || '',
    contingencies: trial?.contingencies || '',
    finalTrialReport: trial?.finalTrialReport || []
  });
  
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRefs = {
    dataSheetTestResults: useRef(null),
    feedbackFormMinutes: useRef(null),
    finalTrialReport: useRef(null)
  };
  
  const handleInputChange = (field, value) => {
    setFormDataState(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleFileUpload = async (field, files) => {
    if (!files || files.length === 0) return;
    
    setUploadingFiles(true);
    try {
      const uploadedFiles = await uploadFilesWithAutoChunking({
        files: Array.from(files),
        context: 'workflows'
      });
      
      setFormDataState(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), ...uploadedFiles]
      }));
      
      // Reset file input after successful upload
      if (fileInputRefs[field] && fileInputRefs[field].current) {
        fileInputRefs[field].current.value = '';
      }
    } catch (error) {
      console.error('File upload error:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  };
  
  const handleFileRemove = (field, fileIndex) => {
    setFormDataState(prev => ({
      ...prev,
      [field]: prev[field].filter((_, index) => index !== fileIndex)
    }));
  };
  
  const handleFileView = (file) => {
    console.log('handleFileView called with file:', file);
    let fileUrl = null;
    
    // Try different URL sources in order of preference
    if (file.url) {
      fileUrl = file.url;
    } else if (file.path) {
      fileUrl = file.path;
    } else if (file.filename) {
      fileUrl = `/uploads/chat/${file.filename}`;
    } else if (file.name) {
      fileUrl = `/uploads/chat/${file.name}`;
    } else if (file._id) {
      // Backend might return file with _id
      fileUrl = `/uploads/chat/${file._id}`;
    }
    
    if (fileUrl) {
      // Ensure the URL is absolute - use backend URL since files are served from there
      const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://intranet.nesimltd.co.ke' : 'http://localhost:5000');
      let fullUrl;
      if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
        fullUrl = fileUrl;
      } else {
        // Construct full URL with backend base
        fullUrl = `${API_BASE}${fileUrl.startsWith('/') ? fileUrl : '/' + fileUrl}`;
      }
      
      console.log('Opening file URL:', fullUrl);
      window.open(fullUrl, '_blank');
    } else {
      console.error('Unable to view file - no URL found. File object:', file);
      alert('Unable to view file. File URL not available.');
    }
  };
  
  const handleFileDownload = async (file) => {
    try {
      await downloadWorkflowFile(file);
    } catch (error) {
      console.error('Unable to download file - no URL found. File object:', file, error);
      alert('Unable to download file. Please try again.');
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formDataState);
  };
  
  return (
    <div className="trial-form">
      <div className="trial-form-header">
        <h3>{trial ? 'Edit Trial' : 'New Trial'}</h3>
        <button className="close-form-btn" onClick={onCancel}>
          <FaArrowLeft /> Back
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        {/* Section 1: Trial Setup */}
        <div className="trial-section">
          <h4>Section 1: Trial Setup</h4>
          
          <div className="form-field">
            <label>Proposed Trial Start Date</label>
            <input
              type="date"
              value={formDataState.proposedTrialStartDate}
              onChange={(e) => handleInputChange('proposedTrialStartDate', e.target.value)}
              disabled={isReadOnly}
              min={effectiveMinDateString}
            />
          </div>
          
          <div className="form-field">
            <label>Proposed Trial End Date</label>
            <input
              type="date"
              value={formDataState.proposedTrialEndDate}
              onChange={(e) => handleInputChange('proposedTrialEndDate', e.target.value)}
              disabled={isReadOnly}
              min={effectiveMinDateString}
            />
          </div>
          
          <div className="form-field">
            <label>Number of Trials</label>
            <select
              value={formDataState.numberOfTrials}
              onChange={(e) => handleInputChange('numberOfTrials', e.target.value)}
              disabled={isReadOnly}
            >
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="custom">Custom</option>
            </select>
            {formDataState.numberOfTrials === 'custom' && (
              <input
                type="text"
                placeholder="Enter number"
                value={formDataState.customNumberOfTrials}
                onChange={(e) => handleInputChange('customNumberOfTrials', e.target.value)}
                disabled={isReadOnly}
                style={{ marginTop: '8px' }}
              />
            )}
          </div>
          
          <div className="form-field">
            <label>Trial Objectives</label>
            <textarea
              value={formDataState.trialObjectives}
              onChange={(e) => handleInputChange('trialObjectives', e.target.value)}
              disabled={isReadOnly}
              rows={4}
            />
          </div>
          
          <div className="form-field">
            <label>Materials/Products to be Tested</label>
            <textarea
              value={formDataState.materialsProductsTested}
              onChange={(e) => handleInputChange('materialsProductsTested', e.target.value)}
              disabled={isReadOnly}
              rows={4}
            />
          </div>
          
          <div className="form-field">
            <label>Trial Plan</label>
            <div className="trial-plan-steps">
              {formDataState.trialPlanSteps && formDataState.trialPlanSteps.length > 0 ? (
                formDataState.trialPlanSteps.map((step, index) => (
                  <div key={index} className="trial-plan-step">
                    <div className="trial-plan-step-header">
                      <span className="step-number">{index + 1}</span>
                      {!isReadOnly && (
                        <button
                          type="button"
                          className="remove-step-btn"
                          onClick={() => {
                            const updatedSteps = formDataState.trialPlanSteps.filter((_, i) => i !== index);
                            handleInputChange('trialPlanSteps', updatedSteps);
                          }}
                          title="Remove Step"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                    <textarea
                      value={step.description || ''}
                      onChange={(e) => {
                        const updatedSteps = [...formDataState.trialPlanSteps];
                        updatedSteps[index] = { ...updatedSteps[index], description: e.target.value };
                        handleInputChange('trialPlanSteps', updatedSteps);
                      }}
                      disabled={isReadOnly}
                      rows={3}
                      placeholder="Describe this step of the trial plan..."
                      className="trial-plan-step-input"
                    />
                  </div>
                ))
              ) : (
                <div className="no-plan-steps">
                  <p>No plan steps added yet. Click "Add Step" to start building your trial plan.</p>
                </div>
              )}
              {!isReadOnly && (
                <button
                  type="button"
                  className="add-plan-step-btn"
                  onClick={() => {
                    const newStep = { description: '' };
                    const updatedSteps = [...(formDataState.trialPlanSteps || []), newStep];
                    handleInputChange('trialPlanSteps', updatedSteps);
                  }}
                >
                  <FaPlus /> Add Step
                </button>
              )}
            </div>
          </div>
        </div>
        
        {/* Section 2: Trial Execution */}
        <div className="trial-section">
          <h4>Section 2: Trial Execution</h4>
          
          <div className="form-field">
            <label>Trial Conducted On</label>
            <input
              type="date"
              value={formDataState.trialConductedOn}
              onChange={(e) => handleInputChange('trialConductedOn', e.target.value)}
              disabled={isReadOnly}
              min={effectiveMinDateString}
            />
          </div>
          
          <div className="form-field">
            <label>Site Location</label>
            <textarea
              value={formDataState.siteLocation}
              onChange={(e) => handleInputChange('siteLocation', e.target.value)}
              disabled={isReadOnly}
              rows={3}
            />
          </div>
          
          <div className="form-field">
            <label>Trial Summary</label>
            <textarea
              value={formDataState.trialSummary}
              onChange={(e) => handleInputChange('trialSummary', e.target.value)}
              disabled={isReadOnly}
              rows={5}
            />
          </div>
          
          <div className="form-field">
            <label>
              <input
                type="checkbox"
                checked={formDataState.conductedPerAgreedParameters}
                onChange={(e) => handleInputChange('conductedPerAgreedParameters', e.target.checked)}
                disabled={isReadOnly}
              />
              Conducted per Agreed Parameters
            </label>
          </div>
          
          <div className="form-field">
            <label>Photos/Videos Links</label>
            <div className="links-section">
              {formDataState.photosVideosLinks && formDataState.photosVideosLinks.length > 0 && (
                <div className="links-list">
                  {formDataState.photosVideosLinks.map((link, index) => {
                    const linkUrl = typeof link === 'string' ? link : (link.url || link);
                    const linkLabel = typeof link === 'string' ? link : (link.label || link.url || link);
                    return (
                    <div key={index} className="link-item">
                      <a 
                        href={linkUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="link-url"
                      >
                        {linkLabel}
                      </a>
                      {!isReadOnly && (
                        <button
                          type="button"
                          className="remove-link-btn"
                          onClick={() => {
                            const updatedLinks = formDataState.photosVideosLinks.filter((_, i) => i !== index);
                            handleInputChange('photosVideosLinks', updatedLinks);
                          }}
                          title="Remove Link"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
              {!isReadOnly && (
                <div className="add-link-section">
                  <input
                    type="text"
                    placeholder="Enter photo/video URL..."
                    className="link-input"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        let newLink = e.target.value.trim();
                        // Ensure URL starts with http:// or https://
                        if (!newLink.startsWith('http://') && !newLink.startsWith('https://')) {
                          newLink = 'https://' + newLink;
                        }
                        const updatedLinks = [...(formDataState.photosVideosLinks || []), newLink];
                        handleInputChange('photosVideosLinks', updatedLinks);
                        e.target.value = '';
                      }
                    }}
                    disabled={isReadOnly}
                  />
                  <button
                    type="button"
                    className="add-link-btn"
                    onClick={(e) => {
                      const input = e.target.previousElementSibling;
                      if (input && input.value.trim()) {
                        let newLink = input.value.trim();
                        // Ensure URL starts with http:// or https://
                        if (!newLink.startsWith('http://') && !newLink.startsWith('https://')) {
                          newLink = 'https://' + newLink;
                        }
                        const updatedLinks = [...(formDataState.photosVideosLinks || []), newLink];
                        handleInputChange('photosVideosLinks', updatedLinks);
                        input.value = '';
                      }
                    }}
                    disabled={isReadOnly}
                  >
                    <FaPlus /> Add Link
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="form-field">
            <label>Data Sheet / Test Results</label>
            <input
              ref={fileInputRefs.dataSheetTestResults}
              type="file"
              multiple
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleFileUpload('dataSheetTestResults', e.target.files);
                }
              }}
              disabled={isReadOnly || uploadingFiles}
            />
            {formDataState.dataSheetTestResults && formDataState.dataSheetTestResults.length > 0 && (
              <div className="file-list">
                {formDataState.dataSheetTestResults.map((file, index) => (
                  <div key={index} className="file-item">
                    <FaPaperclip className="file-icon" />
                    <span className="file-name">{file.originalName || file.filename || file.name}</span>
                    <div className="file-actions">
                      <button type="button" onClick={() => handleFileView(file)} title="View">
                        <FaEye />
                      </button>
                      <button type="button" onClick={() => handleFileDownload(file)} title="Download">
                        <FaDownload />
                      </button>
                      {!isReadOnly && (
                        <button type="button" onClick={() => handleFileRemove('dataSheetTestResults', index)} title="Remove">
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Section 3: Evaluation & Client Feedback */}
        <div className="trial-section">
          <h4>Section 3: Evaluation & Client Feedback</h4>
          
          <div className="form-field">
            <label>Client Satisfaction Level</label>
            <select
              value={formDataState.clientSatisfactionLevel}
              onChange={(e) => handleInputChange('clientSatisfactionLevel', e.target.value)}
              disabled={isReadOnly}
            >
              <option value="">Select...</option>
              <option value="Very Satisfied">Very Satisfied</option>
              <option value="Satisfied">Satisfied</option>
              <option value="Neutral">Neutral</option>
              <option value="Dissatisfied">Dissatisfied</option>
            </select>
          </div>
          
          <div className="form-field">
            <label>Client Comments</label>
            <textarea
              value={formDataState.clientComments}
              onChange={(e) => handleInputChange('clientComments', e.target.value)}
              disabled={isReadOnly}
              rows={4}
            />
          </div>
          
          <div className="form-field">
            <label>NESIM Team Observations</label>
            <textarea
              value={formDataState.nesimTeamObservations}
              onChange={(e) => handleInputChange('nesimTeamObservations', e.target.value)}
              disabled={isReadOnly}
              rows={4}
            />
          </div>
          
          <div className="form-field">
            <label>
              <input
                type="checkbox"
                checked={formDataState.trialMeetsClientExpectations}
                onChange={(e) => handleInputChange('trialMeetsClientExpectations', e.target.checked)}
                disabled={isReadOnly}
              />
              Trial Meets Client Expectations
            </label>
          </div>
          
          <div className="form-field">
            <label>Adjustments Needed</label>
            <textarea
              value={formDataState.adjustmentsNeeded}
              onChange={(e) => handleInputChange('adjustmentsNeeded', e.target.value)}
              disabled={isReadOnly}
              rows={4}
            />
          </div>
          
          <div className="form-field">
            <label>Feedback Form / Minutes of Meeting</label>
            <input
              ref={fileInputRefs.feedbackFormMinutes}
              type="file"
              multiple
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleFileUpload('feedbackFormMinutes', e.target.files);
                }
              }}
              disabled={isReadOnly || uploadingFiles}
            />
            {formDataState.feedbackFormMinutes && formDataState.feedbackFormMinutes.length > 0 && (
              <div className="file-list">
                {formDataState.feedbackFormMinutes.map((file, index) => (
                  <div key={index} className="file-item">
                    <FaPaperclip className="file-icon" />
                    <span className="file-name">{file.originalName || file.filename || file.name}</span>
                    <div className="file-actions">
                      <button type="button" onClick={() => handleFileView(file)} title="View">
                        <FaEye />
                      </button>
                      <button type="button" onClick={() => handleFileDownload(file)} title="Download">
                        <FaDownload />
                      </button>
                      {!isReadOnly && (
                        <button type="button" onClick={() => handleFileRemove('feedbackFormMinutes', index)} title="Remove">
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Section 4: Decision & Follow-up */}
        <div className="trial-section">
          <h4>Section 4: Decision & Follow-up</h4>
          
          <div className="form-field">
            <label>Proceed to Procurement?</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="proceedToProcurement"
                  value="Yes"
                  checked={formDataState.proceedToProcurement === 'Yes'}
                  onChange={(e) => handleInputChange('proceedToProcurement', e.target.value)}
                  disabled={isReadOnly}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="proceedToProcurement"
                  value="No"
                  checked={formDataState.proceedToProcurement === 'No'}
                  onChange={(e) => handleInputChange('proceedToProcurement', e.target.value)}
                  disabled={isReadOnly}
                />
                No
              </label>
              <label>
                <input
                  type="radio"
                  name="proceedToProcurement"
                  value="Pending Decision"
                  checked={formDataState.proceedToProcurement === 'Pending Decision'}
                  onChange={(e) => handleInputChange('proceedToProcurement', e.target.value)}
                  disabled={isReadOnly}
                />
                Pending Decision
              </label>
            </div>
          </div>
          
          <div className="form-field">
            <label>Follow-up Notes</label>
            <textarea
              value={formDataState.followUpNotes}
              onChange={(e) => handleInputChange('followUpNotes', e.target.value)}
              disabled={isReadOnly}
              rows={4}
            />
          </div>
          
          <div className="form-field">
            <label>Follow-up Date</label>
            <input
              type="date"
              value={formDataState.followUpDate}
              onChange={(e) => handleInputChange('followUpDate', e.target.value)}
              disabled={isReadOnly}
              min={effectiveMinDateString}
            />
          </div>
          
          <div className="form-field">
            <label>Contingencies</label>
            <textarea
              value={formDataState.contingencies}
              onChange={(e) => handleInputChange('contingencies', e.target.value)}
              disabled={isReadOnly}
              rows={4}
            />
          </div>
          
          <div className="form-field">
            <label>Final Trial Report</label>
            <input
              ref={fileInputRefs.finalTrialReport}
              type="file"
              multiple
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleFileUpload('finalTrialReport', e.target.files);
                }
              }}
              disabled={isReadOnly || uploadingFiles}
            />
            {formDataState.finalTrialReport && formDataState.finalTrialReport.length > 0 && (
              <div className="file-list">
                {formDataState.finalTrialReport.map((file, index) => (
                  <div key={index} className="file-item">
                    <FaPaperclip className="file-icon" />
                    <span className="file-name">{file.originalName || file.filename || file.name}</span>
                    <div className="file-actions">
                      <button type="button" onClick={() => handleFileView(file)} title="View">
                        <FaEye />
                      </button>
                      <button type="button" onClick={() => handleFileDownload(file)} title="Download">
                        <FaDownload />
                      </button>
                      {!isReadOnly && (
                        <button type="button" onClick={() => handleFileRemove('finalTrialReport', index)} title="Remove">
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {!isReadOnly && (
          <div className="form-actions">
            <button type="submit" className="save-btn" disabled={uploadingFiles}>
              <FaSave /> {trial ? 'Update Trial' : 'Save Trial'}
            </button>
            <button type="button" onClick={onCancel} className="cancel-btn">
              Cancel
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

// Trial Card Component
const TrialCard = ({ trial, onEdit, onDelete, isReadOnly, onFileView, onFileDownload }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };
  
  return (
    <div className="trial-card">
      <div className="trial-card-header">
        <h4>{trial.proposedTrialStartDate ? `Trial - ${formatDate(trial.proposedTrialStartDate)}` : 'Unnamed Trial'}</h4>
        {!isReadOnly && (
          <div className="trial-card-actions">
            <button className="edit-btn" onClick={() => onEdit(trial)} title="Edit">
              <FaEdit />
            </button>
            <button className="delete-btn" onClick={() => onDelete(trial.id)} title="Delete">
              <FaTrash />
            </button>
          </div>
        )}
      </div>
      
      <div className="trial-card-content">
        <div className="trial-card-section">
          <h5>Trial Setup</h5>
          <p><strong>Proposed Dates:</strong> {formatDate(trial.proposedTrialStartDate)} - {formatDate(trial.proposedTrialEndDate)}</p>
          <p><strong>Number of Trials:</strong> {trial.numberOfTrials === 'custom' ? trial.customNumberOfTrials : trial.numberOfTrials}</p>
          {trial.trialPlanSteps && trial.trialPlanSteps.length > 0 && (
            <div className="trial-plan-summary">
              <p><strong>Trial Plan Steps:</strong> {trial.trialPlanSteps.length}</p>
              <ul style={{ marginTop: '8px', paddingLeft: '20px', fontSize: '0.875rem', color: '#64748b' }}>
                {trial.trialPlanSteps.slice(0, 3).map((step, index) => (
                  <li key={index}>{step.description || `Step ${index + 1}`}</li>
                ))}
                {trial.trialPlanSteps.length > 3 && (
                  <li>... and {trial.trialPlanSteps.length - 3} more steps</li>
                )}
              </ul>
            </div>
          )}
        </div>
        
        {trial.trialConductedOn && (
          <div className="trial-card-section">
            <h5>Trial Execution</h5>
            <p><strong>Conducted On:</strong> {formatDate(trial.trialConductedOn)}</p>
            <p><strong>Location:</strong> {trial.siteLocation || 'N/A'}</p>
            <p><strong>Conducted per Agreed Parameters:</strong> {trial.conductedPerAgreedParameters ? 'Yes' : 'No'}</p>
          </div>
        )}
        
        {trial.clientSatisfactionLevel && (
          <div className="trial-card-section">
            <h5>Evaluation</h5>
            <p><strong>Satisfaction:</strong> {trial.clientSatisfactionLevel}</p>
            <p><strong>Meets Expectations:</strong> {trial.trialMeetsClientExpectations ? 'Yes' : 'No'}</p>
          </div>
        )}
        
        {trial.proceedToProcurement && (
          <div className="trial-card-section">
            <h5>Decision</h5>
            <p><strong>Proceed to Procurement:</strong> {trial.proceedToProcurement}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Activity Card Component (kept for backward compatibility if needed)
const ActivityCard = ({ activity, onClose, onOpen, onDelete, onClick, isReadOnly, users, isClosed = false }) => {
  const getActivityIcon = () => {
    switch (activity.type) {
      case 'task': return <FaClipboardList />;
      case 'meeting': return <FaCalendarAlt />;
      case 'call': return <FaPhone />;
      default: return <FaInfoCircle />;
    }
  };
  
  const getActivityTitle = () => {
    switch (activity.type) {
      case 'task': return activity.subject || 'Task';
      case 'meeting': return activity.title || 'Meeting';
      case 'call': return activity.subject || 'Call';
      default: return 'Activity';
    }
  };
  
  const getOwnerName = (ownerId) => {
    if (!ownerId || !users || users.length === 0) return null;
    const user = users.find(u => u.id === ownerId || u._id === ownerId);
    return user ? (user.name || user.email) : null;
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };
  
  const getDateDisplay = () => {
    if (activity.type === 'task' && activity.dueDate) {
      return formatDate(activity.dueDate);
    } else if (activity.type === 'meeting' && activity.from) {
      return formatDate(activity.from);
    } else if (activity.type === 'call' && activity.callStartTime) {
      return formatDate(activity.callStartTime);
    }
    return null;
  };
  
  const getOwnerDisplay = () => {
    if (activity.type === 'task' && activity.owner) {
      return getOwnerName(activity.owner);
    } else if (activity.type === 'meeting' && activity.host) {
      return getOwnerName(activity.host);
    } else if (activity.type === 'call' && activity.callOwner) {
      return getOwnerName(activity.callOwner);
    }
    return null;
  };
  
  const dateDisplay = getDateDisplay();
  const ownerDisplay = getOwnerDisplay();
  
  const handleCardClick = (e) => {
    // Only open detail view if clicking on the card content, not on buttons
    if (e.target.closest('.activity-card-actions')) {
      return; // Don't trigger onClick if clicking on actions
    }
    if (onClick) {
      onClick();
    }
  };
  
  return (
    <div className="activity-card" onClick={handleCardClick}>
      <div className="activity-card-content">
        <div className="activity-icon">{getActivityIcon()}</div>
        <div className="activity-info">
          <h4 className="activity-title">{getActivityTitle()}</h4>
          {dateDisplay && (
            <div className="activity-date">{dateDisplay}</div>
          )}
          {ownerDisplay && (
            <div className="activity-owner">
              <FaUser className="owner-icon" />
              <span>{ownerDisplay}</span>
            </div>
          )}
          {isClosed && activity.closedAt && (
            <div className="activity-closed-time">
              Closed Time: {new Date(activity.closedAt).toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              })}
            </div>
          )}
        </div>
      </div>
      <div className="activity-card-actions" onClick={(e) => e.stopPropagation()}>
        {!isReadOnly && (
          <button className="view-activity-btn" onClick={(e) => { e.stopPropagation(); if (onClick) onClick(); }} title="View activity details">
            <FaEye /> View
          </button>
        )}
        {isClosed && !isReadOnly && onOpen && (
          <button className="open-activity-btn" onClick={(e) => { e.stopPropagation(); onOpen(); }} title="Reopen activity">
            Open
          </button>
        )}
        {!isClosed && !isReadOnly && onClose && (
          <button className="close-activity-btn" onClick={(e) => { e.stopPropagation(); onClose(); }} title="Close activity">
            Close
          </button>
        )}
        {!isReadOnly && (
          <button className="delete-activity-btn" onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete activity">
            <FaTrash /> Delete
          </button>
        )}
      </div>
    </div>
  );
};

// Activity Form Component
const ActivityForm = ({ type, callMode, setCallMode, initialData, onSave, onCancel, users, isReadOnly, minDateString, minDateTimeString }) => {
  const [attachments, setAttachments] = useState(() => {
    return initialData?.attachments || [];
  });
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const defaultMinDateString = formatDateOnly(startOfDay(new Date()));
  const defaultMinDateTimeString = formatDateTimeLocal(startOfDay(new Date()));
  const effectiveMinDateString = minDateString || defaultMinDateString;
  const effectiveMinDateTimeString = minDateTimeString || defaultMinDateTimeString;
  
  // Upload files to backend
  const uploadFilesToBackend = async (files) => {
    return uploadFilesWithAutoChunking({
      files,
      context: 'workflows'
    });
  };
  
  const handleAttachmentChange = async (files) => {
    const filesArray = Array.isArray(files) ? files : [];

    if (filesArray.length === 0) {
      setAttachments([]);
      return;
    }

    const isPersistedFile = (file) =>
      !(file instanceof File) && (file?.url || file?.id || file?.filename);

    const hasPersistedFiles = filesArray.some(isPersistedFile);

    const newFiles = filesArray.filter(file => file instanceof File);
    const existingFiles = filesArray.filter(isPersistedFile);

    if (newFiles.length === 0) {
      setAttachments(filesArray);
      return;
    }

    try {
      setUploadingFiles(true);
      const uploadedFiles = await uploadFilesToBackend(newFiles);
      setAttachments([...existingFiles, ...uploadedFiles]);
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  };
  
  const handleRemoveAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  
  // Handle file viewing (same logic as DynamicListField and ActivityDetailView)
  const handleFileView = (file) => {
    console.log('handleFileView called with file:', file);
    let fileUrl = null;
    
    // Try different URL sources in order of preference
    if (file.url) {
      fileUrl = file.url;
    } else if (file.path) {
      fileUrl = file.path;
    } else if (file.filename) {
      fileUrl = `/uploads/chat/${file.filename}`;
    }
    
    if (!fileUrl) {
      console.error('No valid file URL found');
      alert('Unable to view file: No valid URL found');
      return;
    }
    
    // Construct full URL if relative
    const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://intranet.nesimltd.co.ke' : 'http://localhost:5000');
    const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API_BASE}${fileUrl}`;
    
    console.log('Opening file URL:', fullUrl);
    const newWindow = window.open(fullUrl, '_blank');
    if (!newWindow) {
      console.error('Failed to open new window - popup blocked?');
      alert('Unable to open file. Please check if popups are blocked.');
    }
  };
  
  // Handle file download (same logic as DynamicListField and ActivityDetailView)
  const handleFileDownload = async (file) => {
    try {
      await downloadWorkflowFile(file);
    } catch (error) {
      console.error('Unable to download file:', error);
      alert('Unable to download file. Please try again.');
    }
  };
  
  // Helper function to convert ISO date strings to datetime-local format
  const convertDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return '';
      // Format as YYYY-MM-DDTHH:mm for datetime-local inputs
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      return '';
    }
  };
  
  // Helper function to convert ISO date strings to date format (YYYY-MM-DD)
  const convertDateOnlyForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return '';
    }
  };
  
  const [formData, setFormData] = useState(() => {
    // Initialize with existing data if editing
    if (initialData) {
      // Exclude internal fields that shouldn't be in form data
      const { id, createdAt, updatedAt, closedAt, status, attachments: initialAttachments, ...formFields } = initialData;
      
      // Convert date fields to proper format for inputs
      const processedData = { ...formFields };
      
      // Convert datetime-local fields
      if (processedData.reminder) processedData.reminder = convertDateForInput(processedData.reminder);
      if (processedData.from) processedData.from = convertDateForInput(processedData.from);
      if (processedData.to) processedData.to = convertDateForInput(processedData.to);
      if (processedData.callStartTime) processedData.callStartTime = convertDateForInput(processedData.callStartTime);
      if (processedData.participantsReminder) processedData.participantsReminder = convertDateForInput(processedData.participantsReminder);
      
      // Convert date-only fields
      if (processedData.dueDate) processedData.dueDate = convertDateOnlyForInput(processedData.dueDate);
      
      return processedData;
    }
    return {};
  });
  
  useEffect(() => {
    // Update form data when initialData changes (when switching from detail view to edit)
    if (initialData) {
      const { id, createdAt, updatedAt, closedAt, status, attachments: initialAttachments, ...formFields } = initialData;
      
      // Convert date fields to proper format for inputs
      const processedData = { ...formFields };
      
      // Convert datetime-local fields
      if (processedData.reminder) processedData.reminder = convertDateForInput(processedData.reminder);
      if (processedData.from) processedData.from = convertDateForInput(processedData.from);
      if (processedData.to) processedData.to = convertDateForInput(processedData.to);
      if (processedData.callStartTime) processedData.callStartTime = convertDateForInput(processedData.callStartTime);
      if (processedData.participantsReminder) processedData.participantsReminder = convertDateForInput(processedData.participantsReminder);
      
      // Convert date-only fields
      if (processedData.dueDate) processedData.dueDate = convertDateOnlyForInput(processedData.dueDate);
      
      setFormData(processedData);
      setAttachments(initialAttachments || []);
    } else {
      setFormData({});
      setAttachments([]);
    }
  }, [initialData]);
  
  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // Include attachments in the saved data
    onSave({ ...formData, attachments });
  };
  
  const isEditing = !!initialData;
  
  if (type === 'task') {
    return <TaskForm formData={formData} attachments={attachments} uploadingFiles={uploadingFiles} onFieldChange={handleFieldChange} onAttachmentChange={handleAttachmentChange} onRemoveAttachment={handleRemoveAttachment} onFileView={handleFileView} onFileDownload={handleFileDownload} onSave={handleSubmit} onCancel={onCancel} users={users} isReadOnly={isReadOnly} isEditing={isEditing} minDateString={effectiveMinDateString} minDateTimeString={effectiveMinDateTimeString} />;
  } else if (type === 'meeting') {
    return <MeetingForm formData={formData} attachments={attachments} uploadingFiles={uploadingFiles} onFieldChange={handleFieldChange} onAttachmentChange={handleAttachmentChange} onRemoveAttachment={handleRemoveAttachment} onFileView={handleFileView} onFileDownload={handleFileDownload} onSave={handleSubmit} onCancel={onCancel} users={users} isReadOnly={isReadOnly} isEditing={isEditing} minDateString={effectiveMinDateString} minDateTimeString={effectiveMinDateTimeString} />;
  } else if (type === 'call') {
    return <CallForm callMode={callMode} setCallMode={setCallMode} formData={formData} attachments={attachments} uploadingFiles={uploadingFiles} onFieldChange={handleFieldChange} onAttachmentChange={handleAttachmentChange} onRemoveAttachment={handleRemoveAttachment} onFileView={handleFileView} onFileDownload={handleFileDownload} onSave={handleSubmit} onCancel={onCancel} users={users} isReadOnly={isReadOnly} isEditing={isEditing} minDateString={effectiveMinDateString} minDateTimeString={effectiveMinDateTimeString} />;
  }
  
  return null;
};

// Task Form Component
const TaskForm = ({ formData, attachments = [], uploadingFiles = false, onFieldChange, onAttachmentChange, onRemoveAttachment, onFileView, onFileDownload, onSave, onCancel, users, isReadOnly, isEditing, minDateString, minDateTimeString }) => {
  const fallbackDate = formatDateOnly(startOfDay(new Date()));
  const fallbackDateTime = formatDateTimeLocal(startOfDay(new Date()));
  const effectiveMinDateString = minDateString || fallbackDate;
  const effectiveMinDateTimeString = minDateTimeString || fallbackDateTime;
  return (
    <form className="activity-form" onSubmit={onSave}>
      <div className="form-header">
        <h3>{isEditing ? 'Edit Task' : 'Create Task'}</h3>
      </div>
      <div className="form-content">
        <div className="form-field">
          <label>Subject <span className="required">*</span></label>
          <input
            type="text"
            value={formData.subject || ''}
            onChange={(e) => onFieldChange('subject', e.target.value)}
            required
            disabled={isReadOnly}
            className="form-input"
          />
        </div>
        
        <div className="form-field">
          <label>Due Date <span className="required">*</span></label>
          <input
            type="date"
            value={formData.dueDate || ''}
            onChange={(e) => onFieldChange('dueDate', e.target.value)}
            required
            disabled={isReadOnly}
            className="form-input"
            min={effectiveMinDateString}
          />
        </div>
        
        <div className="form-field">
          <label>Priority</label>
          <select
            value={formData.priority || 'Normal'}
            onChange={(e) => onFieldChange('priority', e.target.value)}
            disabled={isReadOnly}
            className="form-select"
          >
            <option value="High">High</option>
            <option value="Normal">Normal</option>
            <option value="Low">Low</option>
          </select>
        </div>
        
        <div className="form-field">
          <label>Owner</label>
          <select
            value={formData.owner || ''}
            onChange={(e) => onFieldChange('owner', e.target.value)}
            disabled={isReadOnly}
            className="form-select"
          >
            <option value="">Select Owner</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>{user.name || user.email}</option>
            ))}
          </select>
        </div>
        
        <div className="form-field">
          <label>Reminder</label>
          <input
            type="datetime-local"
            value={formData.reminder || ''}
            onChange={(e) => onFieldChange('reminder', e.target.value)}
            disabled={isReadOnly}
            className="form-input"
            min={effectiveMinDateTimeString}
          />
        </div>
        
        {/* File Upload Section */}
        <div className="form-field">
          <label>Attachments</label>
          <div className="file-upload-section">
            <div className="file-input-wrapper">
              <input
                type="file"
                name="files"
                multiple
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    const newFiles = Array.from(e.target.files);
                    const existingFiles = attachments || [];
                    onAttachmentChange([...existingFiles, ...newFiles]);
                  }
                  e.target.value = '';
                }}
                disabled={isReadOnly || uploadingFiles}
                className="form-file-input-hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
                id={`task-file-input-${isEditing ? 'edit' : 'new'}`}
              />
              <label htmlFor={`task-file-input-${isEditing ? 'edit' : 'new'}`} className="file-input-label">
                {uploadingFiles ? 'Uploading...' : (attachments && attachments.length > 0 ? 'Add More Files' : 'Choose Files')}
              </label>
            </div>
            {attachments && attachments.length > 0 && (
              <div className="file-list">
                {attachments.map((file, index) => (
                  <div key={index} className="file-item">
                    <FaPaperclip className="file-icon" />
                    <span className="file-name">{file.originalName || file.name || file.filename}</span>
                    <div className="file-actions">
                      {file.url || file.path || file.filename ? (
                        <>
                          <button
                            type="button"
                            onClick={() => onFileView && onFileView(file)}
                            className="view-file-btn"
                            title="Preview File"
                          >
                            <FaEye />
                          </button>
                          <button
                            type="button"
                            onClick={() => onFileDownload && onFileDownload(file)}
                            className="download-file-btn"
                            title="Download File"
                          >
                            <FaDownload />
                          </button>
                        </>
                      ) : null}
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => onRemoveAttachment(index)}
                          className="remove-file-btn"
                          title="Remove file"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-btn">Cancel</button>
        {!isReadOnly && <button type="submit" className="save-btn" disabled={uploadingFiles}>Save</button>}
      </div>
    </form>
  );
};

// Meeting Form Component
const MeetingForm = ({ formData, attachments = [], uploadingFiles = false, onFieldChange, onAttachmentChange, onRemoveAttachment, onFileView, onFileDownload, onSave, onCancel, users, isReadOnly, isEditing, minDateString, minDateTimeString }) => {
  const fallbackDateTime = formatDateTimeLocal(startOfDay(new Date()));
  const effectiveMinDateTimeString = minDateTimeString || fallbackDateTime;
  const [participants, setParticipants] = useState(() => formData.participants || []);
  
  useEffect(() => {
    // Update participants when formData changes (when editing)
    if (formData.participants) {
      setParticipants(formData.participants);
    }
  }, [formData.participants]);
  
  const addParticipant = () => {
    const newParticipant = { id: Date.now().toString(), name: '', email: '' };
    setParticipants([...participants, newParticipant]);
    onFieldChange('participants', [...participants, newParticipant]);
  };
  
  const removeParticipant = (id) => {
    const updated = participants.filter(p => p.id !== id);
    setParticipants(updated);
    onFieldChange('participants', updated);
  };
  
  const updateParticipant = (id, field, value) => {
    const updated = participants.map(p => p.id === id ? { ...p, [field]: value } : p);
    setParticipants(updated);
    onFieldChange('participants', updated);
  };
  
  return (
    <form className="activity-form" onSubmit={onSave}>
      <div className="form-header">
        <h3>{isEditing ? 'Edit Meeting' : 'Meeting Information'}</h3>
      </div>
      <div className="form-content">
        <div className="form-field">
          <label>Title <span className="required">*</span></label>
          <input
            type="text"
            value={formData.title || ''}
            onChange={(e) => onFieldChange('title', e.target.value)}
            required
            disabled={isReadOnly}
            className="form-input"
          />
        </div>
        
        <div className="form-field">
          <label>Meeting Venue</label>
          <input
            type="text"
            value={formData.meetingVenue || ''}
            onChange={(e) => onFieldChange('meetingVenue', e.target.value)}
            disabled={isReadOnly}
            className="form-input"
          />
        </div>
        
        <div className="form-field">
          <label>Client location</label>
          <input
            type="text"
            value={formData.clientLocation || ''}
            onChange={(e) => onFieldChange('clientLocation', e.target.value)}
            disabled={isReadOnly}
            className="form-input"
          />
        </div>
        
        <div className="form-field">
          <label>Location</label>
          <input
            type="text"
            value={formData.location || ''}
            onChange={(e) => onFieldChange('location', e.target.value)}
            disabled={isReadOnly}
            className="form-input"
          />
        </div>
        
        <div className="form-field">
          <label>
            <input
              type="checkbox"
              checked={formData.allDay || false}
              onChange={(e) => onFieldChange('allDay', e.target.checked)}
              disabled={isReadOnly}
            />
            All day
          </label>
        </div>
        
        <div className="form-field">
          <label>From</label>
          <input
            type="datetime-local"
            value={formData.from || ''}
            onChange={(e) => onFieldChange('from', e.target.value)}
            disabled={isReadOnly}
            className="form-input"
            min={effectiveMinDateTimeString}
          />
        </div>
        
        <div className="form-field">
          <label>To</label>
          <input
            type="datetime-local"
            value={formData.to || ''}
            onChange={(e) => onFieldChange('to', e.target.value)}
            disabled={isReadOnly}
            className="form-input"
            min={effectiveMinDateTimeString}
          />
        </div>
        
        <div className="form-field">
          <label>Host</label>
          <select
            value={formData.host || ''}
            onChange={(e) => onFieldChange('host', e.target.value)}
            disabled={isReadOnly}
            className="form-select"
          >
            <option value="">Select Host</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>{user.name || user.email}</option>
            ))}
          </select>
        </div>
        
        <div className="form-field">
          <label>Participants</label>
          <div className="participants-list">
            {participants.length === 0 ? (
              <div className="empty-participants">None</div>
            ) : (
              participants.map(p => (
                <div key={p.id} className="participant-item">
                  <input
                    type="text"
                    value={p.name || ''}
                    onChange={(e) => updateParticipant(p.id, 'name', e.target.value)}
                    placeholder="Name"
                    disabled={isReadOnly}
                    className="form-input"
                  />
                  <input
                    type="email"
                    value={p.email || ''}
                    onChange={(e) => updateParticipant(p.id, 'email', e.target.value)}
                    placeholder="Email"
                    disabled={isReadOnly}
                    className="form-input"
                  />
                  {!isReadOnly && (
                    <button type="button" onClick={() => removeParticipant(p.id)} className="remove-participant-btn">
                      <FaTrash />
                    </button>
                  )}
                </div>
              ))
            )}
            {!isReadOnly && (
              <button type="button" onClick={addParticipant} className="add-participant-btn">
                <FaPlus /> Add
              </button>
            )}
          </div>
        </div>
        
        <div className="form-field">
          <label>Related To</label>
          <select
            value={formData.relatedTo || 'None'}
            onChange={(e) => onFieldChange('relatedTo', e.target.value)}
            disabled={isReadOnly}
            className="form-select"
          >
            <option value="None">None</option>
            <option value="Lead">Lead</option>
            <option value="Customers">Customers</option>
            <option value="Others">Others</option>
          </select>
        </div>
        
        <div className="form-field">
          <label>Participants Reminder</label>
          <input
            type="datetime-local"
            value={formData.participantsReminder || ''}
            onChange={(e) => onFieldChange('participantsReminder', e.target.value)}
            disabled={isReadOnly}
            className="form-input"
            min={effectiveMinDateTimeString}
          />
        </div>
        
        {/* File Upload Section */}
        <div className="form-field">
          <label>Attachments</label>
          <div className="file-upload-section">
            <div className="file-input-wrapper">
              <input
                type="file"
                name="files"
                multiple
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    const newFiles = Array.from(e.target.files);
                    const existingFiles = attachments || [];
                    onAttachmentChange([...existingFiles, ...newFiles]);
                  }
                  e.target.value = '';
                }}
                disabled={isReadOnly || uploadingFiles}
                className="form-file-input-hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
                id={`meeting-file-input-${isEditing ? 'edit' : 'new'}`}
              />
              <label htmlFor={`meeting-file-input-${isEditing ? 'edit' : 'new'}`} className="file-input-label">
                {uploadingFiles ? 'Uploading...' : (attachments && attachments.length > 0 ? 'Add More Files' : 'Choose Files')}
              </label>
            </div>
            {attachments && attachments.length > 0 && (
              <div className="file-list">
                {attachments.map((file, index) => (
                  <div key={index} className="file-item">
                    <FaPaperclip className="file-icon" />
                    <span className="file-name">{file.originalName || file.name || file.filename}</span>
                    <div className="file-actions">
                      {file.url || file.path || file.filename ? (
                        <>
                          <button
                            type="button"
                            onClick={() => onFileView && onFileView(file)}
                            className="view-file-btn"
                            title="Preview File"
                          >
                            <FaEye />
                          </button>
                          <button
                            type="button"
                            onClick={() => onFileDownload && onFileDownload(file)}
                            className="download-file-btn"
                            title="Download File"
                          >
                            <FaDownload />
                          </button>
                        </>
                      ) : null}
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => onRemoveAttachment(index)}
                          className="remove-file-btn"
                          title="Remove file"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-btn">Cancel</button>
        {!isReadOnly && <button type="submit" className="save-btn" disabled={uploadingFiles}>Save</button>}
      </div>
    </form>
  );
};

// Call Form Component
const CallForm = ({ callMode, setCallMode, formData, attachments = [], uploadingFiles = false, onFieldChange, onAttachmentChange, onRemoveAttachment, onFileView, onFileDownload, onSave, onCancel, users, isReadOnly, isEditing, minDateString, minDateTimeString }) => {
  const fallbackDateTime = formatDateTimeLocal(startOfDay(new Date()));
  const effectiveMinDateTimeString = minDateTimeString || fallbackDateTime;
  // Update callMode based on formData when editing
  useEffect(() => {
    if (isEditing && formData) {
      // If editing and form has duration, it's a logged call
      if (formData.callDurationMinutes !== undefined || formData.callDurationSeconds !== undefined) {
        if (callMode !== 'log') setCallMode('log');
      } else if (formData.callStartTime && callMode !== 'schedule') {
        // If it has start time but no duration, it's scheduled
        setCallMode('schedule');
      }
    }
  }, [isEditing, formData, callMode, setCallMode]);
  
  return (
    <form className="activity-form" onSubmit={onSave}>
      <div className="form-header">
        <h3>{isEditing ? `Edit ${callMode === 'schedule' ? 'Scheduled' : 'Logged'} Call` : (callMode === 'schedule' ? 'Schedule a call' : 'Log a call')}</h3>
        <div className="call-mode-switch">
          <button
            type="button"
            className={callMode === 'schedule' ? 'active' : ''}
            onClick={() => setCallMode('schedule')}
          >
            Schedule a call
          </button>
          <button
            type="button"
            className={callMode === 'log' ? 'active' : ''}
            onClick={() => setCallMode('log')}
          >
            Log a call
          </button>
        </div>
      </div>
      
      <div className="form-content">
        <h4>Call Information</h4>
        
        <div className="form-field">
          <label>Call For</label>
          <select
            value={formData.callFor || 'Lead'}
            onChange={(e) => onFieldChange('callFor', e.target.value)}
            disabled={isReadOnly}
            className="form-select"
          >
            <option value="Lead">Lead</option>
            <option value="Customers">Customers</option>
          </select>
        </div>
        
        <div className="form-field">
          <label>Related To</label>
          <select
            value={formData.relatedTo || ''}
            onChange={(e) => onFieldChange('relatedTo', e.target.value)}
            disabled={isReadOnly}
            className="form-select"
          >
            <option value="">-None-</option>
            <option value="Customer Mapping">Customer Mapping</option>
            <option value="Inventory">Inventory</option>
            <option value="Nesim Customer">Nesim Customer</option>
            <option value="Suppliers">Suppliers</option>
            <option value="Deal">Deal</option>
            <option value="Quote">Quote</option>
            <option value="Sales Order">Sales Order</option>
            <option value="Invoice">Invoice</option>
            <option value="Case">Case</option>
            <option value="Customer Preference">Customer Preference</option>
            <option value="Cost Cards">Cost Cards</option>
          </select>
        </div>
        
        <div className="form-field">
          <label>Call Type</label>
          <select
            value={formData.callType || 'Outbound'}
            onChange={(e) => onFieldChange('callType', e.target.value)}
            disabled={isReadOnly}
            className="form-select"
          >
            <option value="Outbound">Outbound</option>
            <option value="Inbound">Inbound</option>
            <option value="Missed">Missed</option>
          </select>
        </div>
        
        <div className="form-field">
          <label>Outgoing Call Status</label>
          <select
            value={formData.callStatus || 'Scheduled'}
            onChange={(e) => onFieldChange('callStatus', e.target.value)}
            disabled={isReadOnly}
            className="form-select"
          >
            <option value="Scheduled">Scheduled</option>
            <option value="Completed">Completed</option>
          </select>
        </div>
        
        <div className="form-field">
          <label>Call Start Time</label>
          <input
            type="datetime-local"
            value={formData.callStartTime || ''}
            onChange={(e) => onFieldChange('callStartTime', e.target.value)}
            disabled={isReadOnly}
            className="form-input"
            min={effectiveMinDateTimeString}
          />
        </div>
        
        {callMode === 'log' && (
          <div className="form-field">
            <label>Call Duration</label>
            <div className="duration-inputs">
              <input
                type="number"
                min="0"
                placeholder="minutes"
                value={formData.callDurationMinutes || ''}
                onChange={(e) => onFieldChange('callDurationMinutes', e.target.value)}
                disabled={isReadOnly}
                className="form-input"
              />
              <span>minutes</span>
              <input
                type="number"
                min="0"
                max="59"
                placeholder="seconds"
                value={formData.callDurationSeconds || ''}
                onChange={(e) => onFieldChange('callDurationSeconds', e.target.value)}
                disabled={isReadOnly}
                className="form-input"
              />
              <span>seconds</span>
            </div>
          </div>
        )}
        
        <div className="form-field">
          <label>Call Owner</label>
          <select
            value={formData.callOwner || ''}
            onChange={(e) => onFieldChange('callOwner', e.target.value)}
            disabled={isReadOnly}
            className="form-select"
          >
            <option value="">Select Owner</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>{user.name || user.email}</option>
            ))}
          </select>
        </div>
        
        <div className="form-field">
          <label>Subject</label>
          <input
            type="text"
            value={formData.subject || ''}
            onChange={(e) => onFieldChange('subject', e.target.value)}
            disabled={isReadOnly}
            className="form-input"
          />
        </div>
        
        {callMode === 'schedule' && (
          <div className="form-field">
            <label>Reminder</label>
            <select
              value={formData.reminder || 'None'}
              onChange={(e) => onFieldChange('reminder', e.target.value)}
              disabled={isReadOnly}
              className="form-select"
            >
              <option value="None">None</option>
              <option value="5 minutes before">5 minutes before</option>
              <option value="10 minutes before">10 minutes before</option>
              <option value="15 minutes before">15 minutes before</option>
              <option value="30 minutes before">30 minutes before</option>
            </select>
          </div>
        )}
        
        {callMode === 'log' && (
          <div className="form-field">
            <label>Voice Recording</label>
            <input
              type="file"
              accept="audio/*"
              onChange={(e) => onFieldChange('voiceRecording', e.target.files[0])}
              disabled={isReadOnly}
              className="form-file"
            />
          </div>
        )}
        
        <div className="form-field">
          <label>Purpose Of Outgoing Call</label>
          <textarea
            value={formData.purpose || ''}
            onChange={(e) => onFieldChange('purpose', e.target.value)}
            disabled={isReadOnly}
            className="form-textarea"
            rows={3}
          />
        </div>
        
        <div className="form-field">
          <label>Call Purpose</label>
          <select
            value={formData.callPurpose || ''}
            onChange={(e) => onFieldChange('callPurpose', e.target.value)}
            disabled={isReadOnly}
            className="form-select"
          >
            <option value="">-None-</option>
            <option value="Follow-up">Follow-up</option>
            <option value="Sales">Sales</option>
            <option value="Support">Support</option>
            <option value="Information">Information</option>
          </select>
        </div>
        
        <div className="form-field">
          <label>Call Agenda</label>
          <textarea
            value={formData.callAgenda || ''}
            onChange={(e) => onFieldChange('callAgenda', e.target.value)}
            disabled={isReadOnly}
            className="form-textarea"
            rows={3}
          />
        </div>
        
        {/* File Upload Section */}
        <div className="form-field">
          <label>Attachments</label>
          <div className="file-upload-section">
            <div className="file-input-wrapper">
              <input
                type="file"
                name="files"
                multiple
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    const newFiles = Array.from(e.target.files);
                    const existingFiles = attachments || [];
                    onAttachmentChange([...existingFiles, ...newFiles]);
                  }
                  e.target.value = '';
                }}
                disabled={isReadOnly || uploadingFiles}
                className="form-file-input-hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
                id={`call-file-input-${isEditing ? 'edit' : 'new'}`}
              />
              <label htmlFor={`call-file-input-${isEditing ? 'edit' : 'new'}`} className="file-input-label">
                {uploadingFiles ? 'Uploading...' : (attachments && attachments.length > 0 ? 'Add More Files' : 'Choose Files')}
              </label>
            </div>
            {attachments && attachments.length > 0 && (
              <div className="file-list">
                {attachments.map((file, index) => (
                  <div key={index} className="file-item">
                    <FaPaperclip className="file-icon" />
                    <span className="file-name">{file.originalName || file.name || file.filename}</span>
                    <div className="file-actions">
                      {file.url || file.path || file.filename ? (
                        <>
                          <button
                            type="button"
                            onClick={() => onFileView && onFileView(file)}
                            className="view-file-btn"
                            title="Preview File"
                          >
                            <FaEye />
                          </button>
                          <button
                            type="button"
                            onClick={() => onFileDownload && onFileDownload(file)}
                            className="download-file-btn"
                            title="Download File"
                          >
                            <FaDownload />
                          </button>
                        </>
                      ) : null}
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => onRemoveAttachment(index)}
                          className="remove-file-btn"
                          title="Remove file"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-btn">Cancel</button>
        {!isReadOnly && <button type="submit" className="save-btn" disabled={uploadingFiles}>Save</button>}
      </div>
    </form>
  );
};

// Activity Detail View Component
const ActivityDetailView = ({ activity, users, onClose, onEdit, isReadOnly }) => {
  const handleEdit = () => {
    if (onEdit) {
      onEdit(activity); // Pass the activity data to edit
    }
  };
  
  const getOwnerName = (ownerId) => {
    if (!ownerId || !users || users.length === 0) return 'Unassigned';
    const user = users.find(u => u.id === ownerId || u._id === ownerId);
    return user ? (user.name || user.email) : 'Unassigned';
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return dateString;
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };
  
  // Handle file viewing (same logic as DynamicListField)
  const handleFileView = (file) => {
    console.log('handleFileView called with file:', file);
    let fileUrl = null;
    
    // Try different URL sources in order of preference
    if (file.url) {
      fileUrl = file.url;
    } else if (file.path) {
      fileUrl = file.path;
    } else if (file.filename) {
      fileUrl = `/uploads/chat/${file.filename}`;
    }
    
    if (!fileUrl) {
      console.error('No valid file URL found');
      alert('Unable to view file: No valid URL found');
      return;
    }
    
    // Construct full URL if relative
    const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://intranet.nesimltd.co.ke' : 'http://localhost:5000');
    const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${API_BASE}${fileUrl}`;
    
    console.log('Opening file URL:', fullUrl);
    const newWindow = window.open(fullUrl, '_blank');
    if (!newWindow) {
      console.error('Failed to open new window - popup blocked?');
      alert('Unable to open file. Please check if popups are blocked.');
    }
  };
  
  // Handle file download (same logic as DynamicListField)
  const handleFileDownload = async (file) => {
    try {
      await downloadWorkflowFile(file);
    } catch (error) {
      console.error('Unable to download file:', error);
      alert('Unable to download file. Please try again.');
    }
  };
  
  const renderTaskDetails = () => (
    <div className="activity-detail-content">
      <div className="detail-section">
        <h3>Task Details</h3>
        <div className="detail-field">
          <label>Subject:</label>
          <span>{activity.subject || 'N/A'}</span>
        </div>
        <div className="detail-field">
          <label>Due Date:</label>
          <span>{formatDate(activity.dueDate) || 'N/A'}</span>
        </div>
        <div className="detail-field">
          <label>Priority:</label>
          <span>{activity.priority || 'Normal'}</span>
        </div>
        <div className="detail-field">
          <label>Owner:</label>
          <span><FaUser /> {getOwnerName(activity.owner)}</span>
        </div>
        {activity.reminder && (
          <div className="detail-field">
            <label>Reminder:</label>
            <span>{formatDateTime(activity.reminder)}</span>
          </div>
        )}
        {activity.description && (
          <div className="detail-field">
            <label>Description:</label>
            <span>{activity.description}</span>
          </div>
        )}
        {activity.attachments && activity.attachments.length > 0 && (
          <div className="detail-field">
            <label>Attachments:</label>
            <div className="activity-attachments-list">
              {activity.attachments.map((file, idx) => (
                <div key={idx} className="activity-attachment-item">
                  <FaPaperclip className="attachment-icon" />
                  <span className="attachment-name">{file.originalName || file.name || file.filename}</span>
                  <button
                    onClick={() => handleFileView(file)}
                    className="view-file-btn"
                    title="Preview File"
                  >
                    <FaEye />
                  </button>
                  <button
                    onClick={() => handleFileDownload(file)}
                    className="download-file-btn"
                    title="Download File"
                  >
                    <FaDownload />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
  const renderMeetingDetails = () => (
    <div className="activity-detail-content">
      <div className="detail-section">
        <h3>Meeting Information</h3>
        <div className="detail-field">
          <label>Title:</label>
          <span>{activity.title || 'N/A'}</span>
        </div>
        {activity.meetingVenue && (
          <div className="detail-field">
            <label>Meeting Venue:</label>
            <span>{activity.meetingVenue}</span>
          </div>
        )}
        {activity.clientLocation && (
          <div className="detail-field">
            <label>Client Location:</label>
            <span>{activity.clientLocation}</span>
          </div>
        )}
        {activity.location && (
          <div className="detail-field">
            <label>Location:</label>
            <span>{activity.location}</span>
          </div>
        )}
        <div className="detail-field">
          <label>All Day:</label>
          <span>{activity.allDay ? 'Yes' : 'No'}</span>
        </div>
        {activity.from && (
          <div className="detail-field">
            <label>From:</label>
            <span>{formatDateTime(activity.from)}</span>
          </div>
        )}
        {activity.to && (
          <div className="detail-field">
            <label>To:</label>
            <span>{formatDateTime(activity.to)}</span>
          </div>
        )}
        {activity.host && (
          <div className="detail-field">
            <label>Host:</label>
            <span><FaUser /> {getOwnerName(activity.host)}</span>
          </div>
        )}
        {activity.participants && activity.participants.length > 0 && (
          <div className="detail-field">
            <label>Participants:</label>
            <div className="participants-detail">
              {activity.participants.map((p, idx) => (
                <div key={idx} className="participant-detail">
                  {p.name || p.email || 'Unnamed'}
                  {p.email && p.name && ` (${p.email})`}
                </div>
              ))}
            </div>
          </div>
        )}
        {activity.relatedTo && activity.relatedTo !== 'None' && (
          <div className="detail-field">
            <label>Related To:</label>
            <span>{activity.relatedTo}</span>
          </div>
        )}
        {activity.participantsReminder && (
          <div className="detail-field">
            <label>Participants Reminder:</label>
            <span>{formatDateTime(activity.participantsReminder)}</span>
          </div>
        )}
        {activity.description && (
          <div className="detail-field">
            <label>Description:</label>
            <span>{activity.description}</span>
          </div>
        )}
        {activity.attachments && activity.attachments.length > 0 && (
          <div className="detail-field">
            <label>Attachments:</label>
            <div className="activity-attachments-list">
              {activity.attachments.map((file, idx) => (
                <div key={idx} className="activity-attachment-item">
                  <FaPaperclip className="attachment-icon" />
                  <span className="attachment-name">{file.originalName || file.name || file.filename}</span>
                  <button
                    onClick={() => handleFileView(file)}
                    className="view-file-btn"
                    title="Preview File"
                  >
                    <FaEye />
                  </button>
                  <button
                    onClick={() => handleFileDownload(file)}
                    className="download-file-btn"
                    title="Download File"
                  >
                    <FaDownload />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
  const renderCallDetails = () => (
    <div className="activity-detail-content">
      <div className="detail-section">
        <h3>Call Information</h3>
        <div className="detail-field">
          <label>Call For:</label>
          <span>{activity.callFor || 'Lead'}</span>
        </div>
        {activity.relatedTo && (
          <div className="detail-field">
            <label>Related To:</label>
            <span>{activity.relatedTo}</span>
          </div>
        )}
        <div className="detail-field">
          <label>Call Type:</label>
          <span>{activity.callType || 'Outbound'}</span>
        </div>
        <div className="detail-field">
          <label>Outgoing Call Status:</label>
          <span>{activity.callStatus || 'Scheduled'}</span>
        </div>
        {activity.callStartTime && (
          <div className="detail-field">
            <label>Call Start Time:</label>
            <span>{formatDateTime(activity.callStartTime)}</span>
          </div>
        )}
        {activity.callDurationMinutes !== undefined && activity.callDurationSeconds !== undefined && (
          <div className="detail-field">
            <label>Call Duration:</label>
            <span>
              {activity.callDurationMinutes || 0} minutes {activity.callDurationSeconds || 0} seconds
            </span>
          </div>
        )}
        {activity.callOwner && (
          <div className="detail-field">
            <label>Call Owner:</label>
            <span><FaUser /> {getOwnerName(activity.callOwner)}</span>
          </div>
        )}
        {activity.subject && (
          <div className="detail-field">
            <label>Subject:</label>
            <span>{activity.subject}</span>
          </div>
        )}
        {activity.reminder && activity.reminder !== 'None' && (
          <div className="detail-field">
            <label>Reminder:</label>
            <span>{activity.reminder}</span>
          </div>
        )}
        {activity.voiceRecording && (
          <div className="detail-field">
            <label>Voice Recording:</label>
            <span>Recorded</span>
          </div>
        )}
        {activity.purpose && (
          <div className="detail-field">
            <label>Purpose Of Outgoing Call:</label>
            <span>{activity.purpose}</span>
          </div>
        )}
        {activity.callPurpose && (
          <div className="detail-field">
            <label>Call Purpose:</label>
            <span>{activity.callPurpose}</span>
          </div>
        )}
        {activity.callAgenda && (
          <div className="detail-field">
            <label>Call Agenda:</label>
            <span>{activity.callAgenda}</span>
          </div>
        )}
        {activity.attachments && activity.attachments.length > 0 && (
          <div className="detail-field">
            <label>Attachments:</label>
            <div className="activity-attachments-list">
              {activity.attachments.map((file, idx) => (
                <div key={idx} className="activity-attachment-item">
                  <FaPaperclip className="attachment-icon" />
                  <span className="attachment-name">{file.originalName || file.name || file.filename}</span>
                  <button
                    onClick={() => handleFileView(file)}
                    className="view-file-btn"
                    title="Preview File"
                  >
                    <FaEye />
                  </button>
                  <button
                    onClick={() => handleFileDownload(file)}
                    className="download-file-btn"
                    title="Download File"
                  >
                    <FaDownload />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
  const renderDetails = () => {
    switch (activity.type) {
      case 'task': return renderTaskDetails();
      case 'meeting': return renderMeetingDetails();
      case 'call': return renderCallDetails();
      default: return <div>No details available</div>;
    }
  };
  
  return (
    <div className="activity-detail-view">
      <div className="activity-detail-header">
        <h2>
          {activity.type === 'task' ? activity.subject || 'Task' :
           activity.type === 'meeting' ? activity.title || 'Meeting' :
           activity.subject || 'Call'}
        </h2>
        <div className="activity-detail-actions">
          {!isReadOnly && (
            <button className="edit-activity-btn" onClick={handleEdit}>
              <FaEdit /> Edit
            </button>
          )}
          <button className="close-detail-btn" onClick={onClose}>
            <FaArrowLeft /> Back
          </button>
        </div>
      </div>
      {activity.status === 'closed' && activity.closedAt && (
        <div className="activity-closed-banner">
          <FaCheckCircle /> Closed on {formatDateTime(activity.closedAt)}
        </div>
      )}
      {renderDetails()}
    </div>
  );
};

// Workflow Type Selection Modal Component
const WorkflowTypeModal = ({ isOpen, onClose, customer, onSelectWorkflowType, loading }) => {
  const [customerHistory, setCustomerHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (isOpen && customer?.customerId) {
      fetchCustomerHistory();
    }
  }, [isOpen, customer?.customerId]);

  const fetchCustomerHistory = async () => {
    if (!customer?.customerId) return;
    
    setLoadingHistory(true);
    try {
      // Try to fetch customer sales summary
      const response = await get(`/contacts/customers/${customer.customerId}/sales-summary`);
      const data = response.data?.data || response.data;
      
      if (data && data.totalTransactions > 0) {
        setCustomerHistory({
          totalTransactions: data.totalTransactions,
          lastPurchaseDate: data.lastPurchaseDate,
          transactions: data.transactions || []
        });
      } else {
        setCustomerHistory(null);
      }
    } catch (err) {
      console.log('📋 [WORKFLOW] No purchase history found or API not available:', err.message);
      setCustomerHistory(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const getProductNames = () => {
    if (!customerHistory?.transactions || customerHistory.transactions.length === 0) return [];
    const products = new Set();
    customerHistory.transactions.slice(0, 5).forEach(tx => {
      if (tx.itemId?.name) products.add(tx.itemId.name);
    });
    return Array.from(products);
  };

  return (
    <div 
      className="modal-overlay" 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}
      onClick={onClose}
    >
      <div 
        className="modal-content"
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '30px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>
          How would you like to proceed?
        </h2>
        
        {loadingHistory ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>Loading customer history...</p>
          </div>
        ) : customerHistory ? (
          <div style={{ 
            backgroundColor: '#f5f5f5', 
            padding: '15px', 
            borderRadius: '6px', 
            marginBottom: '20px' 
          }}>
            <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#333' }}>
              Customer Purchase History:
            </p>
            <p style={{ margin: '5px 0', color: '#666' }}>
              <strong>Total Purchases:</strong> {customerHistory.totalTransactions}
            </p>
            <p style={{ margin: '5px 0', color: '#666' }}>
              <strong>Last Purchase:</strong> {formatDate(customerHistory.lastPurchaseDate)}
            </p>
            {getProductNames().length > 0 && (
              <p style={{ margin: '5px 0', color: '#666' }}>
                <strong>Recent Products:</strong> {getProductNames().join(', ')}
              </p>
            )}
          </div>
        ) : (
          <div style={{ 
            backgroundColor: '#fff3cd', 
            padding: '15px', 
            borderRadius: '6px', 
            marginBottom: '20px',
            border: '1px solid #ffc107'
          }}>
            <p style={{ margin: 0, color: '#856404' }}>
              No purchase history found for this customer. You can still choose either workflow type.
            </p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={() => onSelectWorkflowType('standard')}
            disabled={loading}
            style={{
              padding: '15px 20px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              textAlign: 'left',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              opacity: loading ? 0.6 : 1
            }}
          >
            <span>
              <strong>New Inquiry</strong>
              <div style={{ fontSize: '14px', fontWeight: 'normal', marginTop: '4px' }}>
                Full process: Visit → Activities → Trials → Quotation
              </div>
            </span>
            <span>→</span>
          </button>

          <button
            onClick={() => onSelectWorkflowType('repeat_purchase')}
            disabled={loading}
            style={{
              padding: '15px 20px',
              backgroundColor: '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '500',
              textAlign: 'left',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              opacity: loading ? 0.6 : 1
            }}
          >
            <span>
              <strong>Repeat Purchase</strong>
              <div style={{ fontSize: '14px', fontWeight: 'normal', marginTop: '4px' }}>
                Skip directly to Customer Inquiry and Quotation
              </div>
            </span>
            <span>→</span>
          </button>
        </div>

        <button
          onClick={onClose}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#f5f5f5',
            color: '#333',
            border: '1px solid #ddd',
            borderRadius: '6px',
            cursor: 'pointer',
            width: '100%'
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const CustomerSelectField = ({ value, onChange, disabled, onCustomerSelected }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('📋 [WORKFLOW] Fetching customers...');
        
        // Use the same API method as CustomersTable
        const response = await get('/contacts/customers', { 
          params: {
            limit: 1000 // Get all customers for dropdown
          }
        });
        
        const data = response.data;
        const customerList = data.data || data.customers || [];
        
        console.log('✅ [WORKFLOW] Customers loaded:', customerList.length);
        setCustomers(customerList);
      } catch (err) {
        console.error('❌ [WORKFLOW] Error fetching customers:', err);
        setError(err.message || 'Failed to load customers');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  const handleCustomerChange = async (e) => {
    const customerId = e.target.value;
    
    console.log('📋 [WORKFLOW] Customer selection changed to:', customerId);
    
    // Always update the field value first
    onChange(customerId);
    
    // If a customer is selected, fetch and populate details
    if (customerId && onCustomerSelected) {
      try {
        console.log('📋 [WORKFLOW] Fetching customer details for:', customerId);
        
        // Fetch full customer details
        const response = await get(`/contacts/customers/${customerId}`);
        const customer = response.data.customer || response.data;
        
        console.log('✅ [WORKFLOW] Customer details loaded:', customer);
        
        // Call the callback to populate the form
        onCustomerSelected(customer);
      } catch (err) {
        console.error('❌ [WORKFLOW] Error fetching customer details:', err);
        alert('Failed to load customer details');
      }
    } else if (!customerId && onCustomerSelected) {
      // If customer selection is cleared (empty option selected), clear the form
      console.log('📋 [WORKFLOW] Customer selection cleared - clearing form fields');
      onCustomerSelected({
        name: '',
        primaryContact: { name: '', phone: '', email: '' },
        roles: {
          buyer: { name: '', phone: '', email: '' },
          owner: { name: '', phone: '', email: '' },
          user: { name: '', phone: '', email: '' }
        }
      });
    }
  };

  return (
    <select
      value={value || ''}
      onChange={handleCustomerChange}
      disabled={disabled || loading}
      className="form-select"
    >
      <option value="">Select Customer</option>
      {loading ? (
        <option disabled>Loading customers...</option>
      ) : error ? (
        <option disabled>Error loading customers</option>
      ) : (
        customers.map(customer => (
          <option key={customer.customerId} value={customer.customerId}>
            {customer.name}
          </option>
        ))
      )}
    </select>
  );
};

// Workflow Step Form Component
const WorkflowStepForm = ({ step, formData, attachments, checklist, comments, onInputChange, onAttachmentChange, onChecklistChange, onCommentsChange, onComplete, onCancel, onPrevious, onSave, isLastStep, users, usersLoading, usersError, isReadOnly, editMode, isCompleted, currentStepIndex, uploadingFiles, onAutoSave, workflowStartDate, workflow, onWorkflowTypeSelected }) => {
  const { minDateString, minDateTimeString } = useMemo(
    () => getWorkflowDateBounds(workflowStartDate),
    [workflowStartDate]
  );
  
  // Track previous customer type to detect changes
  const [prevCustomerType, setPrevCustomerType] = useState(formData['customer_type']);
  const [showWorkflowTypeModal, setShowWorkflowTypeModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectingWorkflowType, setSelectingWorkflowType] = useState(false);
  
  // Clear customer fields when switching to "New Customer"
  useEffect(() => {
    const currentCustomerType = formData['customer_type'];
    
    // If customer type changed from "Existing Customer" to "New Customer", clear all fields
    if (prevCustomerType === 'Existing Customer' && currentCustomerType === 'New Customer') {
      console.log('📋 [WORKFLOW] Switching to New Customer - clearing all fields');
      
      // Clear all customer fields
      onInputChange('existing_customer_id', '');
      onInputChange('customer_name', '');
      onInputChange('contact_name', '');
      onInputChange('contact_phone', '');
      onInputChange('contact_email', '');
      onInputChange('buyer_name', '');
      onInputChange('buyer_phone', '');
      onInputChange('buyer_email', '');
      onInputChange('owner_name', '');
      onInputChange('owner_phone', '');
      onInputChange('owner_email', '');
      onInputChange('user_name', '');
      onInputChange('user_phone', '');
      onInputChange('user_email', '');
      setShowWorkflowTypeModal(false);
      setSelectedCustomer(null);
    }
    
    // Update previous customer type
    setPrevCustomerType(currentCustomerType);
  }, [formData['customer_type'], prevCustomerType, onInputChange]);
  
  // Handle customer selection and populate form fields
  const handleCustomerSelected = (customer) => {
    console.log('📋 [WORKFLOW] Customer selected:', customer);
    
    // Store the selected customer
    setSelectedCustomer(customer);
    
    // Populate all customer fields
    onInputChange('customer_name', customer.name || '');
    onInputChange('contact_name', customer.primaryContact?.name || '');
    onInputChange('contact_phone', customer.primaryContact?.phone?.raw || customer.primaryContact?.phone || '');
    onInputChange('contact_email', customer.primaryContact?.email || '');
    onInputChange('buyer_name', customer.roles?.buyer?.name || '');
    onInputChange('buyer_phone', customer.roles?.buyer?.phone?.raw || customer.roles?.buyer?.phone || '');
    onInputChange('buyer_email', customer.roles?.buyer?.email || '');
    onInputChange('owner_name', customer.roles?.owner?.name || '');
    onInputChange('owner_phone', customer.roles?.owner?.phone?.raw || customer.roles?.owner?.phone || '');
    onInputChange('owner_email', customer.roles?.owner?.email || '');
    onInputChange('user_name', customer.roles?.user?.name || '');
    onInputChange('user_phone', customer.roles?.user?.phone?.raw || customer.roles?.user?.phone || '');
    onInputChange('user_email', customer.roles?.user?.email || '');
    
    // Show workflow type modal for existing customers
    // Only show if workflow type hasn't been set yet and we're on the make_inquiry step
    if (step.id === 'make_inquiry' && !formData['workflow_type']) {
      setShowWorkflowTypeModal(true);
    }
  };
  
  // Handle workflow type selection
  const handleWorkflowTypeSelected = async (workflowType) => {
    console.log('📋 [WORKFLOW] Workflow type selected:', workflowType);
    setSelectingWorkflowType(true);
    
    try {
      // Store workflow type in form data
      onInputChange('workflow_type', workflowType);
      
      // If callback is provided, notify parent component
      if (onWorkflowTypeSelected) {
        await onWorkflowTypeSelected(workflowType);
      }
      
      // Close modal
      setShowWorkflowTypeModal(false);
    } catch (error) {
      console.error('❌ [WORKFLOW] Error setting workflow type:', error);
      alert('Failed to set workflow type. Please try again.');
    } finally {
      setSelectingWorkflowType(false);
    }
  };
  
  // Handle file viewing
  const handleFileView = (file) => {
    console.log('handleFileView called with file:', file);
    console.log('File properties:', {
      url: file.url,
      filename: file.filename,
      name: file.name,
      originalName: file.originalName,
      path: file.path
    });
    
    let fileUrl = null;
    
    // Try different URL sources in order of preference
    if (file.url) {
      fileUrl = file.url;
      console.log('Using file.url:', fileUrl);
    } else if (file.path) {
      fileUrl = file.path;
      console.log('Using file.path:', fileUrl);
    } else if (file.filename) {
      fileUrl = `/uploads/chat/${file.filename}`;
      console.log('Constructed URL from filename:', fileUrl);
    } else if (file.name) {
      fileUrl = `/uploads/chat/${file.name}`;
      console.log('Constructed URL from name:', fileUrl);
    }
    
    if (fileUrl) {
      // Ensure the URL is absolute - use backend URL since files are served from there
      let fullUrl;
      if (fileUrl.startsWith('http')) {
        fullUrl = fileUrl;
      } else {
        // Use backend URL (port 5000) instead of frontend origin (port 3000)
        const backendUrl = 'http://localhost:5000';
        fullUrl = `${backendUrl}${fileUrl}`;
      }
      console.log('Final URL to open:', fullUrl);
      console.log('Current origin:', window.location.origin);
      console.log('Backend URL used:', 'http://localhost:5000');
      
      // Try to open the file
      const newWindow = window.open(fullUrl, '_blank');
      if (!newWindow) {
        console.error('Failed to open new window - popup blocked?');
        alert('Unable to open file. Please check if popups are blocked.');
      } else {
        console.log('Successfully opened new window');
      }
    } else {
      console.error('No file URL or filename available for viewing:', file);
      alert('Unable to open file. File URL not available.');
    }
  };

  // Handle file download
  const handleFileDownload = async (file) => {
    try {
      await downloadWorkflowFile(file);
    } catch (error) {
      console.error('No file URL or filename available for download:', file, error);
      alert('Unable to download file. Please try again.');
    }
  };

  // Handle file removal
  const handleFileRemove = (file, fieldId) => {
    if (window.confirm('Are you sure you want to remove this file?')) {
      // Remove file from attachments
      const updatedAttachments = { ...attachments };
      if (updatedAttachments[fieldId]) {
        updatedAttachments[fieldId] = updatedAttachments[fieldId].filter(f => f !== file);
        if (updatedAttachments[fieldId].length === 0) {
          delete updatedAttachments[fieldId];
        }
      }
      
      // Update the step data
      onAttachmentChange(fieldId, updatedAttachments[fieldId] || []);
    }
  };

  const handleSubmit = () => {
    // Validate required fields using the same logic as the validation display
    const requiredFields = step.formFields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => {
      // For file fields, check attachments object instead of formData
      if (f.type === 'file' || f.type === 'file_upload') {
        return !attachments[f.id] || (Array.isArray(attachments[f.id]) && attachments[f.id].length === 0);
      }
      // For checkbox fields, check if it's checked (truthy)
      if (f.type === 'checkbox') {
        return !formData[f.id];
      }
      // For text, textarea, date, select fields, check formData
      return !formData[f.id] || (typeof formData[f.id] === 'string' && formData[f.id].trim() === '');
    });
    
    if (missingFields.length > 0) {
      alert('Please fill all required fields before proceeding.');
      return;
    }
    
    // Validate checklist if present
    const checklistField = step.formFields.find(f => f.type === 'checklist');
    if (checklistField) {
      const uncheckedItems = checklistField.options.filter(item => !checklist[item]);
      if (uncheckedItems.length > 0) {
        alert('Please fill all required fields before proceeding.');
        return;
      }
    }

    onComplete();
  };

  // Validation logic
  const requiredFields = step.formFields.filter(f => f.required);
  const missingFields = requiredFields.filter(f => {
    // For file fields, check attachments object instead of formData
    if (f.type === 'file') {
      return !attachments[f.id] || (Array.isArray(attachments[f.id]) && attachments[f.id].length === 0);
    }
    // For checkbox fields, check if it's checked (truthy)
    if (f.type === 'checkbox') {
      return !formData[f.id];
    }
    // For text, textarea, date, select fields, check formData
    return !formData[f.id] || (typeof formData[f.id] === 'string' && formData[f.id].trim() === '');
  });

  // Validation: Ensure assignee 1 and assignee 2 are different
  const assigneeValidationError = formData['assigned_user'] && formData['assigned_user_2'] && 
    formData['assigned_user'] === formData['assigned_user_2'] 
    ? 'The second assignee must be different from the first assignee.'
    : null;

  // Checklist validation (if step has a checklist field type)
  let checklistField = step.formFields.find(f => f.type === 'checklist');
  let allChecklistChecked = true;
  if (checklistField && checklistField.options) {
    allChecklistChecked = checklistField.options.every(item => checklist[item]);
  }
  // New button enabled/disabled logic - also check assignee validation
  const canProceed = missingFields.length === 0 && allChecklistChecked && !assigneeValidationError;

  return (
    <div className="workflow-step-form">
      <div className="form-header">
        <h3>{step.name}</h3>
        <p>{step.description}</p>
        {step.guidance && (
          <div className="guidance">
            <FaInfoCircle className="guidance-icon" />
            <span>{step.guidance}</span>
          </div>
        )}
      </div>

      <div className="form-content">
        {/* Show phase transition message for Sales Team when workflow has moved to Finance Team */}
        {isReadOnly && currentStepIndex >= 5 && (
          <div style={{
            backgroundColor: '#fff3e0',
            border: '2px solid #f57c00',
            borderRadius: '8px',
            padding: '16px 20px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <FaExclamationTriangle style={{ fontSize: '24px', color: '#f57c00', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#f57c00', fontSize: '16px', fontWeight: '600' }}>
                Workflow Has Moved to Finance Department
              </h4>
              <p style={{ margin: 0, color: '#666', fontSize: '14px', lineHeight: '1.5' }}>
                You can no longer edit this workflow as it has been moved to the Finance Department. 
                The Finance Team assignee will continue from Step 6 onwards. 
                You can still view your completed steps (Steps 1-5) in read-only mode.
              </p>
            </div>
          </div>
        )}
        
        {(step.formFields || []).map(field => {
          // Check if field should be shown based on showWhen condition
          if (field.showWhen) {
            const dependentFieldValue = formData[field.showWhen.field];
            
            // Support both single value and array of values
            if (field.showWhen.values) {
              // Multiple values - show if dependent field matches any value in the array
              if (!field.showWhen.values.includes(dependentFieldValue)) {
                return null; // Don't render this field
              }
            } else if (field.showWhen.value) {
              // Single value - show only if dependent field matches exactly
              if (dependentFieldValue !== field.showWhen.value) {
                return null; // Don't render this field
              }
            }
          }
          
          return (
          <div key={field.id} className="form-field">
            <label className="field-label">
              {field.label}
              {field.required && <span className="required">*</span>}
            </label>
            
            {field.type === 'text' && (
              <input
                type="text"
                value={formData[field.id] || ''}
                onChange={(e) => onInputChange(field.id, e.target.value)}
                readOnly={isReadOnly}
                disabled={isReadOnly}
                className="form-input"
              />
            )}
            
            {field.type === 'textarea' && (
              <textarea
                value={formData[field.id] || ''}
                onChange={(e) => onInputChange(field.id, e.target.value)}
                readOnly={isReadOnly}
                disabled={isReadOnly}
                className="form-textarea"
                rows={4}
              />
            )}
            
            {field.type === 'number' && (
              <input
                type="number"
                value={formData[field.id] || ''}
                onChange={(e) => onInputChange(field.id, e.target.value)}
                readOnly={isReadOnly}
                disabled={isReadOnly}
                className="form-input"
              />
            )}
            
            {field.type === 'date' && (
              <input
                type="date"
                value={formData[field.id] || ''}
                onChange={(e) => onInputChange(field.id, e.target.value)}
                readOnly={isReadOnly}
                disabled={isReadOnly}
                className="form-input"
                min={minDateString}
              />
            )}
            
            {field.type === 'select' && (
              <select
                value={formData[field.id] || ''}
                onChange={(e) => onInputChange(field.id, e.target.value)}
                disabled={isReadOnly}
                className="form-select"
              >
                <option value="">Select {field.label}</option>
                {field.options.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            )}
            
            {field.type === 'user_select' && (
              <select
                value={formData[field.id] || ''}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  
                  // Validation: Prevent selecting the same user for both assignees
                  if (field.id === 'assigned_user_2' && selectedValue && selectedValue === formData['assigned_user']) {
                    alert('The second assignee must be different from the first assignee.');
                    return; // Don't update if same user selected
                  }
                  
                  // If first assignee is changed and it matches second assignee, clear second assignee
                  if (field.id === 'assigned_user' && selectedValue && selectedValue === formData['assigned_user_2']) {
                    onInputChange('assigned_user_2', ''); // Clear second assignee
                  }
                  
                  onInputChange(field.id, selectedValue);
                }}
                disabled={isReadOnly}
                className="form-select"
              >
                <option value="">Select User</option>
                {usersLoading ? (
                  <option disabled>Loading users...</option>
                ) : usersError ? (
                  <option disabled>Error loading users</option>
                ) : (
                  users
                    .filter(userItem => {
                      // For second assignee field, filter out the first assignee
                      if (field.id === 'assigned_user_2' && formData['assigned_user']) {
                        return userItem.username !== formData['assigned_user'];
                      }
                      return true; // Show all users for first assignee
                    })
                    .map(userItem => (
                      <option key={userItem._id} value={userItem.username}>
                        {userItem.name}
                      </option>
                    ))
                )}
              </select>
            )}
            
            {field.type === 'email' && (
              <input
                type="email"
                value={formData[field.id] || ''}
                onChange={(e) => onInputChange(field.id, e.target.value)}
                readOnly={isReadOnly}
                disabled={isReadOnly}
                className="form-input"
                placeholder="Enter email address"
              />
            )}
            
            {field.type === 'customer_select' && (
              <CustomerSelectField
                value={formData[field.id] || ''}
                onChange={(value) => onInputChange(field.id, value)}
                disabled={isReadOnly}
                onCustomerSelected={handleCustomerSelected}
              />
            )}
            
            {field.type === 'checkbox' && (
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData[field.id] || false}
                  onChange={(e) => onInputChange(field.id, e.target.checked)}
                  disabled={isReadOnly}
                  className="form-checkbox"
                />
                <span className="checkbox-text">{field.label}</span>
              </label>
            )}
            
            {field.type === 'file' && (
              <div className="file-upload">
                <div className="file-input-wrapper">
                  <input
                    type="file"
                    name="files"
                    key={`${step.id}-${field.id}-${currentStepIndex}`} // Force re-render when step changes
                    onChange={(e) => onAttachmentChange(field.id, Array.from(e.target.files))}
                    disabled={isReadOnly}
                    className="form-file"
                    multiple
                    value="" // Explicitly control the value
                    id={`file-input-${step.id}-${field.id}`}
                  />
                  <label htmlFor={`file-input-${step.id}-${field.id}`} className="file-input-label">
                    {uploadingFiles ? "Uploading..." : 
                     attachments[field.id] && attachments[field.id].length > 0 
                      ? "Upload another file" 
                      : "Choose Files"
                    }
                  </label>
                </div>
                {attachments[field.id] && attachments[field.id].length > 0 ? (
                  <div className="uploaded-files">
                    <h4>Uploaded Files:</h4>
                    {attachments[field.id].map((file, index) => (
                      <div key={index} className="uploaded-file">
                        <FaPaperclipIcon className="file-icon" />
                        <span 
                          className="file-name clickable"
                          onClick={() => handleFileView(file)}
                          title="Click to view/download"
                        >
                          {file.name || file.originalName}
                        </span>
                        <div className="file-actions">
                          <button
                            className="file-action-btn view-btn"
                            onClick={() => handleFileView(file)}
                            title="View file"
                          >
                            <FaEye />
                          </button>
                          <button
                            className="file-action-btn download-btn"
                            onClick={() => handleFileDownload(file)}
                            title="Download file"
                          >
                            <FaDownload />
                          </button>
                          {!isReadOnly && (
                            <button
                              className="file-action-btn remove-btn"
                              onClick={() => handleFileRemove(file, field.id)}
                              title="Remove file"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="file-upload-prompt">
                    <p>No files uploaded yet. Please select files to upload.</p>
                  </div>
                )}
              </div>
            )}
            
            {field.type === 'checklist' && (
              <div className="checklist">
                {field.options.map(item => (
                  <label key={item} className="checklist-item">
                    <input
                      type="checkbox"
                      checked={checklist[item] || false}
                      onChange={(e) => onChecklistChange(item, e.target.checked)}
                      disabled={isReadOnly}
                      className="checklist-checkbox"
                    />
                    <span className="checklist-text">{item}</span>
                  </label>
                ))}
              </div>
            )}
            
            {field.type === 'comments' && (
              <textarea
                value={comments || ''}
                onChange={(e) => onCommentsChange(e.target.value)}
                readOnly={isReadOnly}
                disabled={isReadOnly}
                className="form-textarea"
                rows={3}
                placeholder="Add comments or notes..."
              />
            )}
            
            {field.type === 'dynamic_list' && (
              <DynamicListField
                field={field}
                formData={formData}
                attachments={attachments}
                onInputChange={onInputChange}
                onAttachmentChange={onAttachmentChange}
                isReadOnly={isReadOnly}
                currentStepId={step.id}
                minDateString={minDateString}
              />
            )}
            
            {field.type === 'activities_manager' && (
              <ActivitiesManager
                formData={formData}
                onInputChange={onInputChange}
                isReadOnly={isReadOnly}
                users={users}
                onAutoSave={onAutoSave}
                minDateString={minDateString}
                minDateTimeString={minDateTimeString}
              />
            )}
            
            {field.type === 'trials_manager' && (
              <TrialsManager
                formData={formData}
                onInputChange={onInputChange}
                isReadOnly={isReadOnly}
                users={users}
                onAutoSave={onAutoSave}
                minDateString={minDateString}
              />
            )}
          </div>
          );
        })}
      </div>

      {/* Show validation error for duplicate assignees */}
      {assigneeValidationError && (
        <div className="form-error" style={{ 
          padding: '12px', 
          marginBottom: '16px', 
          backgroundColor: '#fee2e2', 
          border: '1px solid #fca5a5', 
          borderRadius: '8px', 
          color: '#dc2626' 
        }}>
          <strong>Validation Error:</strong> {assigneeValidationError}
        </div>
      )}

      <div className="form-actions">
        {onPrevious && (
          <button onClick={onPrevious} className="prev-btn">
            <FaArrowLeft className="btn-icon" />
            Previous Step
          </button>
        )}
        {!isCompleted && (
          <button
            onClick={onSave || handleSubmit}
            className="save-btn"
            disabled={!canProceed} // block unless valid
          >
            <FaSave className="btn-icon" />
            {isLastStep ? 'Complete Workflow' : 'Save & Continue'}
          </button>
        )}
        {isCompleted && editMode && (
          <button onClick={handleSubmit} className="save-btn">
            <FaSave className="btn-icon" />
            Save Changes
          </button>
        )}
        {onCancel && (
          <button onClick={onCancel} className="cancel-btn">
            Cancel
          </button>
        )}
      </div>
      
      {/* Workflow Type Selection Modal */}
      {showWorkflowTypeModal && selectedCustomer && (
        <WorkflowTypeModal
          isOpen={showWorkflowTypeModal}
          onClose={() => setShowWorkflowTypeModal(false)}
          customer={selectedCustomer}
          onSelectWorkflowType={handleWorkflowTypeSelected}
          loading={selectingWorkflowType}
        />
      )}
    </div>
  );
};

// Workflow Execution Window Component
const WorkflowExecutionWindow = forwardRef(({ workflow, onClose, onComplete, users, usersLoading, usersError, onSave, onEdit }, ref) => {
  const steps = Array.isArray(workflow?.steps) ? workflow.steps : [];
  const hasClosedRef = useRef(false);
  
  // If workflow is invalid, close immediately without showing error
  useEffect(() => {
    if ((!workflow || steps.length === 0) && !hasClosedRef.current) {
      hasClosedRef.current = true;
      onClose();
    }
  }, [workflow, steps.length, onClose]);
  
  if (!workflow || steps.length === 0) {
    return null; // Don't render anything, just close
  }
  const { user } = useContext(AuthContext);
  
  // Get workflow definition for phase metadata
  const workflowDef = workflowDefinitions[workflow.id] || workflowDefinitions[workflow.workflowId];
  const currentPhase = workflow.workflowPhase || 'Sales Team';
  const userRoles = user?.roles || [];
  const isAdmin = user?.role === 'admin' || user?.role === 'super';
  const isSales = userRoles.includes('SALES_TEAM');
  const isFinance = userRoles.includes('FINANCE_TEAM');
  const isStandardUser = user?.role === 'standard' || user?.role === 'user';
  
  // Standard users cannot edit workflows - force read-only mode
  const isReadOnlyForStandard = isStandardUser && !isAdmin;
  
  // Helper to check if user can edit a step
  const canEditStep = (stepOrder) => {
    if (isAdmin) return true;
    if (workflow.id !== 'technical_sales' && workflow.workflowId !== 'technical_sales') {
      return true; // Use default permissions for other workflows
    }
    
    if (currentPhase === 'Sales Team') {
      return isSales && stepOrder >= 1 && stepOrder <= 5;
    }
    if (currentPhase === 'Finance Team') {
      return isFinance && stepOrder >= 6 && stepOrder <= 12;
    }
    return false;
  };
  
  // Initialize step index with immediate phase check for Sales Team
  const getInitialStepIndex = () => {
    let initialIndex = Number.isFinite(workflow.currentStepIndex) ? workflow.currentStepIndex : 0;
    
    // Immediate check: If Sales Team member and phase is Finance Team, ensure they're on Step 5 or earlier
    if ((workflow.id === 'technical_sales' || workflow.workflowId === 'technical_sales') && 
        currentPhase === 'Finance Team' && !isAdmin && !isFinance) {
      const stepOrder = initialIndex + 1;
      if (stepOrder >= 6) {
        // Find Step 5 index
        const step5Index = steps.findIndex((s, idx) => {
          const stepDef = workflowDef?.steps?.find(sd => sd.id === s.id);
          return stepDef?.phase === 'Sales Team' && idx === 4; // Step 5 is at index 4
        });
        initialIndex = step5Index >= 0 ? step5Index : 4; // Fallback to index 4
        console.log('🚫 [INIT] Sales Team member - forcing Step 5, was on Step', stepOrder);
      }
    }
    
    return initialIndex;
  };
  
  const [currentStepIndex, setCurrentStepIndex] = useState(getInitialStepIndex);
  const [stepData, setStepData] = useState(() => {
    console.log('🏁 [INIT] WorkflowExecutionWindow initializing with stepData:', {
      hasStepData: !!workflow.stepData,
      stepDataKeys: workflow.stepData ? Object.keys(workflow.stepData) : [],
      stepData: workflow.stepData,
      currentStepIndex: workflow.currentStepIndex,
      isResuming: workflow.isResuming
    });
    return workflow.stepData || {};
  });
  
  const [completedSteps, setCompletedSteps] = useState(() => {
    return new Set(workflow.completedSteps || []);
  });
  
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const redirectRef = useRef(false); // Track redirect state to prevent loops
  
  // Update state when resuming a workflow (workflow prop changes with saved data)
  useEffect(() => {
    console.log('🔄 [RESUME] WorkflowExecutionWindow useEffect triggered:', {
      isResuming: workflow.isResuming,
      hasStepData: !!workflow.stepData,
      stepDataKeys: workflow.stepData ? Object.keys(workflow.stepData) : [],
      currentStepIndex: workflow.currentStepIndex,
      currentStepId: steps[workflow.currentStepIndex || 0]?.id
    });
    
    // Always update if workflow data is provided (for both initial mount and updates)
    if (workflow.stepData) {
      console.log('✅ [RESUME] Updating state from workflow data:', {
        stepDataStructure: Object.keys(workflow.stepData).map(key => ({
          stepId: key,
          hasFormData: !!(workflow.stepData[key]?.formData),
          formDataKeys: workflow.stepData[key]?.formData ? Object.keys(workflow.stepData[key].formData) : []
        })),
        currentStepIndex: workflow.currentStepIndex || 0,
        currentStepId: steps[workflow.currentStepIndex || 0]?.id
      });
      
      let targetStepIndex = workflow.currentStepIndex !== undefined ? workflow.currentStepIndex : 0;
      
      // For Technical Sales workflows, check if Sales Team member is trying to access Finance Team steps
      if ((workflow.id === 'technical_sales' || workflow.workflowId === 'technical_sales') && 
          currentPhase === 'Finance Team' && !isAdmin) {
        const stepOrder = targetStepIndex + 1;
        if (stepOrder >= 6 && !canEditStep(stepOrder)) {
          // Sales Team member trying to access Finance Team step - redirect to Step 5
          console.log('⚠️ [PHASE_RESTRICTION] Sales Team member cannot access Step', stepOrder, '- redirecting to Step 5');
          // Find Step 5 (last Sales Team step)
          const step5Index = steps.findIndex((s, idx) => {
            const stepDef = workflowDef?.steps?.find(sd => sd.id === s.id);
            return stepDef?.phase === 'Sales Team' && idx === 4; // Step 5 is at index 4
          });
          targetStepIndex = step5Index >= 0 ? step5Index : 4; // Fallback to index 4
        }
      }
      
      setCurrentStepIndex(targetStepIndex);
      // Deep clone to ensure React detects the change
      setStepData(JSON.parse(JSON.stringify(workflow.stepData || {})));
      setCompletedSteps(new Set(workflow.completedSteps || []));
    }
  }, [workflow.isResuming, workflow.currentStepIndex, workflow.instanceId, workflow.stepData, workflow.completedSteps, steps, currentPhase, isAdmin]);
  
  // Monitor current step and IMMEDIATELY redirect if Sales Team tries to access Finance Team steps
  // This runs on every render to catch any attempts to access Step 6+
  useEffect(() => {
    if ((workflow.id === 'technical_sales' || workflow.workflowId === 'technical_sales') && 
        currentPhase === 'Finance Team' && !isAdmin && !isFinance) {
      const stepOrder = currentStepIndex + 1;
      if (stepOrder >= 6) {
        // Sales Team member on Finance Team step - IMMEDIATELY redirect to Step 5
        console.log('🚫 [PHASE_RESTRICTION] Sales Team member detected on Step', stepOrder, '- IMMEDIATELY redirecting to Step 5');
        // Find Step 5 index (last Sales Team step)
        const step5Index = steps.findIndex((s, idx) => {
          const stepDef = workflowDef?.steps?.find(sd => sd.id === s.id);
          return stepDef?.phase === 'Sales Team' && idx === 4; // Step 5 is at index 4
        });
        const targetIndex = step5Index >= 0 ? step5Index : 4; // Fallback to index 4
        
        // Force immediate redirect
        if (currentStepIndex !== targetIndex) {
          setCurrentStepIndex(targetIndex);
        }
      }
    }
  }, [currentStepIndex, currentPhase, workflow.id, workflow.workflowId, isAdmin, isFinance, steps, workflowDef]);
  
  // CRITICAL: Prevent rendering Step 6+ content for Sales Team - runs BEFORE any rendering
  // This is the most important check - it must happen before currentStep is used
  const currentStepOrder = currentStepIndex + 1;
  const isSalesTeamOnFinanceStep = (workflow.id === 'technical_sales' || workflow.workflowId === 'technical_sales') && 
                                    currentPhase === 'Finance Team' && 
                                    isSales && 
                                    !isAdmin && 
                                    !isFinance && 
                                    currentStepOrder >= 6;
  
  // If Sales Team is on blocked step, IMMEDIATELY redirect and block rendering
  if (isSalesTeamOnFinanceStep) {
    // Find Step 5 (last Sales Team step)
    const step5Index = steps.findIndex((s, idx) => {
      const stepDef = workflowDef?.steps?.find(sd => sd.id === s.id);
      return stepDef?.phase === 'Sales Team' && idx === 4; // Step 5 is at index 4
    });
    const targetIndex = step5Index >= 0 ? step5Index : 4;
    
    // Force immediate redirect
    if (!redirectRef.current && currentStepIndex !== targetIndex) {
      redirectRef.current = true;
      // Use requestAnimationFrame for immediate update
      requestAnimationFrame(() => {
        setCurrentStepIndex(targetIndex);
        redirectRef.current = false;
      });
    }
    
    // Block rendering completely - show message instead
    return (
      <div className="workflow-execution-window" style={{ 
        padding: '60px 40px', 
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          backgroundColor: '#fff3e0',
          border: '2px solid #f57c00',
          borderRadius: '8px',
          padding: '30px',
          maxWidth: '500px'
        }}>
          <h3 style={{ color: '#f57c00', marginBottom: '16px' }}>⚠️ Workflow Moved to Finance Department</h3>
          <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
            You have completed your assigned steps (Steps 1-5). The workflow has been moved to the Finance Department.
          </p>
          <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '20px' }}>
            <strong>You can no longer edit this workflow.</strong> The Finance Team assignee will continue from Step 6 onwards.
          </p>
          <button 
            onClick={() => {
              const step5Index = steps.findIndex((s, idx) => {
                const stepDef = workflowDef?.steps?.find(sd => sd.id === s.id);
                return stepDef?.phase === 'Sales Team' && idx === 4;
              });
              setCurrentStepIndex(step5Index >= 0 ? step5Index : 4);
            }}
            style={{
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            View My Completed Steps (1-5)
          </button>
        </div>
      </div>
    );
  }
  
  // Expose live progress state via ref
  useImperativeHandle(ref, () => ({
    getCurrentProgress: () => ({
      stepData,
      completedSteps: Array.from(completedSteps),
      currentStepIndex,
      currentStep: steps[currentStepIndex]?.id || undefined
    })
  }), [stepData, completedSteps, currentStepIndex, steps]);
  
  // Clear file inputs when step changes
  useEffect(() => {
    // Clear any file inputs when the step changes
    const fileInputs = document.querySelectorAll('.form-file');
    fileInputs.forEach(input => {
      if (input.value) {
        input.value = '';
      }
    });
    
    // Also clear any file inputs that might be in the current step
    setTimeout(() => {
      const currentFileInputs = document.querySelectorAll('.form-file');
      currentFileInputs.forEach(input => {
        if (input.value) {
          input.value = '';
        }
      });
    }, 100);
  }, [currentStepIndex]);
  
  // Get current step data
  const currentStep = steps[currentStepIndex];
  const currentStepData = stepData[currentStep?.id] || {};
  const formData = currentStepData.formData || {};
  // Only show attachments for the current step, not previous steps
  const attachments = currentStepData.attachments || {};
  const checklist = currentStepData.checklist || {};
  const comments = currentStepData.comments || '';
  
  // Debug logging for formData loading
  useEffect(() => {
    if (currentStep?.id) {
      console.log('📝 [FORM_DATA] Current step form data:', {
        stepId: currentStep.id,
        stepName: currentStep.name,
        currentStepIndex,
        hasStepData: !!stepData[currentStep.id],
        hasFormData: !!formData,
        formDataKeys: Object.keys(formData),
        formData: formData,
        fullStepData: stepData[currentStep.id]
      });
    }
  }, [currentStep?.id, currentStepIndex, stepData, formData]);
  
  const isLastStep = currentStepIndex >= steps.length - 1;

  const handleInputChange = (fieldId, value) => {
    setStepData(prev => {
      const updated = {
        ...prev,
        [currentStep.id]: {
          ...prev[currentStep.id],
          formData: {
            ...prev[currentStep.id]?.formData,
            [fieldId]: value
          }
        }
      };
      
      // Debug log for trials specifically
      if (fieldId === 'trials') {
        console.log('📝 [HANDLE_INPUT_CHANGE] Trials updated:', {
          stepId: currentStep.id,
          fieldId: fieldId,
          trialsCount: Array.isArray(value) ? value.length : 0,
          trialsData: value,
          updatedStepData: updated[currentStep.id]
        });
      }
      
      return updated;
    });
  };

  const handleAttachmentChange = async (fieldId, files) => {
    if (!currentStep?.id) return;

    const filesArray = Array.isArray(files) ? files : [];
    const currentAttachments =
      stepData[currentStep.id]?.attachments?.[fieldId] || [];

    if (filesArray.length === 0) {
      setStepData(prev => ({
        ...prev,
        [currentStep.id]: {
          ...prev[currentStep.id],
          attachments: {
            ...prev[currentStep.id]?.attachments,
            [fieldId]: []
          }
        }
      }));
      return;
    }

    const isPersistedFile = (file) =>
      !(file instanceof File) && (file?.url || file?.id || file?.filename);

    const hasPersistedFiles = filesArray.some(isPersistedFile);

    const newFiles = filesArray.filter(file => file instanceof File);
    const existingFiles = filesArray.filter(isPersistedFile);

    if (newFiles.length === 0) {
      setStepData(prev => ({
        ...prev,
        [currentStep.id]: {
          ...prev[currentStep.id],
          attachments: {
            ...prev[currentStep.id]?.attachments,
            [fieldId]: filesArray
          }
        }
      }));
      return;
    }

    try {
      setUploadingFiles(true);
      const uploadedFiles = await uploadFilesToBackend(newFiles);

      const remainingCurrent = currentAttachments.filter(f =>
        !existingFiles.some(ef => (ef.id || ef.filename) === (f.id || f.filename))
      );
      const mergedFiles = [...remainingCurrent, ...existingFiles, ...uploadedFiles];

      setStepData(prev => ({
        ...prev,
        [currentStep.id]: {
          ...prev[currentStep.id],
          attachments: {
            ...prev[currentStep.id]?.attachments,
            [fieldId]: mergedFiles
          }
        }
      }));
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  };

  // Upload files to backend with environment-aware API URL
  const uploadFilesToBackend = async (files) => {
    console.log('📎 [WORKFLOW] uploadFilesToBackend called with files:', files);
    try {
      const mappedFiles = await uploadFilesWithAutoChunking({
        files,
        context: 'workflows'
      });
      console.log('📎 [WORKFLOW] All mapped files:', mappedFiles);
      return mappedFiles;
    } catch (error) {
      console.error('❌ [WORKFLOW] Upload error:', error);
      throw new Error(`File upload failed: ${error.message}`);
    }
  };

  const handleChecklistChange = (item, checked) => {
    setStepData(prev => ({
      ...prev,
      [currentStep.id]: {
        ...prev[currentStep.id],
        checklist: {
          ...prev[currentStep.id]?.checklist,
          [item]: checked
        }
      }
    }));
  };

  const handleCommentsChange = (value) => {
    setStepData(prev => ({
      ...prev,
      [currentStep.id]: {
        ...prev[currentStep.id],
        comments: value
      }
    }));
  };

  // Auto-save activities changes immediately to backend
  const handleAutoSaveActivities = async (updatedActivities) => {
    if (!workflow.instanceId || !workflow.id) {
      console.warn('⚠️ [AUTO_SAVE] Cannot auto-save activities: workflow instanceId or id missing');
      return;
    }

    try {
      // Update stepData with the new activities
      const updatedStepData = {
        ...stepData,
        [currentStep.id]: {
          ...stepData[currentStep.id],
          formData: {
            ...stepData[currentStep.id]?.formData,
            activities: updatedActivities
          }
        }
      };

      // Create workflow instance for save
      // Preserve completed status if workflow was completed
      const preserveStatus = workflow.isCompleted ? 'completed' : 'in_progress';
      const workflowInstance = {
        id: workflow.instanceId,
        workflowId: workflow.id,
        workflowName: workflow.name,
        currentStep: currentStep.id,
        currentStepIndex: currentStepIndex,
        status: preserveStatus,
        startedBy: user.username,
        startedAt: workflow.startedAt || new Date().toISOString(),
        stepData: updatedStepData,
        completedSteps: Array.from(completedSteps)
      };

      console.log('💾 [AUTO_SAVE] Auto-saving activities change:', {
        instanceId: workflow.instanceId,
        stepId: currentStep.id,
        activitiesCount: {
          open: updatedActivities.open?.length || 0,
          closed: updatedActivities.closed?.length || 0
        }
      });

      // Update local stepData immediately
      setStepData(updatedStepData);

      // Save to backend (without showing alert)
      if (onSave) {
        await onSave(workflowInstance, false);
      }
    } catch (error) {
      console.error('❌ [AUTO_SAVE] Error auto-saving activities:', error);
      // Don't show alert for auto-save failures, just log
    }
  };

  // Auto-save trials changes immediately to backend
  const handleAutoSaveTrials = async (updatedTrials) => {
    if (!workflow.instanceId || !workflow.id) {
      console.warn('⚠️ [AUTO_SAVE] Cannot auto-save trials: workflow instanceId or id missing');
      return;
    }

    try {
      // Use functional update to get the latest stepData and update it atomically
      let updatedStepData;
      setStepData(prev => {
        updatedStepData = {
          ...prev,
          [currentStep.id]: {
            ...prev[currentStep.id],
            formData: {
              ...prev[currentStep.id]?.formData,
              trials: updatedTrials
            }
          }
        };
        return updatedStepData;
      });
      
      // Ensure we have the updated stepData
      if (!updatedStepData) {
        updatedStepData = {
          ...stepData,
          [currentStep.id]: {
            ...stepData[currentStep.id],
            formData: {
              ...stepData[currentStep.id]?.formData,
              trials: updatedTrials
            }
          }
        };
      }

      // Create workflow instance for save
      // Preserve completed status if workflow was completed
      const preserveStatus = workflow.isCompleted ? 'completed' : 'in_progress';
      const workflowInstance = {
        id: workflow.instanceId,
        workflowId: workflow.id,
        workflowName: workflow.name,
        currentStep: currentStep.id,
        currentStepIndex: currentStepIndex,
        status: preserveStatus,
        startedBy: user.username,
        startedAt: workflow.startedAt || new Date().toISOString(),
        stepData: updatedStepData,
        completedSteps: Array.from(completedSteps)
      };

      console.log('💾 [AUTO_SAVE] Auto-saving trials change:', {
        instanceId: workflow.instanceId,
        stepId: currentStep.id,
        trialsCount: updatedTrials?.length || 0,
        updatedTrials: updatedTrials,
        updatedStepData: updatedStepData,
        trialsInStepData: updatedStepData[currentStep.id]?.formData?.trials
      });

      // Update local stepData immediately
      setStepData(updatedStepData);

      // Save to backend (without showing alert)
      if (onSave) {
        await onSave(workflowInstance, false);
      }
    } catch (error) {
      console.error('❌ [AUTO_SAVE] Error auto-saving trials:', error);
      // Don't show alert for auto-save failures, just log
    }
  };

  const handlePreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleSave = async () => {
    // Use stepData directly - React state updates are synchronous, so this should have the latest value
    // However, we'll use a functional update pattern to ensure we get the absolute latest state
    const latestStepData = stepData;
    const currentFormData = latestStepData[currentStep.id]?.formData || {};
    const currentAttachments = latestStepData[currentStep.id]?.attachments || {};
    const currentChecklist = latestStepData[currentStep.id]?.checklist || {};
    
    // Debug log to verify trials data is present
    console.log('💾 [HANDLE_SAVE] Current step data:', {
      stepId: currentStep.id,
      formDataKeys: Object.keys(currentFormData),
      hasTrials: 'trials' in currentFormData,
      trialsData: currentFormData.trials,
      trialsCount: currentFormData.trials?.length || 0,
      fullStepData: latestStepData[currentStep.id],
      allStepDataKeys: Object.keys(latestStepData),
      stepDataSnapshot: JSON.stringify(latestStepData[currentStep.id], null, 2)
    });
    
    // Validate required fields before proceeding
    const requiredFields = currentStep.formFields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => {
      // For file fields, check attachments object instead of formData
      if (f.type === 'file' || f.type === 'file_upload') {
        return !currentAttachments[f.id] || (Array.isArray(currentAttachments[f.id]) && currentAttachments[f.id].length === 0);
      }
      // For checkbox fields, check if it's checked (truthy)
      if (f.type === 'checkbox') {
        return !currentFormData[f.id];
      }
      // For text, textarea, date, select fields, check formData
      return !currentFormData[f.id] || (typeof currentFormData[f.id] === 'string' && currentFormData[f.id].trim() === '');
    });
    
    if (missingFields.length > 0) {
      alert('Please fill all required fields before proceeding.');
      return;
    }
    
    // Validate checklist if present
    const checklistField = currentStep.formFields.find(f => f.type === 'checklist');
    if (checklistField) {
      const uncheckedItems = checklistField.options.filter(item => !currentChecklist[item]);
      if (uncheckedItems.length > 0) {
        alert('Please fill all required fields before proceeding.');
        return;
      }
    }
    
    // Check if this is a repeat purchase workflow BEFORE saving
    // If so, prepare to skip steps 2-4 (plan_visit, activities, trials) and go directly to step 5 (customer_inquiry_quotation)
    let shouldSkipToQuotation = false;
    if (workflow.id === 'technical_sales' || workflow.workflowId === 'technical_sales') {
      // Check workflow_type from multiple sources to ensure we catch it
      const workflowType = currentFormData.workflow_type || 
                           latestStepData[currentStep.id]?.formData?.workflow_type ||
                           latestStepData['make_inquiry']?.formData?.workflow_type;
      const isMakeInquiryStep = currentStep.id === 'make_inquiry';
      
      console.log('🔍 [HANDLE_SAVE] Checking workflow type:', {
        workflowType,
        isMakeInquiryStep,
        currentFormData_workflow_type: currentFormData.workflow_type,
        stepData_workflow_type: latestStepData[currentStep.id]?.formData?.workflow_type,
        makeInquiry_workflow_type: latestStepData['make_inquiry']?.formData?.workflow_type,
        currentStepId: currentStep.id
      });
      
      if (isMakeInquiryStep && workflowType === 'repeat_purchase') {
        shouldSkipToQuotation = true;
        console.log('🔄 [HANDLE_SAVE] Repeat purchase detected - will skip steps 2-4, going directly to Customer Inquiry and Quotation');
        
        // Mark steps 2-4 as skipped in stepData BEFORE saving
        const skippedSteps = ['plan_visit', 'activities', 'trials'];
        skippedSteps.forEach(stepId => {
          if (!latestStepData[stepId]) {
            latestStepData[stepId] = {
              formData: {},
              skipped: true,
              skipReason: 'Repeat purchase - skipped to quotation'
            };
          }
        });
        
        // Ensure workflow_type is stored in the make_inquiry stepData
        if (!latestStepData[currentStep.id]) {
          latestStepData[currentStep.id] = { formData: {} };
        }
        if (!latestStepData[currentStep.id].formData) {
          latestStepData[currentStep.id].formData = {};
        }
        latestStepData[currentStep.id].formData.workflow_type = 'repeat_purchase';
      }
    }
    
    // Handle new customer creation for technical sales workflow step 1
    if (workflow.id === 'technical_sales' && currentStep.id === 'make_inquiry' && currentFormData.customer_type === 'New Customer') {
      try {
        // Prepare customer data
        const customerData = {
          name: currentFormData.customer_name,
          primaryContact: {
            name: currentFormData.contact_name,
            phone: currentFormData.contact_phone,
            email: currentFormData.contact_email
          },
          roles: {
            buyer: {
              name: currentFormData.buyer_name,
              phone: currentFormData.buyer_phone,
              email: currentFormData.buyer_email
            },
            owner: {
              name: currentFormData.owner_name,
              phone: currentFormData.owner_phone,
              email: currentFormData.owner_email
            },
            user: {
              name: currentFormData.user_name,
              phone: currentFormData.user_phone,
              email: currentFormData.user_email
            }
          }
        };

        // Validate that at least customer name is provided
        if (!customerData.name) {
          alert('Please provide Customer Name before continuing.');
          return;
        }

        // Save customer to database
        console.log('📋 [WORKFLOW] Creating customer...');
        console.log('📋 [WORKFLOW] Customer data:', customerData);
        
        // Use the same API method as CustomersForm
        const response = await post('/contacts/customers', customerData);
        const result = response.data;
        
        console.log('✅ [WORKFLOW] Customer created successfully:', result);
        
        // Store the created customer ID in the form data
        setStepData(prev => ({
          ...prev,
          [currentStep.id]: {
            ...prev[currentStep.id],
            formData: {
              ...currentFormData,
              created_customer_id: result.customer?.customerId
            }
          }
        }));
        
        alert('Customer created successfully!');
      } catch (error) {
        console.error('Error creating customer:', error);
        alert(`Failed to create customer: ${error.message}`);
        return; // Don't proceed if customer creation fails
      }
    }
    
    const newCompletedSteps = new Set([...completedSteps, currentStep.id]);
    setCompletedSteps(newCompletedSteps);

    // Create workflow instance data
    // Preserve completed status if workflow was completed (in edit mode)
    const preserveStatus = workflow.isCompleted ? 'completed' : 'in_progress';
    const workflowInstance = {
      id: workflow.instanceId, // Always use the existing instanceId
      workflowId: workflow.id,
      workflowName: workflow.name,
      currentStep: currentStep.id,
      currentStepIndex: currentStepIndex,
      status: preserveStatus,
      startedBy: user.username,
      startedAt: workflow.startedAt || new Date().toISOString(),
      stepData: latestStepData, // Use latestStepData to ensure we have the most recent data including trials
      completedSteps: Array.from(newCompletedSteps)
    };
    
    // Debug log before saving
    console.log('💾 [HANDLE_SAVE] Workflow instance to save:', {
      instanceId: workflowInstance.id,
      stepId: currentStep.id,
      stepDataKeys: Object.keys(workflowInstance.stepData),
      trialsStepData: workflowInstance.stepData[currentStep.id],
      trialsInFormData: workflowInstance.stepData[currentStep.id]?.formData?.trials,
      trialsCount: workflowInstance.stepData[currentStep.id]?.formData?.trials?.length || 0
    });

    // If it's the last step, complete the workflow
    if (currentStepIndex >= steps.length - 1) {
      onComplete({
        instanceId: workflow.instanceId,
        workflowId: workflow.id,
        completedSteps: Array.from(newCompletedSteps),
        finalStep: currentStep.id,
        stepData: latestStepData // Use latestStepData to ensure we have the most recent data including trials
      });
      onClose();
      return;
    }

    // Call the parent's save function (this will save to backend)
    console.log('💾 [HANDLE_SAVE] Saving workflow step:', {
      instanceId: workflow.instanceId,
      stepId: currentStep.id,
      stepIndex: currentStepIndex,
      stepDataKeys: Object.keys(latestStepData),
      formDataKeys: currentFormData ? Object.keys(currentFormData) : [],
      trialsData: latestStepData[currentStep.id]?.formData?.trials,
      trialsCount: latestStepData[currentStep.id]?.formData?.trials?.length || 0,
      fullTrialsData: JSON.stringify(latestStepData[currentStep.id]?.formData?.trials, null, 2),
      workflowInstanceStepData: workflowInstance.stepData[currentStep.id]?.formData?.trials,
      workflowInstanceTrialsCount: workflowInstance.stepData[currentStep.id]?.formData?.trials?.length || 0
    });
    
    // Wait for save to complete before moving to next step
    await onSave(workflowInstance, false);
    
    // Move to next step after saving completes
    const nextStepIndex = currentStepIndex + 1;
    
    // Check if user can navigate to the next step (role-based permission check)
    if (workflow.id === 'technical_sales' || workflow.workflowId === 'technical_sales') {
      const nextStepOrder = nextStepIndex + 1; // Convert to 1-based
      
      // Special check: If Sales Team just completed Step 5, prevent navigation to Step 6
      if (currentStepIndex === 4 && nextStepOrder === 6 && currentPhase === 'Finance Team' && !canEditStep(nextStepOrder)) {
        console.log('⚠️ [PHASE_TRANSITION] Sales Team completed Step 5 - workflow moved to Finance Team. Cannot proceed to Step 6.');
        alert('You have completed Step 5. The workflow has been moved to the Finance Department. You can no longer edit this workflow. The Finance Team assignee will continue from Step 6 onwards.');
        return; // Don't navigate - stay on Step 5
      }
      
      if (!canEditStep(nextStepOrder)) {
        // User cannot edit the next step - prevent navigation
        console.log('⚠️ [NAVIGATION] User cannot navigate to Step', nextStepOrder, 'in current phase:', currentPhase);
        alert(`You cannot proceed to Step ${nextStepOrder}. This step is assigned to ${nextStepOrder <= 5 ? 'Sales Team' : 'Finance Team'}, and the workflow is currently in the ${currentPhase} phase.`);
        return; // Don't navigate
      }
    }
    
    // If repeat purchase, skip to Customer Inquiry and Quotation
    if (shouldSkipToQuotation) {
      // Find the index of customer_inquiry_quotation step (step 5)
      const customerInquiryIndex = steps.findIndex(s => s.id === 'customer_inquiry_quotation');
      
      if (customerInquiryIndex >= 0) {
        // Mark steps 2-4 as completed (skipped)
        const skippedSteps = ['plan_visit', 'activities', 'trials'];
        const newCompletedSteps = new Set([...completedSteps, currentStep.id, ...skippedSteps]);
        setCompletedSteps(newCompletedSteps);
        
        // Jump to Customer Inquiry and Quotation step
        console.log('✅ [HANDLE_SAVE] Jumping to Customer Inquiry and Quotation (step', customerInquiryIndex + 1, ')');
        setCurrentStepIndex(customerInquiryIndex);
        return; // Exit early, don't proceed with normal navigation
      } else {
        console.warn('⚠️ [HANDLE_SAVE] Customer Inquiry and Quotation step not found, proceeding normally');
      }
    }
    
    console.log('➡️ [HANDLE_SAVE] Moving to next step:', nextStepIndex);
    setCurrentStepIndex(nextStepIndex);
    
    // Update currentStep in the workflow state as well
    if (nextStepIndex < steps.length) {
      const nextStep = steps[nextStepIndex];
      console.log('✅ [HANDLE_SAVE] Next step:', nextStep?.id, nextStep?.name);
    }
  };

  // If completed and not in edit mode, show summary view
  if (workflow.isCompleted && !workflow.editMode) {
    return (
      <div className="workflow-execution-window summary-view">
        <div className="execution-header">
          <div className="header-content">
            <h2>{workflow.name}</h2>
            <div className="progress-info">
              <span>Completed Workflow</span>
            </div>
          </div>
          <button onClick={onClose} className="close-btn">
            <FaArrowLeft className="btn-icon" />
            Back to Tasks
          </button>
        </div>
        <div className="execution-content">
          <WorkflowSummaryView 
            workflow={workflow} 
            stepData={stepData}
            onEdit={onEdit}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="workflow-execution-window">
      <div className="execution-header">
        <div className="header-content">
          <h2>{workflow.name}</h2>
          <div className="progress-info">
            <span>Step {currentStepIndex + 1} of {steps.length}</span>
            {workflow.id === 'technical_sales' || workflow.workflowId === 'technical_sales' ? (
              <span className="workflow-phase-badge" style={{ 
                marginLeft: '15px', 
                padding: '4px 12px', 
                borderRadius: '4px',
                backgroundColor: currentPhase === 'Sales Team' ? '#e3f2fd' : '#fff3e0',
                color: currentPhase === 'Sales Team' ? '#1976d2' : '#f57c00',
                fontSize: '12px',
                fontWeight: '500'
              }}>
                Current Phase: {currentPhase}
              </span>
            ) : null}
          </div>
        </div>
        <button onClick={onClose} className="close-btn">×</button>
      </div>

      <div className="execution-content">
        <div className="steps-sidebar">
          {(() => {
            // Group steps by phase for Technical Sales workflow
            if (workflow.id === 'technical_sales' || workflow.workflowId === 'technical_sales') {
              // Check if this is a repeat purchase workflow (skip steps 2-4)
              const workflowType = stepData['make_inquiry']?.formData?.workflow_type || 
                                   stepData[currentStep.id]?.formData?.workflow_type;
              const isRepeatPurchase = workflowType === 'repeat_purchase';
              const skippedStepIds = isRepeatPurchase ? ['plan_visit', 'activities', 'trials'] : [];
              
              const salesSteps = steps.filter(s => {
                const stepDef = workflowDef?.steps?.find(sd => sd.id === s.id);
                // Filter out skipped steps
                if (skippedStepIds.includes(s.id)) {
                  return false;
                }
                return stepDef?.phase === 'Sales Team';
              });
              const financeSteps = steps.filter(s => {
                const stepDef = workflowDef?.steps?.find(sd => sd.id === s.id);
                return stepDef?.phase === 'Finance Team';
              });
              
              return (
                <>
                  {salesSteps.length > 0 && (
                    <div className="phase-group">
                      <div className="phase-header" style={{
                        padding: '10px 15px',
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: '600',
                        fontSize: '13px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Sales Team (Steps 1-5)
                      </div>
                      {currentPhase === 'Finance Team' && !isFinance && !isAdmin && (
                        <div style={{
                          padding: '12px 15px',
                          backgroundColor: '#e8f5e9',
                          borderLeft: '3px solid #2e7d32',
                          margin: '8px 0',
                          fontSize: '12px',
                          color: '#2e7d32',
                          lineHeight: '1.4'
                        }}>
                          <strong>✓ Your phase completed</strong>
                          <div style={{ marginTop: '4px' }}>
                            Workflow has moved to Finance Department. Finance Team will continue from Step 6.
                          </div>
                        </div>
                      )}
                      {salesSteps.map((step, idx) => {
                        const index = steps.findIndex(s => s.id === step.id);
                        const stepOrder = index + 1;
                        const isReadOnly = !canEditStep(stepOrder);
                        return (
                          <div
                            key={step.id}
                            className={`step-item ${index === currentStepIndex ? 'active' : ''} ${completedSteps.has(step.id) ? 'completed' : ''} ${index > currentStepIndex ? 'locked' : ''} ${isReadOnly ? 'read-only' : ''}`}
                            onClick={() => {
                              if (index <= currentStepIndex) {
                                // Check permissions for Technical Sales workflows
                                if ((workflow.id === 'technical_sales' || workflow.workflowId === 'technical_sales') && !canEditStep(stepOrder) && !completedSteps.has(step.id)) {
                                  alert(`You cannot navigate to Step ${stepOrder}. This step is assigned to ${stepOrder <= 5 ? 'Sales Team' : 'Finance Team'}, and the workflow is currently in the ${currentPhase} phase.`);
                                  return;
                                }
                                setCurrentStepIndex(index);
                              }
                            }}
                            style={{ cursor: index <= currentStepIndex ? 'pointer' : 'not-allowed' }}
                          >
                            <div className="step-icon">
                              {completedSteps.has(step.id) ? (
                                <FaCheckCircle className="completed-icon" style={{fontSize: '16px'}} />
                              ) : index === currentStepIndex ? (
                                <FaCircle className="active-icon" style={{fontSize: '16px'}} />
                              ) : (
                                <FaLock className="locked-icon" style={{fontSize: '16px'}} />
                              )}
                            </div>
                            <div className="step-info">
                              <h4>{step.name} {isReadOnly && <span style={{fontSize: '11px', color: '#999'}}>(Read-only)</span>}</h4>
                              <p>{step.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Only show Finance Team steps section if:
                      1. User is Finance Team member (can see and edit when phase is Finance Team)
                      2. User is Admin (can see and edit always)
                      3. Phase is still Sales Team AND user is not Sales Team (Sales Team shouldn't see Finance steps even in Sales phase) */}
                  {financeSteps.length > 0 && (
                    (currentPhase === 'Finance Team' && (isFinance || isAdmin)) ||
                    (currentPhase === 'Sales Team' && !isSales && (isFinance || isAdmin))
                  ) && (
                    <div className="phase-group">
                      <div className="phase-header" style={{
                        padding: '10px 15px',
                        backgroundColor: '#fff3e0',
                        color: '#f57c00',
                        fontWeight: '600',
                        fontSize: '13px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Finance Team (Steps 6-12)
                      </div>
                      {currentPhase === 'Finance Team' && !isFinance && !isAdmin && (
                        <div style={{
                          padding: '12px 15px',
                          backgroundColor: '#fff3e0',
                          borderLeft: '3px solid #f57c00',
                          margin: '8px 0',
                          fontSize: '12px',
                          color: '#666',
                          lineHeight: '1.4'
                        }}>
                          <strong style={{ color: '#f57c00' }}>⚠️ Workflow moved to Finance Department</strong>
                          <div style={{ marginTop: '4px' }}>
                            You can no longer edit. Finance Team will continue from here.
                          </div>
                        </div>
                      )}
                      {financeSteps.map((step, idx) => {
                        const index = steps.findIndex(s => s.id === step.id);
                        const stepOrder = index + 1;
                        const isReadOnly = !canEditStep(stepOrder);
                        return (
                          <div
                            key={step.id}
                            className={`step-item ${index === currentStepIndex ? 'active' : ''} ${completedSteps.has(step.id) ? 'completed' : ''} ${index > currentStepIndex ? 'locked' : ''} ${isReadOnly ? 'read-only' : ''}`}
                            onClick={() => {
                              if (index <= currentStepIndex) {
                                // Check permissions for Technical Sales workflows
                                if ((workflow.id === 'technical_sales' || workflow.workflowId === 'technical_sales') && !canEditStep(stepOrder) && !completedSteps.has(step.id)) {
                                  alert(`You cannot navigate to Step ${stepOrder}. This step is assigned to ${stepOrder <= 5 ? 'Sales Team' : 'Finance Team'}, and the workflow is currently in the ${currentPhase} phase.`);
                                  return;
                                }
                                setCurrentStepIndex(index);
                              }
                            }}
                            style={{ cursor: index <= currentStepIndex ? 'pointer' : 'not-allowed' }}
                          >
                            <div className="step-icon">
                              {completedSteps.has(step.id) ? (
                                <FaCheckCircle className="completed-icon" style={{fontSize: '16px'}} />
                              ) : index === currentStepIndex ? (
                                <FaCircle className="active-icon" style={{fontSize: '16px'}} />
                              ) : (
                                <FaLock className="locked-icon" style={{fontSize: '16px'}} />
                              )}
                            </div>
                            <div className="step-info">
                              <h4>{step.name} {isReadOnly && <span style={{fontSize: '11px', color: '#999'}}>(Read-only)</span>}</h4>
                              <p>{step.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            } else {
              // Default rendering for other workflows
              // Check if this is a repeat purchase workflow (skip steps 2-4)
              const workflowType = stepData['make_inquiry']?.formData?.workflow_type || 
                                   stepData[currentStep.id]?.formData?.workflow_type;
              const isRepeatPurchase = workflowType === 'repeat_purchase';
              const skippedStepIds = isRepeatPurchase ? ['plan_visit', 'activities', 'trials'] : [];
              
              return steps.filter(step => !skippedStepIds.includes(step.id)).map((step) => {
                // Recalculate index after filtering
                const originalIndex = steps.findIndex(s => s.id === step.id);
                return (
                  <div
                    key={step.id}
                    className={`step-item ${originalIndex === currentStepIndex ? 'active' : ''} ${completedSteps.has(step.id) ? 'completed' : ''} ${originalIndex > currentStepIndex ? 'locked' : ''}`}
                    onClick={() => {
                      if (originalIndex <= currentStepIndex) {
                        setCurrentStepIndex(originalIndex);
                      }
                    }}
                    style={{ cursor: originalIndex <= currentStepIndex ? 'pointer' : 'not-allowed' }}
                  >
                    <div className="step-icon">
                      {completedSteps.has(step.id) ? (
                        <FaCheckCircle className="completed-icon" style={{fontSize: '16px'}} />
                      ) : originalIndex === currentStepIndex ? (
                        <FaCircle className="active-icon" style={{fontSize: '16px'}} />
                      ) : (
                        <FaLock className="locked-icon" style={{fontSize: '16px'}} />
                      )}
                    </div>
                    <div className="step-info">
                      <h4>{step.name}</h4>
                      <p>{step.description}</p>
                    </div>
                  </div>
                );
              });
            }
          })()}
        </div>

        <div className="step-content">
          {currentStep && (
            <WorkflowStepForm
              step={currentStep}
              formData={formData}
              attachments={attachments}
              checklist={checklist}
              comments={comments}
              onInputChange={handleInputChange}
              onAttachmentChange={handleAttachmentChange}
              onChecklistChange={handleChecklistChange}
              onCommentsChange={handleCommentsChange}
              onComplete={handleSave}
              onCancel={null}
              onPrevious={currentStepIndex > 0 ? handlePreviousStep : null}
              onSave={handleSave}
              isLastStep={isLastStep}
              users={users}
              usersLoading={usersLoading}
              usersError={usersError}
              isReadOnly={(() => {
                // Standard users cannot edit workflows
                if (isReadOnlyForStandard) {
                  return true;
                }
                if (workflow.id === 'technical_sales' || workflow.workflowId === 'technical_sales') {
                  return !canEditStep(currentStepIndex + 1);
                }
                return false;
              })()}
              editMode={workflow.editMode}
              isCompleted={workflow.isCompleted}
              currentStepIndex={currentStepIndex}
              uploadingFiles={uploadingFiles}
              onAutoSave={currentStep.id === 'activities' ? handleAutoSaveActivities : currentStep.id === 'trials' ? handleAutoSaveTrials : undefined}
              workflowStartDate={workflow.startedAt || workflow.startDate || workflow.createdAt || new Date().toISOString()}
              workflow={workflow}
              onWorkflowTypeSelected={async (workflowType) => {
                // Store workflow type in stepData
                const currentFormData = stepData[currentStep.id]?.formData || {};
                setStepData(prev => ({
                  ...prev,
                  [currentStep.id]: {
                    ...prev[currentStep.id],
                    formData: {
                      ...currentFormData,
                      workflow_type: workflowType
                    }
                  }
                }));
                console.log('✅ [WORKFLOW] Workflow type stored:', workflowType);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
});

const WorkflowAutomations = () => {
  const { user, token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('my-tasks');
  const [workflowTypeTab, setWorkflowTypeTab] = useState('technical_sales'); // New state for workflow type tabs
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Check if user can create/edit workflows (standard users cannot)
  const canCreateWorkflows = user?.role && !['standard', 'user'].includes(user.role);

  const tabs = [
    { id: 'my-tasks', label: 'My Workflows', icon: FaClipboardList },
    { id: 'workflows', label: 'Start Workflow', icon: FaPlus },
    { id: 'calendar', label: 'Calendar', icon: FaCalendarAlt }
  ];

  const myTasks = getMyTasks(user, instances);
  
  // Company drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedCompanyData, setSelectedCompanyData] = useState(null);
  
  // Auto-select appropriate tab based on available tasks
  useEffect(() => {
    if (activeTab === 'my-tasks' && myTasks.length > 0) {
      const hasTechnicalSales = myTasks.some(task => 
        task.workflowId === 'technical_sales' || 
        task.workflowType === 'technical_sales'
      );
      const hasStoreManagement = myTasks.some(task => {
        const isStoreManagement = 
          task.workflowId === 'store_management_combined' ||
          task.workflowId === 'store_management' ||
          task.workflowType === 'store_management_combined' ||
          task.workflowType === 'store_management';
        return isStoreManagement;
      });
      
      // If current tab has no tasks but the other does, switch
      if (workflowTypeTab === 'technical_sales' && !hasTechnicalSales && hasStoreManagement) {
        setWorkflowTypeTab('store_management');
      } else if (workflowTypeTab === 'store_management' && !hasStoreManagement && hasTechnicalSales) {
        setWorkflowTypeTab('technical_sales');
      }
    }
  }, [myTasks, activeTab, workflowTypeTab]);

  // Aggregate all activities from all workflow instances for the calendar
  const getAllActivities = () => {
    const allActivities = { open: [], closed: [] };
    
    instances.forEach(instance => {
      // Get activities from stepData (Technical Sales workflow has activities in step 3)
      if (instance.stepData) {
        Object.values(instance.stepData).forEach(stepData => {
          if (stepData.formData && stepData.formData.activities) {
            const activities = stepData.formData.activities;
            // Add instance ID to each activity for navigation
            const openActivities = (activities.open || []).map(act => ({
              ...act,
              instanceId: instance.id,
              workflowId: instance.workflowId,
              workflowName: workflowDefinitions[instance.workflowId]?.name || 'Workflow'
            }));
            const closedActivities = (activities.closed || []).map(act => ({
              ...act,
              instanceId: instance.id,
              workflowId: instance.workflowId,
              workflowName: workflowDefinitions[instance.workflowId]?.name || 'Workflow'
            }));
            allActivities.open.push(...openActivities);
            allActivities.closed.push(...closedActivities);
          }
        });
      }
      // Also check formData for backward compatibility
      if (instance.formData && instance.formData.activities) {
        const activities = instance.formData.activities;
        const openActivities = (activities.open || []).map(act => ({
          ...act,
          instanceId: instance.id,
          workflowId: instance.workflowId,
          workflowName: workflowDefinitions[instance.workflowId]?.name || 'Workflow'
        }));
        const closedActivities = (activities.closed || []).map(act => ({
          ...act,
          instanceId: instance.id,
          workflowId: instance.workflowId,
          workflowName: workflowDefinitions[instance.workflowId]?.name || 'Workflow'
        }));
        allActivities.open.push(...openActivities);
        allActivities.closed.push(...closedActivities);
      }
    });
    
    return allActivities;
  };

  const allWorkflowsActivities = getAllActivities();

  // Handle activity view from calendar - navigate to the workflow
  const handleCalendarActivityView = (activity) => {
    if (activity.instanceId) {
      // Find the workflow instance
      const workflowInstance = instances.find(inst => 
        String(inst.id) === String(activity.instanceId) || 
        String(inst._id) === String(activity.instanceId)
      );
      
      if (workflowInstance) {
        const workflowDef = workflowDefinitions[workflowInstance.workflowId];
        if (workflowDef) {
          // Find the step that contains activities (usually step 3 for Technical Sales)
          const activitiesStep = workflowDef.steps.find(step => 
            step.formFields && step.formFields.some(field => field.type === 'activities_manager')
          );
          
          if (activitiesStep) {
            setActiveWorkflowExecution({
              ...workflowDef,
              instanceId: workflowInstance.id,
              startedAt: workflowInstance.startedAt,
              stepData: workflowInstance.stepData,
              currentStepIndex: workflowDef.steps.findIndex(s => s.id === activitiesStep.id),
              isCompleted: workflowInstance.status === 'completed'
            });
            setActiveTab('my-tasks'); // Switch to my-tasks to show the workflow execution window
          }
        }
      }
    }
  };

  // Handle adding activity from calendar - navigate to the workflow first
  const handleCalendarAddActivity = (type, hour, prefillData) => {
    // For now, show a message that user needs to open the workflow first
    // In the future, we could auto-open the Technical Sales workflow step 3
    alert('Please open a workflow to add activities. Navigate to "My Tasks" and open a Technical Sales Workflow to Step 3 (Activities) to add new activities.');
  };

  // Load all workflow instances from backend on mount
  useEffect(() => {
    async function fetchWorkflows() {
      setLoading(true);
      setError(null);
      try {
        const response = await get(`/workflows/my-workflows?limit=10000&t=${Date.now()}`);
        const rawList = (response.data?.data || []).map(w => ({
          ...w,
          id: w.id || w._id,
          workflowId: w.workflowId || (w.workflowType === 'store_management' && !w.workflowId ? 'store_management_combined' : w.workflowId)
        }));
        
        // First deduplicate by ID
        const byId = Array.from(
          new Map(rawList.map(w => [String(w.id || w._id), w])).values()
        );
        
        // Additional deduplication: If multiple instances have same workflowId + customer_name/vendorName,
        // keep only the most recent/complete one
        const instancesByKey = {};
        byId.forEach(instance => {
          let key = null;
          
          // Create a unique key based on workflowId + company identifier
          if (instance.workflowId === 'technical_sales' && instance.stepData?.make_inquiry?.formData?.customer_name) {
            key = `${instance.workflowId}_${instance.stepData.make_inquiry.formData.customer_name}`;
          } else if ((instance.workflowId === 'store_management_combined' || instance.workflowId === 'store_management') 
                     && instance.stepData?.SM1?.formData?.vendorName) {
            key = `${instance.workflowId}_${instance.stepData.SM1.formData.vendorName}`;
          } else {
            // Fallback: use ID if no company identifier
            key = `id_${instance.id}`;
          }
          
          if (key) {
            if (!instancesByKey[key]) {
              instancesByKey[key] = [];
            }
            instancesByKey[key].push(instance);
          }
        });
        
        // For each key, keep only the best instance (most recent or most complete)
        const finalList = [];
        Object.keys(instancesByKey).forEach(key => {
          const instances = instancesByKey[key];
          if (instances.length === 1) {
            finalList.push(instances[0]);
          } else {
            // Multiple instances with same key - pick the best one
            const sorted = instances.sort((a, b) => {
              // Prefer completed workflows
              const aCompleted = a.status === 'completed' ? 1 : 0;
              const bCompleted = b.status === 'completed' ? 1 : 0;
              if (aCompleted !== bCompleted) return bCompleted - aCompleted;
              
              // Then prefer most recent update
              const aDate = new Date(a.updatedAt || a.createdAt || 0).getTime();
              const bDate = new Date(b.updatedAt || b.createdAt || 0).getTime();
              return bDate - aDate;
            });
            
            console.warn(`⚠️ [FETCH_WORKFLOWS] Found ${instances.length} duplicate instances for key ${key}, keeping most recent/complete:`, {
              kept: sorted[0].id,
              discarded: instances.slice(1).map(i => i.id)
            });
            
            finalList.push(sorted[0]);
          }
        });
        
        setInstances(finalList);
      } catch (err) {
        setError('Failed to fetch workflows.');
      } finally {
        setLoading(false);
      }
    }
    fetchWorkflows();
  }, []);



  const [activeWorkflowExecution, setActiveWorkflowExecution] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [showUserAssignmentModal, setShowUserAssignmentModal] = useState(false);
  const [pendingWorkflowId, setPendingWorkflowId] = useState(null);
  const [selectedAssignedUsers, setSelectedAssignedUsers] = useState([]);

  // Load users from database on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      setUsersLoading(true);
      setUsersError(null);
      
      try {
        const response = await fetch('/api/users', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch users: ${response.status}`);
        }
        
        const userData = await response.json();
        const usersList = Array.isArray(userData) ? userData : (userData.users || []);
        setUsers(usersList);
      } catch (error) {
        console.error('Error loading users:', error);
        setUsersError('Failed to load users from database');
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    };

    if (token) {
      fetchUsers();
    }
  }, [token]);

  // Group workflows by company (must be after users is declared)
  const groupedCompanies = useMemo(() => {
    if (workflowTypeTab === 'technical_sales') {
      const filteredTasks = myTasks.filter(task => 
        task.workflowId === 'technical_sales' || 
        task.workflowType === 'technical_sales'
      );
      return groupWorkflowsByCompany(filteredTasks, workflowTypeTab, users);
    } else {
      const filteredTasks = myTasks.filter(task => {
        const isStoreManagement = 
          task.workflowId === 'store_management_combined' ||
          task.workflowId === 'store_management' ||
          task.workflowType === 'store_management_combined' ||
          task.workflowType === 'store_management';
        return isStoreManagement;
      });
      return groupWorkflowsByCompany(filteredTasks, workflowTypeTab, users);
    }
  }, [myTasks, workflowTypeTab, users]);

  const handleStartWorkflow = (workflowId) => {
    const workflowDef = workflowDefinitions[workflowId];
    if (!workflowDef) return;
    
    // Create a new instance ID for this workflow and open directly
    const instanceId = `wf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    setActiveWorkflowExecution({
      ...workflowDef,
      instanceId: instanceId,
      startedAt: new Date().toISOString(),
      assignedTo: [user._id] // Pre-select current user, but user can change in the form
    });
  };

  const handleConfirmUserAssignment = () => {
    if (selectedAssignedUsers.length === 0) {
      alert('Please select at least one user to assign this workflow to.');
      return;
    }
    
    const workflowDef = workflowDefinitions[pendingWorkflowId];
    if (!workflowDef) return;
    
    // Create a new instance ID for this workflow
    const instanceId = `wf_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    setActiveWorkflowExecution({
      ...workflowDef,
      instanceId: instanceId,
      startedAt: new Date().toISOString(),
      assignedTo: selectedAssignedUsers // Store selected users
    });
    
    // Close modal and reset
    setShowUserAssignmentModal(false);
    setPendingWorkflowId(null);
  };

  const handleCancelUserAssignment = () => {
    setShowUserAssignmentModal(false);
    setPendingWorkflowId(null);
    setSelectedAssignedUsers([user._id]); // Reset to current user
  };

  const handleToggleAssignedUser = (userId) => {
    setSelectedAssignedUsers(prev => {
      if (prev.includes(userId)) {
        // Remove user (but keep at least one)
        if (prev.length <= 1) {
          alert('At least one user must be assigned to the workflow.');
          return prev;
        }
        return prev.filter(id => id !== userId);
      } else {
        // Add user (but limit to 2)
        if (prev.length >= 2) {
          alert('You can assign a maximum of 2 users to a workflow.');
          return prev;
        }
        return [...prev, userId];
      }
    });
  };

  const handleCloseWorkflowExecution = () => {
    // Immediately close the UI to prevent showing error states
    const workflowToSave = activeWorkflowExecution;
    setActiveWorkflowExecution(null);
    
    // Save workflow progress on close ONLY if it hasn't been saved yet (temp ID starting with 'wf_')
    // This prevents double-saving when user clicks "Save & Continue" then closes
    if (workflowToSave && workflowExecRef.current && workflowExecRef.current.getCurrentProgress) {
      const instanceId = workflowToSave.instanceId;
      // Only save if this is a temporary ID (meaning it was never saved to backend)
      if (instanceId && instanceId.toString().startsWith('wf_')) {
        // Check if a save is already in progress to prevent duplicate saves
        if (savingRef.current) {
          console.warn('⚠️ [CLOSE] Save already in progress, skipping save on close');
          return;
        }
        
        const progress = workflowExecRef.current.getCurrentProgress();
        const progressInstance = {
          id: instanceId,
          workflowId: workflowToSave.id, // This is the workflow definition ID (e.g., 'technical_sales', 'store_management_combined')
          workflowName: workflowToSave.name,
          status: 'in_progress',
          ...progress // This includes stepData, completedSteps, currentStepIndex, currentStep
        };
        console.log('💾 [CLOSE] Saving workflow on close:', {
          instanceId,
          workflowId: progressInstance.workflowId,
          hasStepData: !!progressInstance.stepData
        });
        handleSaveWorkflow(progressInstance, false);
      } else {
        console.log('ℹ️ [CLOSE] Workflow already saved (has real ID), not saving again');
      }
    }
  };

  // Track if a save is in progress to prevent duplicate saves
  const savingRef = useRef(false);
  
  const handleSaveWorkflow = async (workflowInstance, showAlert = true) => {
    // Prevent concurrent saves
    if (savingRef.current) {
      console.warn('⚠️ [SAVE] Save already in progress, ignoring duplicate save request');
      return;
    }
    
    try {
      savingRef.current = true;
      setLoading(true);
      
      // Log what we're saving
      console.log('💾 [HANDLE_SAVE_WORKFLOW] Saving workflow:', {
        instanceId: workflowInstance.id,
        workflowId: workflowInstance.workflowId,
        hasStepData: !!workflowInstance.stepData,
        stepDataKeys: workflowInstance.stepData ? Object.keys(workflowInstance.stepData) : [],
        stepDataSample: workflowInstance.stepData ? JSON.stringify(workflowInstance.stepData).substring(0, 300) : null,
        currentStep: workflowInstance.currentStep,
        currentStepIndex: workflowInstance.currentStepIndex
      });
      
      let newInstance;
      const wasNewWorkflow = workflowInstance.id && workflowInstance.id.toString().startsWith('wf_');
      
      if (workflowInstance.id && !wasNewWorkflow) {
        // Update existing workflow - include all progress data
        const updatePayload = {
          stepData: workflowInstance.stepData || {},
          completedSteps: workflowInstance.completedSteps || [],
          currentStep: workflowInstance.currentStep,
          currentStepIndex: workflowInstance.currentStepIndex,
          status: workflowInstance.status || 'in_progress',
          workflowId: workflowInstance.workflowId || undefined, // IMPORTANT: Include workflowId to preserve workflow type
          // Also allow metadata updates if provided
          ...(workflowInstance.title && { title: workflowInstance.title }),
          ...(workflowInstance.description !== undefined && { description: workflowInstance.description }),
          ...(workflowInstance.priority && { priority: workflowInstance.priority }),
          ...(workflowInstance.dueDate && { dueDate: workflowInstance.dueDate }),
          ...(workflowInstance.tags && { tags: workflowInstance.tags }),
          // Extract and update assignedTo from stepData if form fields are present
          ...(workflowInstance.stepData && (() => {
            let assignedUsers = [];
            
            // Check if assignedTo was explicitly provided
            if (workflowInstance.assignedTo) {
              if (Array.isArray(workflowInstance.assignedTo)) {
                assignedUsers = [...workflowInstance.assignedTo];
              } else {
                assignedUsers = [workflowInstance.assignedTo];
              }
            }
            
            // Extract from stepData formData
            for (const stepId in workflowInstance.stepData) {
              const stepData = workflowInstance.stepData[stepId];
              if (stepData && stepData.formData) {
                const formData = stepData.formData;
                
                // Get assigned_user (first assignee)
                if (formData.assigned_user) {
                  // Try to find user by username, email, or if it's already an ID
                  let userId = null;
                  
                  // Check if it's already a valid ObjectId string
                  if (typeof formData.assigned_user === 'string' && /^[0-9a-fA-F]{24}$/.test(formData.assigned_user)) {
                    userId = formData.assigned_user;
                  } else {
                    // Try to find by username or email
                    const userObj = users.find(u => 
                      u.username === formData.assigned_user || 
                      u.email === formData.assigned_user ||
                      u.name === formData.assigned_user
                    );
                    if (userObj && userObj._id) {
                      userId = userObj._id;
                    }
                  }
                  
                  // Only add if we found a valid user ID
                  if (userId && !assignedUsers.some(id => String(id) === String(userId))) {
                    assignedUsers.push(userId);
                  }
                }
                
                // Get assigned_user_2 (second assignee, optional)
                if (formData.assigned_user_2) {
                  // Try to find user by username, email, or if it's already an ID
                  let userId2 = null;
                  
                  // Check if it's already a valid ObjectId string
                  if (typeof formData.assigned_user_2 === 'string' && /^[0-9a-fA-F]{24}$/.test(formData.assigned_user_2)) {
                    userId2 = formData.assigned_user_2;
                  } else {
                    // Try to find by username or email
                    const userObj2 = users.find(u => 
                      u.username === formData.assigned_user_2 || 
                      u.email === formData.assigned_user_2 ||
                      u.name === formData.assigned_user_2
                    );
                    if (userObj2 && userObj2._id) {
                      userId2 = userObj2._id;
                    }
                  }
                  
                  // Only add if we found a valid user ID and it's different from first assignee
                  if (userId2 && 
                      !assignedUsers.some(id => String(id) === String(userId2)) && 
                      assignedUsers.length < 2) {
                    assignedUsers.push(userId2);
                  }
                }
                
                break; // Only check first step
              }
            }
            
            // Ensure creator is included if not already there
            const creatorId = workflowInstance.createdBy || user._id;
            if (creatorId && !assignedUsers.some(id => String(id) === String(creatorId))) {
              assignedUsers.unshift(creatorId);
            }
            
            // If we found assigned users from form, return them (limit to 2)
            if (assignedUsers.length > 0) {
              assignedUsers = assignedUsers.slice(0, 2);
              // Ensure all values are strings (ObjectIds will be converted by backend)
              return { assignedTo: assignedUsers.map(id => String(id)) };
            }
            
            return {};
          })())
        };
        // Reduced logging for performance - only log in development
        if (process.env.NODE_ENV === 'development') {
          const trialsStepData = updatePayload.stepData?.['trials'];
          console.log('💾 [SAVE] Updating workflow:', {
            instanceId: workflowInstance.id,
            stepDataKeys: Object.keys(updatePayload.stepData || {}),
            currentStepIndex: updatePayload.currentStepIndex
          });
        }
        const response = await put(`/workflows/${workflowInstance.id}`, updatePayload);
        newInstance = response.data.data || response.data;
        
        // Immediately update activeWorkflowExecution to prevent duplicate saves on close
        if (activeWorkflowExecution && newInstance && newInstance.id) {
          setActiveWorkflowExecution(prev => ({
            ...prev,
            instanceId: newInstance.id // Update to real ID so close handler won't save again
          }));
        }
        
        // Reduced logging for performance
      } else {
        // Extract assigned users from stepData (form fields)
        let assignedUsers = [];
        
        // First, check if assignedTo was set from the modal
        if (workflowInstance.assignedTo) {
          if (Array.isArray(workflowInstance.assignedTo)) {
            assignedUsers = [...workflowInstance.assignedTo];
          } else {
            assignedUsers = [workflowInstance.assignedTo];
          }
        }
        
        // Then, check stepData for assigned_user and assigned_user_2 from the form
        if (workflowInstance.stepData) {
          // Find the first step that has formData with assigned_user
          for (const stepId in workflowInstance.stepData) {
            const stepData = workflowInstance.stepData[stepId];
            if (stepData && stepData.formData) {
              const formData = stepData.formData;
              
              // Get assigned_user (first assignee)
              if (formData.assigned_user) {
                // Try to find user by username, email, or if it's already an ID
                let userId = null;
                
                // Check if it's already a valid ObjectId string
                if (typeof formData.assigned_user === 'string' && /^[0-9a-fA-F]{24}$/.test(formData.assigned_user)) {
                  userId = formData.assigned_user;
                } else {
                  // Try to find by username or email
                  const userObj = users.find(u => 
                    u.username === formData.assigned_user || 
                    u.email === formData.assigned_user ||
                    u.name === formData.assigned_user
                  );
                  if (userObj && userObj._id) {
                    userId = userObj._id;
                  }
                }
                
                // Only add if we found a valid user ID
                if (userId && !assignedUsers.some(id => String(id) === String(userId))) {
                  assignedUsers.push(userId);
                }
              }
              
              // Get assigned_user_2 (second assignee, optional)
              if (formData.assigned_user_2) {
                // Try to find user by username, email, or if it's already an ID
                let userId2 = null;
                
                // Check if it's already a valid ObjectId string
                if (typeof formData.assigned_user_2 === 'string' && /^[0-9a-fA-F]{24}$/.test(formData.assigned_user_2)) {
                  userId2 = formData.assigned_user_2;
                } else {
                  // Try to find by username or email
                  const userObj2 = users.find(u => 
                    u.username === formData.assigned_user_2 || 
                    u.email === formData.assigned_user_2 ||
                    u.name === formData.assigned_user_2
                  );
                  if (userObj2 && userObj2._id) {
                    userId2 = userObj2._id;
                  }
                }
                
                // Only add if we found a valid user ID and it's different from first assignee
                if (userId2 && 
                    !assignedUsers.some(id => String(id) === String(userId2)) && 
                    assignedUsers.length < 2) {
                  assignedUsers.push(userId2);
                }
              }
              
              // Only check the first step that has formData
              break;
            }
          }
        }
        
        // Ensure creator is included if not already there
        if (!assignedUsers.some(id => String(id) === String(user._id))) {
          assignedUsers.unshift(user._id);
        }
        
        // Ensure creator is included if not already there
        if (!assignedUsers.some(id => String(id) === String(user._id))) {
          assignedUsers.unshift(user._id);
        }
        
        // Limit to 2 users
        assignedUsers = assignedUsers.slice(0, 2);
        
        // Ensure all values are strings (ObjectIds will be converted by backend)
        const finalAssignedUsers = assignedUsers.map(id => String(id));
        
        // New workflow: send only allowed backend keys
        const workflowToCreate = {
          title: workflowInstance.workflowName || workflowInstance.workflowId || 'Workflow',
          description: workflowInstance.description || '',
          priority: workflowInstance.priority || undefined,
          assignedTo: finalAssignedUsers.length > 0 ? finalAssignedUsers : [String(user._id)], // Use extracted users or default to creator
          dueDate: workflowInstance.dueDate || undefined,
          tags: workflowInstance.tags || undefined,
          workflowId: workflowInstance.workflowId || undefined // IMPORTANT: Send workflowId so backend knows which workflow type this is
        };
        console.log('💾 [SAVE] Creating new workflow:', workflowToCreate);
        
        // Check if a workflow with the same workflowId and title already exists (prevent duplicates)
        // This can happen if handleSaveWorkflow is called multiple times rapidly
        const existingWorkflow = instances.find(i => {
          const sameWorkflowId = i.workflowId === workflowInstance.workflowId;
          const sameTitle = i.title === workflowToCreate.title;
          const recentCreation = i.createdAt && (Date.now() - new Date(i.createdAt).getTime()) < 5000; // Within last 5 seconds
          return sameWorkflowId && sameTitle && recentCreation && !i.id?.toString().startsWith('wf_');
        });
        
        if (existingWorkflow) {
          console.warn('⚠️ [SAVE] Workflow with same ID and title already exists, using existing:', existingWorkflow.id);
          newInstance = existingWorkflow;
        } else {
          // assignedTo is already an array, no need to convert
          const response = await post('/workflows', workflowToCreate);
          newInstance = response.data.data || response.data;
          if (newInstance && !newInstance.id && newInstance._id) newInstance.id = newInstance._id;
        }
        
        // If we have stepData to save, immediately update the newly created workflow
        if (newInstance && newInstance.id && workflowInstance.stepData && Object.keys(workflowInstance.stepData).length > 0) {
          console.log('💾 [SAVE] New workflow created, now saving stepData:', {
            instanceId: newInstance.id,
            stepDataKeys: Object.keys(workflowInstance.stepData || {}),
            stepDataSample: JSON.stringify(workflowInstance.stepData).substring(0, 300)
          });
          const initialUpdatePayload = {
            stepData: workflowInstance.stepData || {},
            completedSteps: workflowInstance.completedSteps || [],
            currentStep: workflowInstance.currentStep,
            currentStepIndex: workflowInstance.currentStepIndex || 0,
            status: 'in_progress',
            workflowId: workflowInstance.workflowId || undefined // Ensure workflowId is saved in initial update
          };
          const updateResponse = await put(`/workflows/${newInstance.id}`, initialUpdatePayload);
          newInstance = updateResponse.data.data || updateResponse.data;
          
          // Verify stepData was saved
          const savedStepDataKeys = newInstance?.stepData ? Object.keys(newInstance.stepData) : [];
          console.log('✅ [SAVE] Initial stepData saved to new workflow. Saved keys:', savedStepDataKeys);
          if (savedStepDataKeys.length === 0) {
            console.warn('⚠️ [SAVE] WARNING: stepData appears to be empty after save!', {
              sentKeys: Object.keys(workflowInstance.stepData || {}),
              receivedKeys: savedStepDataKeys,
              newInstanceStepData: newInstance?.stepData
            });
          }
          
          // Immediately update activeWorkflowExecution with real ID to prevent duplicate saves
          if (wasNewWorkflow && activeWorkflowExecution && newInstance && newInstance.id) {
            console.log('🔄 [SAVE] Updating activeWorkflowExecution instanceId to real ID:', newInstance.id);
            setActiveWorkflowExecution(prev => ({
              ...prev,
              instanceId: newInstance.id // Update to real ID so close handler won't save again
            }));
          }
        } else if (newInstance && newInstance.id) {
          console.log('ℹ️ [SAVE] New workflow created but no stepData to save yet (user hasn\'t filled step 1)');
          
          // Immediately update activeWorkflowExecution even if no stepData, to prevent duplicate saves
          if (wasNewWorkflow && activeWorkflowExecution && newInstance && newInstance.id) {
            console.log('🔄 [SAVE] Updating activeWorkflowExecution instanceId to real ID (no stepData):', newInstance.id);
            setActiveWorkflowExecution(prev => ({
              ...prev,
              instanceId: newInstance.id // Update to real ID so close handler won't save again
            }));
          }
        }
      }
      // After saving/updating, reload all instances so view is always consistent
      const refreshed = await get(`/workflows/my-workflows?limit=10000&t=${Date.now()}`);
      const rawList = (refreshed.data?.data || []).map(w => ({
        ...w,
        id: w.id || w._id,
        // Preserve workflowId from database; only use fallback if truly missing (for backward compatibility)
        workflowId: w.workflowId || (w.workflowType === 'store_management' && !w.workflowId ? 'store_management_combined' : w.workflowId)
      }));
      
      // Deduplicate instances by ID to prevent duplicate tasks
      const list = Array.from(
        new Map(rawList.map(inst => [String(inst.id || inst._id), inst])).values()
      );
      
      if (rawList.length !== list.length) {
        console.warn(`⚠️ [SAVE] Removed ${rawList.length - list.length} duplicate instances after refresh`);
      }
      
      setInstances(list);
      if (showAlert) alert('Workflow saved successfully!');
    } catch (err) {
      console.error('❌ [HANDLE_SAVE_WORKFLOW] Error saving workflow:', err);
      setError('Failed to save workflow.');
      alert('Failed to save workflow.');
    } finally {
      setLoading(false);
      savingRef.current = false; // Reset save flag
    }
  };

  const handleWorkflowComplete = async (workflowData) => {
    try {
      setLoading(true);
      console.log('✅ [COMPLETE] Completing workflow:', workflowData);
      // Complete/update via the backend (PUT) - explicitly set status to completed
      const completePayload = {
        ...workflowData,
        status: 'completed', // Explicitly set status to completed
        completedAt: new Date().toISOString()
      };
      console.log('✅ [COMPLETE] Sending completion payload:', completePayload);
      await put(`/workflows/${workflowData.instanceId}`, completePayload);
      // Reload all instances
      const refreshed = await get(`/workflows/my-workflows?limit=10000&t=${Date.now()}`);
      const list2 = (refreshed.data?.data || []).map(w => ({
        ...w,
        id: w.id || w._id,
        // Preserve workflowId from database; only use fallback if truly missing (for backward compatibility)
        workflowId: w.workflowId || (w.workflowType === 'store_management' && !w.workflowId ? 'store_management_combined' : w.workflowId)
      }));
      setInstances(list2);
    setActiveWorkflowExecution(null);
    alert('Workflow completed successfully!');
    } catch (err) {
      console.error('❌ [COMPLETE] Failed to complete workflow:', err);
      setError('Failed to complete workflow.');
      alert('Failed to complete workflow.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTask = (task) => {
    const instance = instances.find(i => i.id === task.instanceId);
    if (!instance) {
      console.error('❌ [RESUME] Instance not found for task:', task);
      return;
    }
    
    console.log('🔍 [RESUME] Resuming workflow:', {
      instanceId: instance.id,
      instanceWorkflowId: instance.workflowId,
      instanceWorkflowType: instance.workflowType,
      taskWorkflowId: task.workflowId,
      title: instance.title
    });
    
    const workflowDef = workflowDefinitions[instance.workflowId];
    if (!workflowDef) {
      console.error('❌ [RESUME] Workflow definition not found for:', instance.workflowId, 'Available definitions:', Object.keys(workflowDefinitions));
      return;
    }
    
    console.log('✅ [RESUME] Found workflow definition:', {
      workflowId: workflowDef.id,
      workflowName: workflowDef.name,
      stepCount: workflowDef.steps?.length || 0
    });
    
    // If this is a completed workflow, open in view/edit mode
    if (instance.status === 'completed') {
      setActiveWorkflowExecution({
        ...workflowDef,
        instanceId: instance.id,
        isResuming: true,
        isCompleted: true,
        editMode: task.editMode || false,
        currentStepIndex: 0,
        stepData: instance.stepData || {},
        completedSteps: instance.completedSteps || [],
        startedAt: instance.startedAt || new Date().toISOString(),
        workflowPhase: instance.workflowPhase || 'Sales Team' // Include workflow phase
      });
      return;
    }
    
    // If this is a saved workflow instance that's in progress, resume it
    if (instance.status === 'in_progress') {
      // Determine the correct step index to resume at
      let resumeStepIndex = 0;
      if (instance.currentStepIndex !== undefined && instance.currentStepIndex !== null) {
        resumeStepIndex = instance.currentStepIndex;
      } else if (instance.currentStep) {
        // Find the step index by step ID
        const stepIndex = workflowDef.steps.findIndex(s => s.id === instance.currentStep);
        if (stepIndex >= 0) {
          resumeStepIndex = stepIndex;
        }
      }
      
      // Ensure stepData is properly structured - handle both direct stepData and nested structure
      let savedStepData = instance.stepData || {};
      
      // If stepData is empty but instance has data, try to reconstruct it
      if (Object.keys(savedStepData).length === 0 && instance.formData) {
        console.log('⚠️ [RESUME] stepData is empty, but formData exists. Attempting to reconstruct...');
        // Try to reconstruct stepData from formData (legacy support)
        const firstStep = workflowDef.steps[0];
        if (firstStep) {
          savedStepData = {
            [firstStep.id]: {
              formData: instance.formData
            }
          };
        }
      }
      
      console.log('✅ [RESUME] Resuming workflow:', {
        instanceId: instance.id,
        workflowId: instance.workflowId,
        resumeStepIndex,
        stepDataKeys: Object.keys(savedStepData),
        stepDataStructure: Object.keys(savedStepData).map(key => ({
          stepId: key,
          hasFormData: !!(savedStepData[key]?.formData),
          formDataKeys: savedStepData[key]?.formData ? Object.keys(savedStepData[key].formData) : [],
          formDataSample: savedStepData[key]?.formData ? Object.keys(savedStepData[key].formData).slice(0, 3) : []
        })),
        currentStep: instance.currentStep,
        currentStepIndex: instance.currentStepIndex,
        completedSteps: instance.completedSteps,
        fullStepData: savedStepData
      });
      
      setActiveWorkflowExecution({
        ...workflowDef,
        instanceId: instance.id,
        isResuming: true,
        currentStepIndex: resumeStepIndex,
        stepData: savedStepData,
        completedSteps: instance.completedSteps || [],
        startedAt: instance.startedAt || new Date().toISOString(),
        workflowPhase: instance.workflowPhase || 'Sales Team' // Include workflow phase from instance
      });
      return;
    }
    
    // Otherwise, start as a new task
    setSelectedTask({
      ...task,
      stepDef,
      instance
    });
  };

  const handleDeleteTask = async (task) => {
    if (user?.role !== 'super') {
      alert('Only super admins can delete workflows.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this workflow instance?')) return;
    
    setLoading(true);
    setError(null);
    
    // Save current state for potential rollback
    const originalInstances = [...instances];
    
    // Get the actual workflow instance ID (handle all possible ID formats)
    const workflowId = task.instanceId || task.id || task._id;
    if (!workflowId) {
      console.error('❌ [DELETE] Cannot delete: task missing instanceId/id/_id', task);
      const errorMsg = 'Invalid task ID. Task object: ' + JSON.stringify(task, null, 2);
      setError(errorMsg);
      alert('Error: Cannot delete workflow. Missing workflow ID. Check console for details.');
      setLoading(false);
      return;
    }
    
    // Convert to string and ensure it's a valid MongoDB ObjectId format
    const workflowIdStr = String(workflowId);
    console.log(`🗑️ [DELETE] Frontend attempting to delete workflow ${workflowIdStr}`, {
      task,
      extractedId: workflowIdStr,
      currentInstancesCount: instances.length
    });
    
    try {
      // Call delete endpoint FIRST (don't do optimistic update)
      const deleteResponse = await del(`/workflows/${workflowIdStr}`);
      
      if (!deleteResponse.data?.success) {
        throw new Error(deleteResponse.data?.message || 'Delete failed');
      }
      
      console.log(`✅ [DELETE] Frontend received delete confirmation for ${workflowIdStr}`);
    
      // Wait a small amount to ensure DB commit
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Reload from server with cache busting
      console.log(`🔄 [DELETE] Refreshing workflow list after delete...`);
      const refreshed = await get(`/workflows/my-workflows?limit=10000&t=${Date.now()}`);
      const list = (refreshed.data?.data || []).map(w => ({
        ...w,
        id: w.id || w._id,
        // Preserve workflowId from database; only use fallback if truly missing (for backward compatibility)
        workflowId: w.workflowId || (w.workflowType === 'store_management' && !w.workflowId ? 'store_management_combined' : w.workflowId)
      }));
      
      // Deduplicate instances by id
      const uniqueList = Array.from(new Map(list.map(item => [String(item.id || item._id), item])).values());
      
      // ALWAYS filter out the deleted workflow ID, even if it appears in the response
      const filteredList = uniqueList.filter(w => {
        const wId = String(w.id || w._id);
        const deletedId = String(workflowIdStr);
        return wId !== deletedId;
      });
      
      // Verify the deleted workflow is not in the list
      const deletedStillExists = filteredList.length !== uniqueList.length;
      if (deletedStillExists) {
        console.log(`✅ [DELETE] Filtered out deleted workflow ${workflowIdStr} from list`);
      }
      
      // Update instances state
      setInstances(filteredList);
      
      console.log(`✅ [DELETE] Frontend updated list: ${filteredList.length} workflows remaining (was ${instances.length})`);
      
      // If drawer is open, refresh the company data to reflect the deletion
      // The groupedCompanies useMemo will automatically recalculate, but we need to update selectedCompanyData
      if (drawerOpen && selectedCompany) {
        console.log(`🔄 [DELETE] Refreshing drawer data for company: ${selectedCompany}`);
        // Recalculate myTasks with updated instances
        const updatedTasks = getMyTasks(user, filteredList);
        // Filter tasks based on current workflowTypeTab
        const filteredTasks = workflowTypeTab === 'technical_sales'
          ? updatedTasks.filter(t => t.workflowId === 'technical_sales' || t.workflowType === 'technical_sales')
          : updatedTasks.filter(t => {
              const isStoreManagement = 
                t.workflowId === 'store_management_combined' ||
                t.workflowId === 'store_management' ||
                t.workflowType === 'store_management_combined' ||
                t.workflowType === 'store_management';
              return isStoreManagement;
            });
        // Recalculate grouped companies
        const updatedGrouped = groupWorkflowsByCompany(filteredTasks, workflowTypeTab, users);
        // Find the company in the updated list
        const updatedCompany = updatedGrouped.find(c => c.companyName === selectedCompany);
        if (updatedCompany && updatedCompany.workflows.length > 0) {
          setSelectedCompanyData(updatedCompany);
        } else {
          // Company no longer has workflows, close drawer
          console.log(`🔄 [DELETE] Company ${selectedCompany} no longer has workflows, closing drawer`);
          handleCloseDrawer();
        }
      }
      
      // Show success message
      alert('Workflow deleted successfully');
      
    } catch (err) {
      console.error('❌ [DELETE] Frontend delete error:', err);
      console.error('❌ [DELETE] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        task,
        workflowId: workflowIdStr
      });
      
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete workflow instance.';
      setError(errorMessage);
      alert(`Error deleting workflow: ${errorMessage}`);
      
      // Rollback: restore original instances
      setInstances(originalInstances);
      
      // Try to refresh to get current state
      try {
        const refreshed = await get(`/workflows/my-workflows?limit=10000&t=${Date.now()}`);
        const list = (refreshed.data?.data || []).map(w => ({
          ...w,
          id: w.id || w._id,
          // Preserve workflowId from database; only use fallback if truly missing (for backward compatibility)
        workflowId: w.workflowId || (w.workflowType === 'store_management' && !w.workflowId ? 'store_management_combined' : w.workflowId)
        }));
        const uniqueList = Array.from(new Map(list.map(item => [String(item.id || item._id), item])).values());
        setInstances(uniqueList);
      } catch (refreshErr) {
        console.error('❌ [DELETE] Failed to refresh after delete error:', refreshErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditWorkflow = () => {
    if (activeWorkflowExecution) {
      setActiveWorkflowExecution(prev => ({
        ...prev,
        editMode: true
      }));
    }
  };

  // Company drawer handlers
  const handleCompanyClick = (companyName, companyData) => {
    setSelectedCompany(companyName);
    setSelectedCompanyData(companyData);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedCompany(null);
    setSelectedCompanyData(null);
  };

  const checkForDuplicate = (companyName, workflowType) => {
    // Check all tasks (not just filtered) for exact match
    // For Technical Sales, check customer_name
    // For Store Management, check vendorName
    const existing = myTasks.filter(task => {
      let identifier = null;
      if (workflowType === 'technical_sales') {
        identifier = task.customer_name;
      } else if (workflowType === 'store_management') {
        identifier = task.vendorName;
      }
      return identifier && identifier.trim().toLowerCase() === companyName.trim().toLowerCase();
    });
    return existing.length > 0;
  };

  const handleStartNewWorkflowFromDrawer = (companyName, companyData) => {
    // Check for duplicates
    if (checkForDuplicate(companyName, workflowTypeTab)) {
      alert(`A workflow already exists for "${companyName}". Please continue the existing workflow or use a different company name.`);
      return;
    }
    
    // Close drawer and switch to workflows tab
    handleCloseDrawer();
    setActiveTab('workflows');
    
    // Pre-select the workflow type
    if (workflowTypeTab === 'technical_sales') {
      setSelectedWorkflow(workflowDefinitions.technical_sales);
      // Store company name to pre-fill later
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('prefillCustomerName', companyName);
      }
    } else {
      setSelectedWorkflow(workflowDefinitions.store_management_combined);
      // Store vendor name to pre-fill later
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('prefillVendorName', companyName);
      }
    }
  };

  // Patch parent usage:
  // Somewhere in WorkflowAutomations component
  const workflowExecRef = useRef();

  return (
    <div className="workflow-automations">
      <div className="page-header">
        <div className="header-content">
          <h1>Workflow Automations</h1>
          <p>Automate workflows, reporting, and documents across all functions</p>
        </div>
      </div>

      <div className="tab-navigation">
        {tabs.map(tab => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <IconComponent className="tab-icon" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="tab-content">
        {activeTab === 'workflows' && (
          <div className="start-workflow-tab">
            <div className="intro-card">
              <h2>Start New Workflow</h2>
              <div className="markdown-content">
                {canCreateWorkflows ? (
                  <p>Choose a workflow to start a new process.</p>
                ) : (
                  <p style={{ color: '#dc2626', fontWeight: '500' }}>
                    Standard users cannot create workflows. Please contact an administrator or a team member with appropriate permissions to start a workflow for you.
                  </p>
                )}
              </div>
            </div>
            
            {canCreateWorkflows ? (
              <div className="workflow-cards">
                {Object.values(workflowDefinitions).map(workflow => (
                  <div key={workflow.id} className="workflow-card">
                    <div className="workflow-header">
                      <h3>{workflow.name}</h3>
                      <p>{workflow.description}</p>
                    </div>
                    <div className="workflow-actions">
                      <button 
                        className="start-workflow-btn"
                        onClick={() => handleStartWorkflow(workflow.id)}
                      >
                        <FaPlay className="btn-icon" />
                        Start Workflow
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="workflow-cards">
                {Object.values(workflowDefinitions).map(workflow => (
                  <div key={workflow.id} className="workflow-card" style={{ opacity: 0.6 }}>
                    <div className="workflow-header">
                      <h3>{workflow.name}</h3>
                      <p>{workflow.description}</p>
                    </div>
                    <div className="workflow-actions">
                      <button 
                        className="start-workflow-btn"
                        disabled
                        style={{ cursor: 'not-allowed', opacity: 0.5 }}
                        title="Standard users cannot create workflows"
                      >
                        <FaPlay className="btn-icon" />
                        Start Workflow
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'my-tasks' && (
          <div className="my-tasks-tab">
            <div className="intro-card">
              <h2>My Workflows</h2>
              <div className="markdown-content">
                <p>View and manage your assigned workflow tasks.</p>
              </div>
            </div>
            
            {/* Workflow Type Tabs */}
            <div className="workflow-type-tabs">
              <button
                className={`workflow-tab-button ${workflowTypeTab === 'technical_sales' ? 'active' : ''}`}
                onClick={() => setWorkflowTypeTab('technical_sales')}
              >
                <span className="tab-label">Technical Sales Workflow</span>
              </button>
              <button
                className={`workflow-tab-button ${workflowTypeTab === 'store_management' ? 'active' : ''}`}
                onClick={() => setWorkflowTypeTab('store_management')}
              >
                <span className="tab-label">Store Management Workflow</span>
              </button>
            </div>

            {/* Company Workflows Table */}
            <div className="workflow-tasks-content">
              <CompanyWorkflowsTable
                companies={groupedCompanies}
                onCompanyClick={handleCompanyClick}
                users={users}
              />
            </div>
            
            {/* Company Workflows Drawer */}
            <CompanyWorkflowsDrawer
              isOpen={drawerOpen}
              onClose={handleCloseDrawer}
              companyName={selectedCompany}
              companyData={selectedCompanyData}
              onStartNewWorkflow={handleStartNewWorkflowFromDrawer}
              onViewWorkflow={handleStartTask}
              onDeleteWorkflow={handleDeleteTask}
              users={users}
              allTasks={myTasks}
            />
          </div>
        )}

        {activeTab === 'calendar' && (
          <div className="calendar-tab">
            <div className="intro-card">
              <h2>All Workflows Calendar</h2>
              <div className="markdown-content">
                <p>View all tasks, meetings, and calls from all your workflows in one calendar view.</p>
              </div>
            </div>
            
            <div className="calendar-content">
              <ActivitiesCalendar
                activities={allWorkflowsActivities}
                onView={handleCalendarActivityView}
                users={users}
                onAddActivity={handleCalendarAddActivity}
              />
            </div>
          </div>
        )}


      </div>

      {activeWorkflowExecution && (
        <div className="workflow-execution-overlay">
          <WorkflowExecutionWindow
            ref={workflowExecRef}
            workflow={activeWorkflowExecution}
            onClose={handleCloseWorkflowExecution}
            onComplete={handleWorkflowComplete}
            onSave={handleSaveWorkflow}
            onEdit={handleEditWorkflow}
            users={users}
            usersLoading={usersLoading}
            usersError={usersError}
          />
        </div>
      )}

      {/* User Assignment Modal */}
      {showUserAssignmentModal && (
        <div className="modal-overlay" onClick={handleCancelUserAssignment}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Users to Workflow</h2>
              <button className="modal-close" onClick={handleCancelUserAssignment}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Select 1-2 users who can work on this workflow. The creator is automatically included.</p>
              <div className="user-assignment-list">
                {usersLoading ? (
                  <p>Loading users...</p>
                ) : usersError ? (
                  <p className="error">Error loading users: {usersError}</p>
                ) : (
                  users.map(userItem => {
                    const isSelected = selectedAssignedUsers.includes(userItem._id);
                    const isCreator = userItem._id === user._id;
                    return (
                      <label key={userItem._id} className={`user-assignment-item ${isSelected ? 'selected' : ''} ${isCreator ? 'creator' : ''}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleAssignedUser(userItem._id)}
                          disabled={isCreator} // Creator is always selected and cannot be deselected
                        />
                        <span className="user-info">
                          <strong>{userItem.name || userItem.email}</strong>
                          {userItem.department && <span className="user-department"> - {userItem.department}</span>}
                          {isCreator && <span className="user-badge"> (Creator)</span>}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={handleCancelUserAssignment}>Cancel</button>
                <button className="btn-primary" onClick={handleConfirmUserAssignment}>
                  Start Workflow
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowAutomations;
